var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
import { onMounted, onUnmounted, ref, computed, onUpdated, watchEffect, openBlock, createElementBlock, createElementVNode, normalizeClass, withDirectives, vModelText, toDisplayString, vShow, defineComponent, createCommentVNode, isRef, unref, renderSlot, createVNode, Fragment, renderList, Transition, withCtx, createBlock, resolveDynamicComponent, withModifiers, Teleport, withKeys, resolveComponent, watch, vModelRadio, normalizeStyle, useAttrs, useCssVars, createTextVNode, TransitionGroup, reactive, createApp, nextTick, getCurrentInstance, inject, vModelSelect, toRaw, mergeModels, useModel, vModelDynamic, provide } from "vue";
import { throttle, cloneDeep as cloneDeep$1 } from "lodash-es";
import mitt from "mitt";
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, onAuthStateChanged } from "firebase/auth";
import { collection, onSnapshot, Timestamp } from "firebase/firestore";
import { useRoute } from "vue-router";
function useClickOutside(el, cb) {
  function handleWindowClick(ev) {
    if (el.value && !(el.value === ev.target || el.value.contains(ev.target))) {
      cb(ev);
    }
  }
  onMounted(() => {
    document.body.addEventListener("click", handleWindowClick);
  });
  onUnmounted(() => {
    document.body.removeEventListener("click", handleWindowClick);
  });
}
const DefaultConditions = {
  "mobile": [{ maxWidth: 1e3 }],
  "desktop": [{ minWidth: 1001 }]
};
function useScreenGeometry(conditions = DefaultConditions) {
  const width = ref(window.innerWidth);
  const height = ref(window.innerHeight);
  const _sizeClass = computed(() => {
    for (const sizeClass in conditions) {
      const matchCount = conditions[sizeClass].reduce((count, c) => {
        if (c.maxWidth && width.value <= c.maxWidth) {
          return count + 1;
        }
        if (c.minWidth && width.value >= c.minWidth) {
          return count + 1;
        }
        if (c.maxHeight && height.value <= c.maxHeight) {
          return count + 1;
        }
        if (c.minHeight && height.value >= c.minHeight) {
          return count + 1;
        }
        return count;
      }, 0);
      if (matchCount === conditions[sizeClass].length) return sizeClass;
    }
    return Object.keys(conditions)[0];
  });
  const THROTTLE_TIME = 200;
  const handleResize = throttle(() => {
    width.value = window.innerWidth;
    height.value = window.innerHeight;
  }, THROTTLE_TIME);
  onMounted(() => {
    window.addEventListener("resize", handleResize);
  });
  onUnmounted(() => {
    window.removeEventListener("resize", handleResize);
  });
  return { width, height, sizeClass: _sizeClass };
}
function useMittEvent(mitt2, event, handler) {
  onMounted(() => {
    mitt2.on(event, handler);
  });
  onUnmounted(() => {
    mitt2.off(event);
  });
}
const remoteVersionRequirements = ref();
const localVersion = ref();
const meetsMinimumRequirement = ref(true);
const isAtLeastLatestStable = ref(true);
const stableVersionUpdateNotificationRegistered = ref(false);
const minVersionUpdateNotificationRegistered = ref(false);
function useVersionTracking() {
  return {
    remoteVersionRequirements,
    localVersion,
    meetsMinimumRequirement,
    isAtLeastLatestStable,
    stableVersionUpdateNotificationRegistered,
    minVersionUpdateNotificationRegistered
  };
}
const applicationName = ref();
function useProjectMetadata() {
  return { applicationName };
}
const lockdownConfig = ref();
function useLockdown() {
  return {
    lockdownConfig
  };
}
function useAuth(auth, { emit, mode = "login", minPasswordScore }) {
  const username = ref("");
  const email = ref("");
  const password = ref("");
  const confirmPassword = ref("");
  const validationTimeout = ref(null);
  const validation = ref({
    username: false,
    password: false,
    confirmPassword: false
  });
  const error = ref(null);
  const showError = ref(false);
  const loggingIn = ref(false);
  const processing = ref(false);
  const emitter = mitt();
  const passwordLength = computed(() => {
    if (password.value) {
      return password.value.length;
    } else {
      return 0;
    }
  });
  const formValid = computed(() => {
    switch (mode) {
      case "login":
        return validation.value.username && validation.value.password;
      case "signup":
        return validation.value.username && validation.value.password && validation.value.confirmPassword;
      case "reset":
        return validation.value.password;
      default:
        throw new Error(`Unknown state: ${mode}`);
    }
  });
  const loginButtonLabel = computed(() => {
    if (loggingIn.value) {
      return "Logging in...";
    } else {
      return "SIGN IN";
    }
  });
  function validateUsername() {
    var _a;
    validation.value.username = !!((_a = username.value.match(/([\w_\-.]*)@([_\-.\w]+\.[_\-\w]{2,})/g)) == null ? void 0 : _a[0]);
  }
  async function validatePassword() {
    if (mode === "signup") {
      const { default: zxcvbn } = await import("zxcvbn");
      const { score } = zxcvbn(password.value);
      validation.value.password = score >= minPasswordScore;
    } else {
      const MIN_LENGTH = 7;
      validation.value.password = password.value.length >= MIN_LENGTH;
    }
  }
  function validateConfirmPassword() {
    validation.value.confirmPassword = password.value === confirmPassword.value;
  }
  async function checkValidInput() {
    validateUsername();
    await validatePassword();
    let errorMessage = "";
    if (username.value.length > 0 && !validation.value.username) {
      errorMessage = "Username must be an email address";
      showError.value = true;
    } else if (password.value.length > 0 && !validation.value.password) {
      errorMessage = "Password must be at least 7 characters long";
      showError.value = true;
    } else {
      showError.value = false;
    }
    if (errorMessage) {
      error.value = `* ${errorMessage}`;
    } else {
      error.value = null;
    }
  }
  function validateWithDelay(fn) {
    if (validationTimeout.value) {
      clearTimeout(validationTimeout.value);
      validationTimeout.value = null;
    }
    const DELAY = 750;
    validationTimeout.value = setTimeout(async () => {
      validationTimeout.value = null;
      await fn();
    }, DELAY);
  }
  function validateInputWithDelay() {
    return validateWithDelay(checkValidInput);
  }
  async function onTapLogin() {
    var _a;
    loggingIn.value = true;
    try {
      const result = await signInWithEmailAndPassword(auth, username.value, password.value);
      console.log("Login successful!");
      loggingIn.value = false;
      emit("login", result);
      emitter.emit("login", result);
    } catch (err) {
      if (err instanceof Error) {
        if ((_a = err.stack) == null ? void 0 : _a.includes("Uncaught DOMException: Failed to execute 'put' on")) {
          loggingIn.value = false;
          console.log("AUTH ERR", typeof err, err.value, Object.keys(err), JSON.stringify(err), err);
          return;
        }
        showError.value = true;
        err.value = "* Incorrect Email Or Password";
        emit("error", err);
        emitter.emit("error", err);
        loggingIn.value = false;
      }
    }
  }
  async function onTapCreate() {
    try {
      const result = await createUserWithEmailAndPassword(auth, username.value, password.value);
      alert("Account created!");
      emit("signup", result);
      emitter.emit("signup", result);
    } catch (err) {
      alert(err);
      emit("error", err);
      emitter.emit("error", err);
    }
  }
  async function onTapReset() {
    if (!processing.value) {
      processing.value = true;
      const finishProcessing = () => {
        const FINISH_DELAY = 5e3;
        setTimeout(() => {
          processing.value = false;
        }, FINISH_DELAY);
      };
      try {
        const result = await sendPasswordResetEmail(auth, email.value);
        alert("Reset email sent");
        emit("reset", result);
        finishProcessing();
      } catch (err) {
        alert(err);
        emit("error", err);
        finishProcessing();
      }
    }
  }
  return {
    emitter,
    email,
    username,
    password,
    confirmPassword,
    validationTimeout,
    validation,
    error,
    showError,
    loggingIn,
    processing,
    passwordLength,
    formValid,
    loginButtonLabel,
    validateWithDelay,
    validateInputWithDelay,
    validateUsername,
    validatePassword,
    validateConfirmPassword,
    checkValidInput,
    onTapLogin,
    onTapCreate,
    onTapReset
  };
}
var commonjsGlobal = typeof globalThis !== "undefined" ? globalThis : typeof window !== "undefined" ? window : typeof global !== "undefined" ? global : typeof self !== "undefined" ? self : {};
function getDefaultExportFromCjs(x) {
  return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, "default") ? x["default"] : x;
}
function listCacheClear$1() {
  this.__data__ = [];
  this.size = 0;
}
var _listCacheClear = listCacheClear$1;
function eq$5(value, other) {
  return value === other || value !== value && other !== other;
}
var eq_1$1 = eq$5;
var eq$4 = eq_1$1;
function assocIndexOf$4(array, key) {
  var length = array.length;
  while (length--) {
    if (eq$4(array[length][0], key)) {
      return length;
    }
  }
  return -1;
}
var _assocIndexOf = assocIndexOf$4;
var assocIndexOf$3 = _assocIndexOf;
var arrayProto = Array.prototype;
var splice = arrayProto.splice;
function listCacheDelete$1(key) {
  var data = this.__data__, index = assocIndexOf$3(data, key);
  if (index < 0) {
    return false;
  }
  var lastIndex = data.length - 1;
  if (index == lastIndex) {
    data.pop();
  } else {
    splice.call(data, index, 1);
  }
  --this.size;
  return true;
}
var _listCacheDelete = listCacheDelete$1;
var assocIndexOf$2 = _assocIndexOf;
function listCacheGet$1(key) {
  var data = this.__data__, index = assocIndexOf$2(data, key);
  return index < 0 ? void 0 : data[index][1];
}
var _listCacheGet = listCacheGet$1;
var assocIndexOf$1 = _assocIndexOf;
function listCacheHas$1(key) {
  return assocIndexOf$1(this.__data__, key) > -1;
}
var _listCacheHas = listCacheHas$1;
var assocIndexOf = _assocIndexOf;
function listCacheSet$1(key, value) {
  var data = this.__data__, index = assocIndexOf(data, key);
  if (index < 0) {
    ++this.size;
    data.push([key, value]);
  } else {
    data[index][1] = value;
  }
  return this;
}
var _listCacheSet = listCacheSet$1;
var listCacheClear = _listCacheClear, listCacheDelete = _listCacheDelete, listCacheGet = _listCacheGet, listCacheHas = _listCacheHas, listCacheSet = _listCacheSet;
function ListCache$4(entries) {
  var index = -1, length = entries == null ? 0 : entries.length;
  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}
ListCache$4.prototype.clear = listCacheClear;
ListCache$4.prototype["delete"] = listCacheDelete;
ListCache$4.prototype.get = listCacheGet;
ListCache$4.prototype.has = listCacheHas;
ListCache$4.prototype.set = listCacheSet;
var _ListCache = ListCache$4;
var ListCache$3 = _ListCache;
function stackClear$1() {
  this.__data__ = new ListCache$3();
  this.size = 0;
}
var _stackClear = stackClear$1;
function stackDelete$1(key) {
  var data = this.__data__, result = data["delete"](key);
  this.size = data.size;
  return result;
}
var _stackDelete = stackDelete$1;
function stackGet$1(key) {
  return this.__data__.get(key);
}
var _stackGet = stackGet$1;
function stackHas$1(key) {
  return this.__data__.has(key);
}
var _stackHas = stackHas$1;
var freeGlobal$1 = typeof commonjsGlobal == "object" && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;
var _freeGlobal = freeGlobal$1;
var freeGlobal = _freeGlobal;
var freeSelf = typeof self == "object" && self && self.Object === Object && self;
var root$8 = freeGlobal || freeSelf || Function("return this")();
var _root = root$8;
var root$7 = _root;
var Symbol$5 = root$7.Symbol;
var _Symbol = Symbol$5;
var Symbol$4 = _Symbol;
var objectProto$c = Object.prototype;
var hasOwnProperty$9 = objectProto$c.hasOwnProperty;
var nativeObjectToString$1 = objectProto$c.toString;
var symToStringTag$1 = Symbol$4 ? Symbol$4.toStringTag : void 0;
function getRawTag$1(value) {
  var isOwn = hasOwnProperty$9.call(value, symToStringTag$1), tag = value[symToStringTag$1];
  try {
    value[symToStringTag$1] = void 0;
    var unmasked = true;
  } catch (e) {
  }
  var result = nativeObjectToString$1.call(value);
  if (unmasked) {
    if (isOwn) {
      value[symToStringTag$1] = tag;
    } else {
      delete value[symToStringTag$1];
    }
  }
  return result;
}
var _getRawTag = getRawTag$1;
var objectProto$b = Object.prototype;
var nativeObjectToString = objectProto$b.toString;
function objectToString$1(value) {
  return nativeObjectToString.call(value);
}
var _objectToString = objectToString$1;
var Symbol$3 = _Symbol, getRawTag = _getRawTag, objectToString = _objectToString;
var nullTag = "[object Null]", undefinedTag = "[object Undefined]";
var symToStringTag = Symbol$3 ? Symbol$3.toStringTag : void 0;
function baseGetTag$5(value) {
  if (value == null) {
    return value === void 0 ? undefinedTag : nullTag;
  }
  return symToStringTag && symToStringTag in Object(value) ? getRawTag(value) : objectToString(value);
}
var _baseGetTag = baseGetTag$5;
function isObject$5(value) {
  var type = typeof value;
  return value != null && (type == "object" || type == "function");
}
var isObject_1 = isObject$5;
var baseGetTag$4 = _baseGetTag, isObject$4 = isObject_1;
var asyncTag = "[object AsyncFunction]", funcTag$2 = "[object Function]", genTag$1 = "[object GeneratorFunction]", proxyTag = "[object Proxy]";
function isFunction$2(value) {
  if (!isObject$4(value)) {
    return false;
  }
  var tag = baseGetTag$4(value);
  return tag == funcTag$2 || tag == genTag$1 || tag == asyncTag || tag == proxyTag;
}
var isFunction_1 = isFunction$2;
var root$6 = _root;
var coreJsData$1 = root$6["__core-js_shared__"];
var _coreJsData = coreJsData$1;
var coreJsData = _coreJsData;
var maskSrcKey = function() {
  var uid = /[^.]+$/.exec(coreJsData && coreJsData.keys && coreJsData.keys.IE_PROTO || "");
  return uid ? "Symbol(src)_1." + uid : "";
}();
function isMasked$1(func) {
  return !!maskSrcKey && maskSrcKey in func;
}
var _isMasked = isMasked$1;
var funcProto$1 = Function.prototype;
var funcToString$1 = funcProto$1.toString;
function toSource$2(func) {
  if (func != null) {
    try {
      return funcToString$1.call(func);
    } catch (e) {
    }
    try {
      return func + "";
    } catch (e) {
    }
  }
  return "";
}
var _toSource = toSource$2;
var isFunction$1 = isFunction_1, isMasked = _isMasked, isObject$3 = isObject_1, toSource$1 = _toSource;
var reRegExpChar = /[\\^$.*+?()[\]{}|]/g;
var reIsHostCtor = /^\[object .+?Constructor\]$/;
var funcProto = Function.prototype, objectProto$a = Object.prototype;
var funcToString = funcProto.toString;
var hasOwnProperty$8 = objectProto$a.hasOwnProperty;
var reIsNative = RegExp(
  "^" + funcToString.call(hasOwnProperty$8).replace(reRegExpChar, "\\$&").replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, "$1.*?") + "$"
);
function baseIsNative$1(value) {
  if (!isObject$3(value) || isMasked(value)) {
    return false;
  }
  var pattern = isFunction$1(value) ? reIsNative : reIsHostCtor;
  return pattern.test(toSource$1(value));
}
var _baseIsNative = baseIsNative$1;
function getValue$1(object, key) {
  return object == null ? void 0 : object[key];
}
var _getValue = getValue$1;
var baseIsNative = _baseIsNative, getValue = _getValue;
function getNative$7(object, key) {
  var value = getValue(object, key);
  return baseIsNative(value) ? value : void 0;
}
var _getNative = getNative$7;
var getNative$6 = _getNative, root$5 = _root;
var Map$4 = getNative$6(root$5, "Map");
var _Map = Map$4;
var getNative$5 = _getNative;
var nativeCreate$4 = getNative$5(Object, "create");
var _nativeCreate = nativeCreate$4;
var nativeCreate$3 = _nativeCreate;
function hashClear$1() {
  this.__data__ = nativeCreate$3 ? nativeCreate$3(null) : {};
  this.size = 0;
}
var _hashClear = hashClear$1;
function hashDelete$1(key) {
  var result = this.has(key) && delete this.__data__[key];
  this.size -= result ? 1 : 0;
  return result;
}
var _hashDelete = hashDelete$1;
var nativeCreate$2 = _nativeCreate;
var HASH_UNDEFINED$1 = "__lodash_hash_undefined__";
var objectProto$9 = Object.prototype;
var hasOwnProperty$7 = objectProto$9.hasOwnProperty;
function hashGet$1(key) {
  var data = this.__data__;
  if (nativeCreate$2) {
    var result = data[key];
    return result === HASH_UNDEFINED$1 ? void 0 : result;
  }
  return hasOwnProperty$7.call(data, key) ? data[key] : void 0;
}
var _hashGet = hashGet$1;
var nativeCreate$1 = _nativeCreate;
var objectProto$8 = Object.prototype;
var hasOwnProperty$6 = objectProto$8.hasOwnProperty;
function hashHas$1(key) {
  var data = this.__data__;
  return nativeCreate$1 ? data[key] !== void 0 : hasOwnProperty$6.call(data, key);
}
var _hashHas = hashHas$1;
var nativeCreate = _nativeCreate;
var HASH_UNDEFINED = "__lodash_hash_undefined__";
function hashSet$1(key, value) {
  var data = this.__data__;
  this.size += this.has(key) ? 0 : 1;
  data[key] = nativeCreate && value === void 0 ? HASH_UNDEFINED : value;
  return this;
}
var _hashSet = hashSet$1;
var hashClear = _hashClear, hashDelete = _hashDelete, hashGet = _hashGet, hashHas = _hashHas, hashSet = _hashSet;
function Hash$1(entries) {
  var index = -1, length = entries == null ? 0 : entries.length;
  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}
Hash$1.prototype.clear = hashClear;
Hash$1.prototype["delete"] = hashDelete;
Hash$1.prototype.get = hashGet;
Hash$1.prototype.has = hashHas;
Hash$1.prototype.set = hashSet;
var _Hash = Hash$1;
var Hash = _Hash, ListCache$2 = _ListCache, Map$3 = _Map;
function mapCacheClear$1() {
  this.size = 0;
  this.__data__ = {
    "hash": new Hash(),
    "map": new (Map$3 || ListCache$2)(),
    "string": new Hash()
  };
}
var _mapCacheClear = mapCacheClear$1;
function isKeyable$1(value) {
  var type = typeof value;
  return type == "string" || type == "number" || type == "symbol" || type == "boolean" ? value !== "__proto__" : value === null;
}
var _isKeyable = isKeyable$1;
var isKeyable = _isKeyable;
function getMapData$4(map, key) {
  var data = map.__data__;
  return isKeyable(key) ? data[typeof key == "string" ? "string" : "hash"] : data.map;
}
var _getMapData = getMapData$4;
var getMapData$3 = _getMapData;
function mapCacheDelete$1(key) {
  var result = getMapData$3(this, key)["delete"](key);
  this.size -= result ? 1 : 0;
  return result;
}
var _mapCacheDelete = mapCacheDelete$1;
var getMapData$2 = _getMapData;
function mapCacheGet$1(key) {
  return getMapData$2(this, key).get(key);
}
var _mapCacheGet = mapCacheGet$1;
var getMapData$1 = _getMapData;
function mapCacheHas$1(key) {
  return getMapData$1(this, key).has(key);
}
var _mapCacheHas = mapCacheHas$1;
var getMapData = _getMapData;
function mapCacheSet$1(key, value) {
  var data = getMapData(this, key), size = data.size;
  data.set(key, value);
  this.size += data.size == size ? 0 : 1;
  return this;
}
var _mapCacheSet = mapCacheSet$1;
var mapCacheClear = _mapCacheClear, mapCacheDelete = _mapCacheDelete, mapCacheGet = _mapCacheGet, mapCacheHas = _mapCacheHas, mapCacheSet = _mapCacheSet;
function MapCache$2(entries) {
  var index = -1, length = entries == null ? 0 : entries.length;
  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}
MapCache$2.prototype.clear = mapCacheClear;
MapCache$2.prototype["delete"] = mapCacheDelete;
MapCache$2.prototype.get = mapCacheGet;
MapCache$2.prototype.has = mapCacheHas;
MapCache$2.prototype.set = mapCacheSet;
var _MapCache = MapCache$2;
var ListCache$1 = _ListCache, Map$2 = _Map, MapCache$1 = _MapCache;
var LARGE_ARRAY_SIZE = 200;
function stackSet$1(key, value) {
  var data = this.__data__;
  if (data instanceof ListCache$1) {
    var pairs = data.__data__;
    if (!Map$2 || pairs.length < LARGE_ARRAY_SIZE - 1) {
      pairs.push([key, value]);
      this.size = ++data.size;
      return this;
    }
    data = this.__data__ = new MapCache$1(pairs);
  }
  data.set(key, value);
  this.size = data.size;
  return this;
}
var _stackSet = stackSet$1;
var ListCache = _ListCache, stackClear = _stackClear, stackDelete = _stackDelete, stackGet = _stackGet, stackHas = _stackHas, stackSet = _stackSet;
function Stack$1(entries) {
  var data = this.__data__ = new ListCache(entries);
  this.size = data.size;
}
Stack$1.prototype.clear = stackClear;
Stack$1.prototype["delete"] = stackDelete;
Stack$1.prototype.get = stackGet;
Stack$1.prototype.has = stackHas;
Stack$1.prototype.set = stackSet;
var _Stack = Stack$1;
function arrayEach$1(array, iteratee) {
  var index = -1, length = array == null ? 0 : array.length;
  while (++index < length) {
    if (iteratee(array[index], index, array) === false) {
      break;
    }
  }
  return array;
}
var _arrayEach = arrayEach$1;
var getNative$4 = _getNative;
var defineProperty$1 = function() {
  try {
    var func = getNative$4(Object, "defineProperty");
    func({}, "", {});
    return func;
  } catch (e) {
  }
}();
var _defineProperty = defineProperty$1;
var defineProperty = _defineProperty;
function baseAssignValue$2(object, key, value) {
  if (key == "__proto__" && defineProperty) {
    defineProperty(object, key, {
      "configurable": true,
      "enumerable": true,
      "value": value,
      "writable": true
    });
  } else {
    object[key] = value;
  }
}
var _baseAssignValue = baseAssignValue$2;
var baseAssignValue$1 = _baseAssignValue, eq$3 = eq_1$1;
var objectProto$7 = Object.prototype;
var hasOwnProperty$5 = objectProto$7.hasOwnProperty;
function assignValue$2(object, key, value) {
  var objValue = object[key];
  if (!(hasOwnProperty$5.call(object, key) && eq$3(objValue, value)) || value === void 0 && !(key in object)) {
    baseAssignValue$1(object, key, value);
  }
}
var _assignValue = assignValue$2;
var assignValue$1 = _assignValue, baseAssignValue = _baseAssignValue;
function copyObject$4(source, props, object, customizer) {
  var isNew = !object;
  object || (object = {});
  var index = -1, length = props.length;
  while (++index < length) {
    var key = props[index];
    var newValue = customizer ? customizer(object[key], source[key], key, object, source) : void 0;
    if (newValue === void 0) {
      newValue = source[key];
    }
    if (isNew) {
      baseAssignValue(object, key, newValue);
    } else {
      assignValue$1(object, key, newValue);
    }
  }
  return object;
}
var _copyObject = copyObject$4;
function baseTimes$1(n, iteratee) {
  var index = -1, result = Array(n);
  while (++index < n) {
    result[index] = iteratee(index);
  }
  return result;
}
var _baseTimes = baseTimes$1;
function isObjectLike$6(value) {
  return value != null && typeof value == "object";
}
var isObjectLike_1 = isObjectLike$6;
var baseGetTag$3 = _baseGetTag, isObjectLike$5 = isObjectLike_1;
var argsTag$2 = "[object Arguments]";
function baseIsArguments$1(value) {
  return isObjectLike$5(value) && baseGetTag$3(value) == argsTag$2;
}
var _baseIsArguments = baseIsArguments$1;
var baseIsArguments = _baseIsArguments, isObjectLike$4 = isObjectLike_1;
var objectProto$6 = Object.prototype;
var hasOwnProperty$4 = objectProto$6.hasOwnProperty;
var propertyIsEnumerable$1 = objectProto$6.propertyIsEnumerable;
var isArguments$1 = baseIsArguments(/* @__PURE__ */ function() {
  return arguments;
}()) ? baseIsArguments : function(value) {
  return isObjectLike$4(value) && hasOwnProperty$4.call(value, "callee") && !propertyIsEnumerable$1.call(value, "callee");
};
var isArguments_1 = isArguments$1;
var isArray$6 = Array.isArray;
var isArray_1 = isArray$6;
var isBuffer$2 = { exports: {} };
function stubFalse() {
  return false;
}
var stubFalse_1 = stubFalse;
isBuffer$2.exports;
(function(module, exports) {
  var root2 = _root, stubFalse2 = stubFalse_1;
  var freeExports = exports && !exports.nodeType && exports;
  var freeModule = freeExports && true && module && !module.nodeType && module;
  var moduleExports = freeModule && freeModule.exports === freeExports;
  var Buffer2 = moduleExports ? root2.Buffer : void 0;
  var nativeIsBuffer = Buffer2 ? Buffer2.isBuffer : void 0;
  var isBuffer2 = nativeIsBuffer || stubFalse2;
  module.exports = isBuffer2;
})(isBuffer$2, isBuffer$2.exports);
var isBufferExports = isBuffer$2.exports;
var MAX_SAFE_INTEGER$3 = 9007199254740991;
var reIsUint = /^(?:0|[1-9]\d*)$/;
function isIndex$1(value, length) {
  var type = typeof value;
  length = length == null ? MAX_SAFE_INTEGER$3 : length;
  return !!length && (type == "number" || type != "symbol" && reIsUint.test(value)) && (value > -1 && value % 1 == 0 && value < length);
}
var _isIndex = isIndex$1;
var MAX_SAFE_INTEGER$2 = 9007199254740991;
function isLength$2(value) {
  return typeof value == "number" && value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER$2;
}
var isLength_1 = isLength$2;
var baseGetTag$2 = _baseGetTag, isLength$1 = isLength_1, isObjectLike$3 = isObjectLike_1;
var argsTag$1 = "[object Arguments]", arrayTag$1 = "[object Array]", boolTag$2 = "[object Boolean]", dateTag$2 = "[object Date]", errorTag$1 = "[object Error]", funcTag$1 = "[object Function]", mapTag$4 = "[object Map]", numberTag$2 = "[object Number]", objectTag$2 = "[object Object]", regexpTag$2 = "[object RegExp]", setTag$4 = "[object Set]", stringTag$2 = "[object String]", weakMapTag$2 = "[object WeakMap]";
var arrayBufferTag$2 = "[object ArrayBuffer]", dataViewTag$3 = "[object DataView]", float32Tag$2 = "[object Float32Array]", float64Tag$2 = "[object Float64Array]", int8Tag$2 = "[object Int8Array]", int16Tag$2 = "[object Int16Array]", int32Tag$2 = "[object Int32Array]", uint8Tag$2 = "[object Uint8Array]", uint8ClampedTag$2 = "[object Uint8ClampedArray]", uint16Tag$2 = "[object Uint16Array]", uint32Tag$2 = "[object Uint32Array]";
var typedArrayTags = {};
typedArrayTags[float32Tag$2] = typedArrayTags[float64Tag$2] = typedArrayTags[int8Tag$2] = typedArrayTags[int16Tag$2] = typedArrayTags[int32Tag$2] = typedArrayTags[uint8Tag$2] = typedArrayTags[uint8ClampedTag$2] = typedArrayTags[uint16Tag$2] = typedArrayTags[uint32Tag$2] = true;
typedArrayTags[argsTag$1] = typedArrayTags[arrayTag$1] = typedArrayTags[arrayBufferTag$2] = typedArrayTags[boolTag$2] = typedArrayTags[dataViewTag$3] = typedArrayTags[dateTag$2] = typedArrayTags[errorTag$1] = typedArrayTags[funcTag$1] = typedArrayTags[mapTag$4] = typedArrayTags[numberTag$2] = typedArrayTags[objectTag$2] = typedArrayTags[regexpTag$2] = typedArrayTags[setTag$4] = typedArrayTags[stringTag$2] = typedArrayTags[weakMapTag$2] = false;
function baseIsTypedArray$1(value) {
  return isObjectLike$3(value) && isLength$1(value.length) && !!typedArrayTags[baseGetTag$2(value)];
}
var _baseIsTypedArray = baseIsTypedArray$1;
function baseUnary$3(func) {
  return function(value) {
    return func(value);
  };
}
var _baseUnary = baseUnary$3;
var _nodeUtil = { exports: {} };
_nodeUtil.exports;
(function(module, exports) {
  var freeGlobal2 = _freeGlobal;
  var freeExports = exports && !exports.nodeType && exports;
  var freeModule = freeExports && true && module && !module.nodeType && module;
  var moduleExports = freeModule && freeModule.exports === freeExports;
  var freeProcess = moduleExports && freeGlobal2.process;
  var nodeUtil2 = function() {
    try {
      var types = freeModule && freeModule.require && freeModule.require("util").types;
      if (types) {
        return types;
      }
      return freeProcess && freeProcess.binding && freeProcess.binding("util");
    } catch (e) {
    }
  }();
  module.exports = nodeUtil2;
})(_nodeUtil, _nodeUtil.exports);
var _nodeUtilExports = _nodeUtil.exports;
var baseIsTypedArray = _baseIsTypedArray, baseUnary$2 = _baseUnary, nodeUtil$2 = _nodeUtilExports;
var nodeIsTypedArray = nodeUtil$2 && nodeUtil$2.isTypedArray;
var isTypedArray$1 = nodeIsTypedArray ? baseUnary$2(nodeIsTypedArray) : baseIsTypedArray;
var isTypedArray_1 = isTypedArray$1;
var baseTimes = _baseTimes, isArguments = isArguments_1, isArray$5 = isArray_1, isBuffer$1 = isBufferExports, isIndex = _isIndex, isTypedArray = isTypedArray_1;
var objectProto$5 = Object.prototype;
var hasOwnProperty$3 = objectProto$5.hasOwnProperty;
function arrayLikeKeys$2(value, inherited) {
  var isArr = isArray$5(value), isArg = !isArr && isArguments(value), isBuff = !isArr && !isArg && isBuffer$1(value), isType = !isArr && !isArg && !isBuff && isTypedArray(value), skipIndexes = isArr || isArg || isBuff || isType, result = skipIndexes ? baseTimes(value.length, String) : [], length = result.length;
  for (var key in value) {
    if ((inherited || hasOwnProperty$3.call(value, key)) && !(skipIndexes && // Safari 9 has enumerable `arguments.length` in strict mode.
    (key == "length" || // Node.js 0.10 has enumerable non-index properties on buffers.
    isBuff && (key == "offset" || key == "parent") || // PhantomJS 2 has enumerable non-index properties on typed arrays.
    isType && (key == "buffer" || key == "byteLength" || key == "byteOffset") || // Skip index properties.
    isIndex(key, length)))) {
      result.push(key);
    }
  }
  return result;
}
var _arrayLikeKeys = arrayLikeKeys$2;
var objectProto$4 = Object.prototype;
function isPrototype$3(value) {
  var Ctor = value && value.constructor, proto = typeof Ctor == "function" && Ctor.prototype || objectProto$4;
  return value === proto;
}
var _isPrototype = isPrototype$3;
function overArg$2(func, transform) {
  return function(arg) {
    return func(transform(arg));
  };
}
var _overArg = overArg$2;
var overArg$1 = _overArg;
var nativeKeys$1 = overArg$1(Object.keys, Object);
var _nativeKeys = nativeKeys$1;
var isPrototype$2 = _isPrototype, nativeKeys = _nativeKeys;
var objectProto$3 = Object.prototype;
var hasOwnProperty$2 = objectProto$3.hasOwnProperty;
function baseKeys$1(object) {
  if (!isPrototype$2(object)) {
    return nativeKeys(object);
  }
  var result = [];
  for (var key in Object(object)) {
    if (hasOwnProperty$2.call(object, key) && key != "constructor") {
      result.push(key);
    }
  }
  return result;
}
var _baseKeys = baseKeys$1;
var isFunction = isFunction_1, isLength = isLength_1;
function isArrayLike$2(value) {
  return value != null && isLength(value.length) && !isFunction(value);
}
var isArrayLike_1 = isArrayLike$2;
var arrayLikeKeys$1 = _arrayLikeKeys, baseKeys = _baseKeys, isArrayLike$1 = isArrayLike_1;
function keys$3(object) {
  return isArrayLike$1(object) ? arrayLikeKeys$1(object) : baseKeys(object);
}
var keys_1 = keys$3;
var copyObject$3 = _copyObject, keys$2 = keys_1;
function baseAssign$1(object, source) {
  return object && copyObject$3(source, keys$2(source), object);
}
var _baseAssign = baseAssign$1;
function nativeKeysIn$1(object) {
  var result = [];
  if (object != null) {
    for (var key in Object(object)) {
      result.push(key);
    }
  }
  return result;
}
var _nativeKeysIn = nativeKeysIn$1;
var isObject$2 = isObject_1, isPrototype$1 = _isPrototype, nativeKeysIn = _nativeKeysIn;
var objectProto$2 = Object.prototype;
var hasOwnProperty$1 = objectProto$2.hasOwnProperty;
function baseKeysIn$1(object) {
  if (!isObject$2(object)) {
    return nativeKeysIn(object);
  }
  var isProto = isPrototype$1(object), result = [];
  for (var key in object) {
    if (!(key == "constructor" && (isProto || !hasOwnProperty$1.call(object, key)))) {
      result.push(key);
    }
  }
  return result;
}
var _baseKeysIn = baseKeysIn$1;
var arrayLikeKeys = _arrayLikeKeys, baseKeysIn = _baseKeysIn, isArrayLike = isArrayLike_1;
function keysIn$3(object) {
  return isArrayLike(object) ? arrayLikeKeys(object, true) : baseKeysIn(object);
}
var keysIn_1 = keysIn$3;
var copyObject$2 = _copyObject, keysIn$2 = keysIn_1;
function baseAssignIn$1(object, source) {
  return object && copyObject$2(source, keysIn$2(source), object);
}
var _baseAssignIn = baseAssignIn$1;
var _cloneBuffer = { exports: {} };
_cloneBuffer.exports;
(function(module, exports) {
  var root2 = _root;
  var freeExports = exports && !exports.nodeType && exports;
  var freeModule = freeExports && true && module && !module.nodeType && module;
  var moduleExports = freeModule && freeModule.exports === freeExports;
  var Buffer2 = moduleExports ? root2.Buffer : void 0, allocUnsafe = Buffer2 ? Buffer2.allocUnsafe : void 0;
  function cloneBuffer2(buffer, isDeep) {
    if (isDeep) {
      return buffer.slice();
    }
    var length = buffer.length, result = allocUnsafe ? allocUnsafe(length) : new buffer.constructor(length);
    buffer.copy(result);
    return result;
  }
  module.exports = cloneBuffer2;
})(_cloneBuffer, _cloneBuffer.exports);
var _cloneBufferExports = _cloneBuffer.exports;
function copyArray$1(source, array) {
  var index = -1, length = source.length;
  array || (array = Array(length));
  while (++index < length) {
    array[index] = source[index];
  }
  return array;
}
var _copyArray = copyArray$1;
function arrayFilter$1(array, predicate) {
  var index = -1, length = array == null ? 0 : array.length, resIndex = 0, result = [];
  while (++index < length) {
    var value = array[index];
    if (predicate(value, index, array)) {
      result[resIndex++] = value;
    }
  }
  return result;
}
var _arrayFilter = arrayFilter$1;
function stubArray$2() {
  return [];
}
var stubArray_1 = stubArray$2;
var arrayFilter = _arrayFilter, stubArray$1 = stubArray_1;
var objectProto$1 = Object.prototype;
var propertyIsEnumerable = objectProto$1.propertyIsEnumerable;
var nativeGetSymbols$1 = Object.getOwnPropertySymbols;
var getSymbols$3 = !nativeGetSymbols$1 ? stubArray$1 : function(object) {
  if (object == null) {
    return [];
  }
  object = Object(object);
  return arrayFilter(nativeGetSymbols$1(object), function(symbol) {
    return propertyIsEnumerable.call(object, symbol);
  });
};
var _getSymbols = getSymbols$3;
var copyObject$1 = _copyObject, getSymbols$2 = _getSymbols;
function copySymbols$1(source, object) {
  return copyObject$1(source, getSymbols$2(source), object);
}
var _copySymbols = copySymbols$1;
function arrayPush$2(array, values) {
  var index = -1, length = values.length, offset = array.length;
  while (++index < length) {
    array[offset + index] = values[index];
  }
  return array;
}
var _arrayPush = arrayPush$2;
var overArg = _overArg;
var getPrototype$2 = overArg(Object.getPrototypeOf, Object);
var _getPrototype = getPrototype$2;
var arrayPush$1 = _arrayPush, getPrototype$1 = _getPrototype, getSymbols$1 = _getSymbols, stubArray = stubArray_1;
var nativeGetSymbols = Object.getOwnPropertySymbols;
var getSymbolsIn$2 = !nativeGetSymbols ? stubArray : function(object) {
  var result = [];
  while (object) {
    arrayPush$1(result, getSymbols$1(object));
    object = getPrototype$1(object);
  }
  return result;
};
var _getSymbolsIn = getSymbolsIn$2;
var copyObject = _copyObject, getSymbolsIn$1 = _getSymbolsIn;
function copySymbolsIn$1(source, object) {
  return copyObject(source, getSymbolsIn$1(source), object);
}
var _copySymbolsIn = copySymbolsIn$1;
var arrayPush = _arrayPush, isArray$4 = isArray_1;
function baseGetAllKeys$2(object, keysFunc, symbolsFunc) {
  var result = keysFunc(object);
  return isArray$4(object) ? result : arrayPush(result, symbolsFunc(object));
}
var _baseGetAllKeys = baseGetAllKeys$2;
var baseGetAllKeys$1 = _baseGetAllKeys, getSymbols = _getSymbols, keys$1 = keys_1;
function getAllKeys$1(object) {
  return baseGetAllKeys$1(object, keys$1, getSymbols);
}
var _getAllKeys = getAllKeys$1;
var baseGetAllKeys = _baseGetAllKeys, getSymbolsIn = _getSymbolsIn, keysIn$1 = keysIn_1;
function getAllKeysIn$1(object) {
  return baseGetAllKeys(object, keysIn$1, getSymbolsIn);
}
var _getAllKeysIn = getAllKeysIn$1;
var getNative$3 = _getNative, root$4 = _root;
var DataView$1 = getNative$3(root$4, "DataView");
var _DataView = DataView$1;
var getNative$2 = _getNative, root$3 = _root;
var Promise$2 = getNative$2(root$3, "Promise");
var _Promise = Promise$2;
var getNative$1 = _getNative, root$2 = _root;
var Set$2 = getNative$1(root$2, "Set");
var _Set = Set$2;
var getNative = _getNative, root$1 = _root;
var WeakMap$1 = getNative(root$1, "WeakMap");
var _WeakMap = WeakMap$1;
var DataView = _DataView, Map$1 = _Map, Promise$1 = _Promise, Set$1 = _Set, WeakMap = _WeakMap, baseGetTag$1 = _baseGetTag, toSource = _toSource;
var mapTag$3 = "[object Map]", objectTag$1 = "[object Object]", promiseTag = "[object Promise]", setTag$3 = "[object Set]", weakMapTag$1 = "[object WeakMap]";
var dataViewTag$2 = "[object DataView]";
var dataViewCtorString = toSource(DataView), mapCtorString = toSource(Map$1), promiseCtorString = toSource(Promise$1), setCtorString = toSource(Set$1), weakMapCtorString = toSource(WeakMap);
var getTag$3 = baseGetTag$1;
if (DataView && getTag$3(new DataView(new ArrayBuffer(1))) != dataViewTag$2 || Map$1 && getTag$3(new Map$1()) != mapTag$3 || Promise$1 && getTag$3(Promise$1.resolve()) != promiseTag || Set$1 && getTag$3(new Set$1()) != setTag$3 || WeakMap && getTag$3(new WeakMap()) != weakMapTag$1) {
  getTag$3 = function(value) {
    var result = baseGetTag$1(value), Ctor = result == objectTag$1 ? value.constructor : void 0, ctorString = Ctor ? toSource(Ctor) : "";
    if (ctorString) {
      switch (ctorString) {
        case dataViewCtorString:
          return dataViewTag$2;
        case mapCtorString:
          return mapTag$3;
        case promiseCtorString:
          return promiseTag;
        case setCtorString:
          return setTag$3;
        case weakMapCtorString:
          return weakMapTag$1;
      }
    }
    return result;
  };
}
var _getTag = getTag$3;
var objectProto = Object.prototype;
var hasOwnProperty = objectProto.hasOwnProperty;
function initCloneArray$1(array) {
  var length = array.length, result = new array.constructor(length);
  if (length && typeof array[0] == "string" && hasOwnProperty.call(array, "index")) {
    result.index = array.index;
    result.input = array.input;
  }
  return result;
}
var _initCloneArray = initCloneArray$1;
var root = _root;
var Uint8Array$2 = root.Uint8Array;
var _Uint8Array = Uint8Array$2;
var Uint8Array$1 = _Uint8Array;
function cloneArrayBuffer$3(arrayBuffer) {
  var result = new arrayBuffer.constructor(arrayBuffer.byteLength);
  new Uint8Array$1(result).set(new Uint8Array$1(arrayBuffer));
  return result;
}
var _cloneArrayBuffer = cloneArrayBuffer$3;
var cloneArrayBuffer$2 = _cloneArrayBuffer;
function cloneDataView$1(dataView, isDeep) {
  var buffer = isDeep ? cloneArrayBuffer$2(dataView.buffer) : dataView.buffer;
  return new dataView.constructor(buffer, dataView.byteOffset, dataView.byteLength);
}
var _cloneDataView = cloneDataView$1;
var reFlags = /\w*$/;
function cloneRegExp$1(regexp) {
  var result = new regexp.constructor(regexp.source, reFlags.exec(regexp));
  result.lastIndex = regexp.lastIndex;
  return result;
}
var _cloneRegExp = cloneRegExp$1;
var Symbol$2 = _Symbol;
var symbolProto$1 = Symbol$2 ? Symbol$2.prototype : void 0, symbolValueOf = symbolProto$1 ? symbolProto$1.valueOf : void 0;
function cloneSymbol$1(symbol) {
  return symbolValueOf ? Object(symbolValueOf.call(symbol)) : {};
}
var _cloneSymbol = cloneSymbol$1;
var cloneArrayBuffer$1 = _cloneArrayBuffer;
function cloneTypedArray$1(typedArray, isDeep) {
  var buffer = isDeep ? cloneArrayBuffer$1(typedArray.buffer) : typedArray.buffer;
  return new typedArray.constructor(buffer, typedArray.byteOffset, typedArray.length);
}
var _cloneTypedArray = cloneTypedArray$1;
var cloneArrayBuffer = _cloneArrayBuffer, cloneDataView = _cloneDataView, cloneRegExp = _cloneRegExp, cloneSymbol = _cloneSymbol, cloneTypedArray = _cloneTypedArray;
var boolTag$1 = "[object Boolean]", dateTag$1 = "[object Date]", mapTag$2 = "[object Map]", numberTag$1 = "[object Number]", regexpTag$1 = "[object RegExp]", setTag$2 = "[object Set]", stringTag$1 = "[object String]", symbolTag$2 = "[object Symbol]";
var arrayBufferTag$1 = "[object ArrayBuffer]", dataViewTag$1 = "[object DataView]", float32Tag$1 = "[object Float32Array]", float64Tag$1 = "[object Float64Array]", int8Tag$1 = "[object Int8Array]", int16Tag$1 = "[object Int16Array]", int32Tag$1 = "[object Int32Array]", uint8Tag$1 = "[object Uint8Array]", uint8ClampedTag$1 = "[object Uint8ClampedArray]", uint16Tag$1 = "[object Uint16Array]", uint32Tag$1 = "[object Uint32Array]";
function initCloneByTag$1(object, tag, isDeep) {
  var Ctor = object.constructor;
  switch (tag) {
    case arrayBufferTag$1:
      return cloneArrayBuffer(object);
    case boolTag$1:
    case dateTag$1:
      return new Ctor(+object);
    case dataViewTag$1:
      return cloneDataView(object, isDeep);
    case float32Tag$1:
    case float64Tag$1:
    case int8Tag$1:
    case int16Tag$1:
    case int32Tag$1:
    case uint8Tag$1:
    case uint8ClampedTag$1:
    case uint16Tag$1:
    case uint32Tag$1:
      return cloneTypedArray(object, isDeep);
    case mapTag$2:
      return new Ctor();
    case numberTag$1:
    case stringTag$1:
      return new Ctor(object);
    case regexpTag$1:
      return cloneRegExp(object);
    case setTag$2:
      return new Ctor();
    case symbolTag$2:
      return cloneSymbol(object);
  }
}
var _initCloneByTag = initCloneByTag$1;
var isObject$1 = isObject_1;
var objectCreate = Object.create;
var baseCreate$1 = /* @__PURE__ */ function() {
  function object() {
  }
  return function(proto) {
    if (!isObject$1(proto)) {
      return {};
    }
    if (objectCreate) {
      return objectCreate(proto);
    }
    object.prototype = proto;
    var result = new object();
    object.prototype = void 0;
    return result;
  };
}();
var _baseCreate = baseCreate$1;
var baseCreate = _baseCreate, getPrototype = _getPrototype, isPrototype = _isPrototype;
function initCloneObject$1(object) {
  return typeof object.constructor == "function" && !isPrototype(object) ? baseCreate(getPrototype(object)) : {};
}
var _initCloneObject = initCloneObject$1;
var getTag$2 = _getTag, isObjectLike$2 = isObjectLike_1;
var mapTag$1 = "[object Map]";
function baseIsMap$1(value) {
  return isObjectLike$2(value) && getTag$2(value) == mapTag$1;
}
var _baseIsMap = baseIsMap$1;
var baseIsMap = _baseIsMap, baseUnary$1 = _baseUnary, nodeUtil$1 = _nodeUtilExports;
var nodeIsMap = nodeUtil$1 && nodeUtil$1.isMap;
var isMap$1 = nodeIsMap ? baseUnary$1(nodeIsMap) : baseIsMap;
var isMap_1 = isMap$1;
var getTag$1 = _getTag, isObjectLike$1 = isObjectLike_1;
var setTag$1 = "[object Set]";
function baseIsSet$1(value) {
  return isObjectLike$1(value) && getTag$1(value) == setTag$1;
}
var _baseIsSet = baseIsSet$1;
var baseIsSet = _baseIsSet, baseUnary = _baseUnary, nodeUtil = _nodeUtilExports;
var nodeIsSet = nodeUtil && nodeUtil.isSet;
var isSet$1 = nodeIsSet ? baseUnary(nodeIsSet) : baseIsSet;
var isSet_1 = isSet$1;
var Stack = _Stack, arrayEach = _arrayEach, assignValue = _assignValue, baseAssign = _baseAssign, baseAssignIn = _baseAssignIn, cloneBuffer = _cloneBufferExports, copyArray = _copyArray, copySymbols = _copySymbols, copySymbolsIn = _copySymbolsIn, getAllKeys = _getAllKeys, getAllKeysIn = _getAllKeysIn, getTag = _getTag, initCloneArray = _initCloneArray, initCloneByTag = _initCloneByTag, initCloneObject = _initCloneObject, isArray$3 = isArray_1, isBuffer = isBufferExports, isMap = isMap_1, isObject = isObject_1, isSet = isSet_1, keys = keys_1, keysIn = keysIn_1;
var CLONE_DEEP_FLAG$1 = 1, CLONE_FLAT_FLAG = 2, CLONE_SYMBOLS_FLAG$1 = 4;
var argsTag = "[object Arguments]", arrayTag = "[object Array]", boolTag = "[object Boolean]", dateTag = "[object Date]", errorTag = "[object Error]", funcTag = "[object Function]", genTag = "[object GeneratorFunction]", mapTag = "[object Map]", numberTag = "[object Number]", objectTag = "[object Object]", regexpTag = "[object RegExp]", setTag = "[object Set]", stringTag = "[object String]", symbolTag$1 = "[object Symbol]", weakMapTag = "[object WeakMap]";
var arrayBufferTag = "[object ArrayBuffer]", dataViewTag = "[object DataView]", float32Tag = "[object Float32Array]", float64Tag = "[object Float64Array]", int8Tag = "[object Int8Array]", int16Tag = "[object Int16Array]", int32Tag = "[object Int32Array]", uint8Tag = "[object Uint8Array]", uint8ClampedTag = "[object Uint8ClampedArray]", uint16Tag = "[object Uint16Array]", uint32Tag = "[object Uint32Array]";
var cloneableTags = {};
cloneableTags[argsTag] = cloneableTags[arrayTag] = cloneableTags[arrayBufferTag] = cloneableTags[dataViewTag] = cloneableTags[boolTag] = cloneableTags[dateTag] = cloneableTags[float32Tag] = cloneableTags[float64Tag] = cloneableTags[int8Tag] = cloneableTags[int16Tag] = cloneableTags[int32Tag] = cloneableTags[mapTag] = cloneableTags[numberTag] = cloneableTags[objectTag] = cloneableTags[regexpTag] = cloneableTags[setTag] = cloneableTags[stringTag] = cloneableTags[symbolTag$1] = cloneableTags[uint8Tag] = cloneableTags[uint8ClampedTag] = cloneableTags[uint16Tag] = cloneableTags[uint32Tag] = true;
cloneableTags[errorTag] = cloneableTags[funcTag] = cloneableTags[weakMapTag] = false;
function baseClone$1(value, bitmask, customizer, key, object, stack) {
  var result, isDeep = bitmask & CLONE_DEEP_FLAG$1, isFlat = bitmask & CLONE_FLAT_FLAG, isFull = bitmask & CLONE_SYMBOLS_FLAG$1;
  if (customizer) {
    result = object ? customizer(value, key, object, stack) : customizer(value);
  }
  if (result !== void 0) {
    return result;
  }
  if (!isObject(value)) {
    return value;
  }
  var isArr = isArray$3(value);
  if (isArr) {
    result = initCloneArray(value);
    if (!isDeep) {
      return copyArray(value, result);
    }
  } else {
    var tag = getTag(value), isFunc = tag == funcTag || tag == genTag;
    if (isBuffer(value)) {
      return cloneBuffer(value, isDeep);
    }
    if (tag == objectTag || tag == argsTag || isFunc && !object) {
      result = isFlat || isFunc ? {} : initCloneObject(value);
      if (!isDeep) {
        return isFlat ? copySymbolsIn(value, baseAssignIn(result, value)) : copySymbols(value, baseAssign(result, value));
      }
    } else {
      if (!cloneableTags[tag]) {
        return object ? value : {};
      }
      result = initCloneByTag(value, tag, isDeep);
    }
  }
  stack || (stack = new Stack());
  var stacked = stack.get(value);
  if (stacked) {
    return stacked;
  }
  stack.set(value, result);
  if (isSet(value)) {
    value.forEach(function(subValue) {
      result.add(baseClone$1(subValue, bitmask, customizer, subValue, value, stack));
    });
  } else if (isMap(value)) {
    value.forEach(function(subValue, key2) {
      result.set(key2, baseClone$1(subValue, bitmask, customizer, key2, value, stack));
    });
  }
  var keysFunc = isFull ? isFlat ? getAllKeysIn : getAllKeys : isFlat ? keysIn : keys;
  var props = isArr ? void 0 : keysFunc(value);
  arrayEach(props || value, function(subValue, key2) {
    if (props) {
      key2 = subValue;
      subValue = value[key2];
    }
    assignValue(result, key2, baseClone$1(subValue, bitmask, customizer, key2, value, stack));
  });
  return result;
}
var _baseClone = baseClone$1;
var baseClone = _baseClone;
var CLONE_DEEP_FLAG = 1, CLONE_SYMBOLS_FLAG = 4;
function cloneDeep(value) {
  return baseClone(value, CLONE_DEEP_FLAG | CLONE_SYMBOLS_FLAG);
}
var cloneDeep_1 = cloneDeep;
const _cloneDeep = /* @__PURE__ */ getDefaultExportFromCjs(cloneDeep_1);
function validateFactory(queryFactory) {
  if (typeof queryFactory !== "function") {
    console.log("validateFactory", queryFactory);
    throw new Error(`QueryFactory must be a function`);
  }
  if (queryFactory.params && typeof queryFactory !== "object") {
    console.log("validateFactory", queryFactory);
    throw new Error(`If QueryFactory has a property or getter 'params', it must be an object`);
  }
}
function useFirestoreQuery(queryFactory) {
  validateFactory(queryFactory);
  let queryFactoryResult;
  const queryResult = ref(null);
  const queryParams = ref(_cloneDeep(queryFactory.params));
  function updateQuery(params) {
    queryFactoryResult = queryFactory(params);
  }
  function normalizeDoc(doc) {
    return {
      data: doc.data(),
      id: doc.id,
      path: doc.path,
      ref() {
        return doc.ref;
      }
    };
  }
  function runQuery(params) {
    if (params !== void 0 || !queryFactoryResult) {
      updateQuery(params);
    }
    if (!queryFactoryResult.valid) {
      console.log("queryFactoryResult", queryFactoryResult);
      throw new Error(`queryFactoryResult is invalid, cannot run query`);
    }
    queryFactoryResult.ref.get().then((querySnap) => {
      if (querySnap.empty !== void 0) {
        queryResult.value = querySnap.docs.map((d) => normalizeDoc(d));
      } else {
        const docSnap = querySnap;
        queryResult.value = normalizeDoc(docSnap);
      }
    }).catch((err) => {
      console.log(queryFactory);
      console.warn("An error occurred while running a query");
      console.error(err);
    });
  }
  onMounted(() => {
    runQuery();
  });
  onUpdated(() => {
    runQuery();
  });
  return { runQuery, queryResult, queryParams };
}
function normalizeListItems(props) {
  const normalized = ref([]);
  watchEffect(() => {
    const normalizedRaw = props.items.map((i) => {
      if ("data" in i && i.data) {
        return i;
      } else {
        return {
          data: typeof i === "object" ? i : { id: i },
          get id() {
            return this.data.id || this.data.$id || this.data.name;
          },
          get name() {
            return this.data.name || this.data.id || this.data.$id;
          },
          active: false
        };
      }
    });
    while (normalized.value.length > 0) {
      normalized.value.pop();
    }
    for (const item of normalizedRaw) {
      normalized.value.push(item);
    }
  });
  return normalized;
}
var FeatureFlags;
((FeatureFlags2) => {
  const ICON = `url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAYAAAAeP4ixAAAABmJLR0QA/wD/AP+gvaeTAAADR0lEQVRoge1ZPU8UURQ9d2a/kCUadBNUCoRVCqJAEALdGrRA+TKw0upfsLKkMrb+ABMTC2PQ0thSGEMjwYLEiCZEGwyGgJJAWOYdCwwQXXbe1yIFJ9lq3v04e8+8d98d4BjHqArElyN2jBcJeWQWnA9k7uWUj/gJH04AgCITAJqNbCB3AHgh4qUizA+kmc0uQ1BnaLouJ2tzMv100zWHwNUBACBb129BAgCyWFu/5iMFL0SUYNjaFva2++FMhIAIeMvWXiDD9CBx94p0TFwF0Ojg4Rw6x7pc03AmooTO0lAQZx/ORAQccfchzj6czhG+6W3Cx6UUfoURFUIbHxIgQl2U5sPeJhmYWbTNxe1AzK+NMo9WFxcEQgCtQowAeGzrx0laBJwlsetL3ORlve1xvq2eKfUd/tqcSFTUIK2fftgY21ckrQbhsVcDECIIB2yNrYn4lNWeT3t5Wf2jLBQy/Lbagq1gGRthPSx3rF0EiFATrSCl8iwUMjI9bdxE2knj55nruJ9rt7ItjxBADkAORD+A16YOrKTlq9Hz6duYCAERyk2bYDoQwZBNE2leka6xHgDnje30cRbtxW5TI2Miiu4NXmwMi/uNMRGhe4MXGyMw39qNtMjusWZuB19Mg9hABBdlduqz7nqzikRh1auxBw6arDYiQrpforRjGV62tKXF+bZ6gh+wHdRgK8iwJBmh44n+dwxBJEluIqU2kVAbAmmXtvkVHVv9kz2tBkE0IhUBJyJ/I8p9kJ0TvvbPD9gZajzTsdWWVjWaxPiY+juk1h/LhXyaSC4DVkM4F6xLYiMnFxZjm0i9ijBxA4dPAgCyKNX06yzUIqIcr6EuUKIn6diXnZgM+OTtZQRqFSU55Z6aAZJcFRVcISYCwaSqtDT2HWFnsY/AO3/ZmUMk6JPZFzOV1sRKq5p3D10ojYM4loj8h2333xzip5kVpcWuiRYqpd24VRNCdUnmXi0c9LxyRVQ06j0jW0gwVOlxRSKmjVs1wZh39UBpsef2aZYSS/A7hHNBJEGiQd4/LzuJPLgiW0nfk0RXhFClA4ceCXaMF8o9INRdj5/hvYDEPXaMfy33TFRnkYedUDXg5/P0EcAxkaOGYyJHDb8Bn47flXuY46sAAAAASUVORK5CYII=')`;
  const CSS = `.feature-flag-disabled {
        filter: grayscale(1);
    }
    .feature-flag-disabled:after {
        width: 25px;
        height: 25px;
        display:block;
        position:absolute;
        content:'';
        background: ${ICON};
        background-size: 65%;
        background-position:50% 50%;
        background-repeat: no-repeat;
        border:orange solid 2px;
        border-radius:100%;
    }`;
  const CSS_BLOCK = document.createElement("style");
  CSS_BLOCK.type = "text/css";
  CSS_BLOCK.appendChild(document.createTextNode(CSS));
  let DirectiveArgs;
  ((DirectiveArgs2) => {
    DirectiveArgs2["show"] = "show";
    DirectiveArgs2["enable"] = "enable";
  })(DirectiveArgs || (DirectiveArgs = {}));
  const vars = {
    flags: null,
    contextualFlags: {},
    environment: void 0,
    namedConditions: {}
  };
  function listFeatures() {
    console.log("Feature Flags:", vars.flags);
  }
  const ensureContext = function ensureContext2(context) {
    if (!vars.contextualFlags[context]) {
      vars.contextualFlags[context] = {
        conditions: [],
        flags: {}
      };
    }
    if (!vars.contextualFlags[context]) {
      throw new Error(`Could not find context ${context}`);
    }
    return vars.contextualFlags[context];
  };
  const resolveFlagSet = function resolveFlagSet2(context) {
    let flagSet;
    let contextualFlags = null;
    if (context) {
      const contextObj = ensureContext(context);
      contextualFlags = contextObj.flags;
    }
    if (context && contextualFlags) {
      flagSet = contextualFlags;
    } else if (!context && vars.flags) {
      flagSet = vars.flags;
    } else {
      console.warn(context ? `Could not resolve feature flags for ${context}` : `Could not resolve feature flags`);
      throw new Error("Could not resolve feature flags");
    }
    return flagSet;
  };
  const meetsContextConditions = async function meetsContextConditions2(context) {
    const contextObj = ensureContext(context);
    const allMatched = true;
    for (let i = 0; i < contextObj.conditions.length; i++) {
      const condition = contextObj.conditions[i];
      const result = await condition();
      if (!result) {
        return false;
      }
    }
    return allMatched;
  };
  const isFeatureFlagTrue = function isFeatureFlagTrue2(lookup) {
    const { feature, condition, context, args } = lookup;
    const flagSet = resolveFlagSet(context);
    if (vars.flags === null) {
      console.warn(`FeatureFlags has no hash configured. Cannot check if feature "${feature}" is enabled.`);
      return false;
    } else {
      const result = flagSet[feature];
      if (result === void 0) {
        console.warn(`No Feature Flag named "${feature}" was found. Assuming disabled.`);
      }
      return !!result || false;
    }
  };
  const isFeatureEnabledWithCondition = async function isFeatureEnabledWithCondition2(lookup) {
    const { feature, condition, context, args } = lookup;
    if (vars.flags === null) {
      throw new Error(`FeatureFlags has no hash configured. Cannot check if feature "${name}" is enabled.`);
    } else {
      const featureFlagCheck = isFeatureFlagTrue(lookup);
      if (featureFlagCheck) {
        if (!condition) {
          throw new Error(`Feature "${feature}" is enabled, but no condition was provided.`);
        } else if (typeof condition === "function") {
          return Promise.resolve(condition());
        } else if (!vars.namedConditions[condition]) {
          throw new Error(`Named condition "${condition}" does not exist.`);
        } else if (condition && args && args instanceof Array) {
          return Promise.resolve(vars.namedConditions[condition](...args));
        } else if (condition) {
          return Promise.resolve(vars.namedConditions[condition]());
        } else {
          throw new Error(`Feature "${feature}" is enabled, but no condition could be resolved.`);
        }
      } else {
        return Promise.resolve(false);
      }
    }
  };
  function registerCondition({ name: name2, context }, condition) {
    if (!name2 && !context) {
      throw new Error("You must provide a condition name or a context or both.");
    }
    if (name2) {
      vars.namedConditions[name2] = condition;
    }
    if (context) {
      ensureContext(context);
      vars.contextualFlags[context].conditions.push(condition);
    }
  }
  FeatureFlags2.registerCondition = registerCondition;
  function isFeatureEnabled(featureOrLookup) {
    let lookup;
    if (typeof featureOrLookup === "string") {
      lookup = { feature: featureOrLookup };
    } else {
      lookup = featureOrLookup;
    }
    if (lookup.condition || lookup.context) {
      const { context } = lookup;
      const contextPromise = context ? meetsContextConditions(context) : Promise.resolve(true);
      return contextPromise.then((r) => {
        if (r === true) {
          if (lookup.condition) {
            return isFeatureEnabledWithCondition(lookup);
          } else {
            return isFeatureFlagTrue(lookup);
          }
        } else {
          return isFeatureFlagTrue({
            ...lookup,
            context: void 0
          });
        }
      });
    } else {
      return isFeatureFlagTrue(lookup);
    }
  }
  FeatureFlags2.isFeatureEnabled = isFeatureEnabled;
  FeatureFlags2.setFlags = function setFlags2(flags) {
    vars.flags = flags;
  };
  FeatureFlags2.setFlagsForContext = function setFlagsForContext2(context, flags) {
    if (!vars.flags) {
      throw new Error("Feature Flags have not been set yet. Cannot set contextual flags before normal flags.");
    }
    ensureContext(context);
    vars.contextualFlags[context].flags = {
      ...vars.flags,
      ...flags
    };
  };
  const mergeFeatureFlagList = function mergeFeatureFlagList2(priorityList, secondaryList) {
    const mergedList = {
      flags: {},
      contextualFlags: {}
    };
    mergedList.flags = {
      ...secondaryList.flags,
      ...priorityList.flags
    };
    mergedList.contextualFlags = secondaryList.contextualFlags;
    Object.keys(priorityList.contextualFlags).forEach((context) => {
      var _a;
      if (!((_a = mergedList.contextualFlags) == null ? void 0 : _a[context])) {
        mergedList.contextualFlags[context] = {
          flags: {},
          conditions: []
        };
      }
      mergedList.contextualFlags[context].flags = {
        ...mergedList.contextualFlags[context].flags,
        ...priorityList.contextualFlags[context].flags
      };
    });
    Object.keys(mergedList.contextualFlags).forEach((context) => {
      mergedList.contextualFlags[context].flags = {
        ...mergedList.flags,
        ...mergedList.contextualFlags[context].flags
      };
    });
    return mergedList;
  };
  class FirestoreFeatureFlagSource {
    constructor(db, collectionName = "feature-flags") {
      __publicField(this, "db");
      __publicField(this, "collectionName");
      __publicField(this, "cleanupFunc");
      __publicField(this, "flagDocuments");
      __publicField(this, "firestoreConverter", {
        toFirestore(modelObject) {
          return modelObject;
        },
        fromFirestore(snapshot) {
          const snapData = snapshot.data();
          const data = {
            priority: snapData.priority || 0,
            flags: snapData.flags || {},
            contextualFlags: snapData.contextualFlags || {}
          };
          return data;
        }
      });
      this.db = db;
      this.collectionName = collectionName;
      this.flagDocuments = [];
    }
    addDoc(id, flagDoc) {
      this.flagDocuments.push({ $id: id, ...flagDoc });
    }
    removeDoc(id) {
      const idx = this.flagDocuments.findIndex((f) => f.$id === id);
      if (idx >= 0) {
        this.flagDocuments.splice(idx, 1);
      } else {
        console.warn(`Cannot remove Feature Flag document ${id} because it doesnt exist`);
      }
    }
    updateDoc(id, newDoc) {
      const idx = this.flagDocuments.findIndex((f) => f.$id === id);
      if (idx >= 0) {
        this.flagDocuments[idx] = { $id: id, ...newDoc };
      } else {
        console.warn(`Cannot update Feature Flag document ${id} because it doesnt exist`);
        this.flagDocuments.push({ $id: id, ...newDoc });
      }
    }
    //Gets the resolved list of feature flags from the combination documents
    resolveFirebaseFlags() {
      let featureFlagObject = {
        flags: {},
        contextualFlags: {}
      };
      this.flagDocuments.sort((a, b) => a.priority - b.priority).forEach((doc) => {
        featureFlagObject = mergeFeatureFlagList(doc, featureFlagObject);
      });
      return featureFlagObject;
    }
    async initialize(options) {
      const col = collection(this.db, this.collectionName).withConverter(this.firestoreConverter);
      let resolvedOnce = false;
      console.log("[Feature Flags] Initializing Firestore Feature Flags");
      await new Promise((res) => {
        if (this.cleanupFunc) this.cleanupFunc();
        this.cleanupFunc = onSnapshot(col, (snap) => {
          for (const change of snap.docChanges()) {
            const { id } = change.doc;
            try {
              const data = change.doc.data();
              switch (change.type) {
                case "added":
                  this.addDoc(id, data);
                  break;
                case "removed":
                  this.removeDoc(id);
                  break;
                case "modified":
                  this.updateDoc(id, data);
                  break;
              }
            } catch (e) {
              console.error(`Error while handling Feature Flag document ${id}`, e);
            }
          }
          if (options.overrideExisting) {
            const updatedFlags = mergeFeatureFlagList(this.resolveFirebaseFlags(), vars);
            vars.flags = updatedFlags.flags;
            vars.contextualFlags = updatedFlags.contextualFlags;
          } else {
            const updatedFlags = mergeFeatureFlagList(vars, this.resolveFirebaseFlags());
            vars.flags = updatedFlags.flags;
            vars.contextualFlags = updatedFlags.contextualFlags;
          }
          if (!resolvedOnce) {
            res();
            resolvedOnce = true;
          }
        });
      });
    }
    cleanup() {
      var _a;
      (_a = this.cleanupFunc) == null ? void 0 : _a.call(this);
      return Promise.resolve();
    }
  }
  FeatureFlags2.FirestoreFeatureFlagSource = FirestoreFeatureFlagSource;
  FeatureFlags2.setFlagsFromFirebase = async function setFlagsFromFirebase2(source, options) {
    await source.initialize(options);
  };
  FeatureFlags2.install = (vueApp, options) => {
    var _a;
    (_a = document.querySelector("body")) == null ? void 0 : _a.append(CSS_BLOCK);
    if (!options || !options.flags) {
      console.warn(`FeatureFlags has no hash configured. A feature hash is required to use FeatureFlags.`);
    }
    vars.environment = options.env;
    if (options.flags) {
      vars.flags = options.flags;
    }
    if (vars.environment && vars.environment !== "prod" && vars.environment !== "production") {
      listFeatures();
    }
    vueApp.directive("if-feature", {
      mounted: (el, binding) => {
        console.log("if-feature is mounted");
        const featureName = binding.value;
        const operator = binding.arg ? binding.arg : "show";
        const initialDisplay = el.style.display || "";
        if (operator === "enable") {
          if (isFeatureEnabled(featureName)) {
            el.removeAttribute("disabled");
            el.classList.remove("feature-flag-disabled");
          } else {
            el.setAttribute("disabled", "true");
            el.classList.add("feature-flag-disabled");
          }
        } else if (operator === "show") {
          if (isFeatureEnabled(featureName)) {
            el.style.display = initialDisplay;
          } else {
            el.style.display = "none";
          }
        }
      }
    });
  };
  FeatureFlags2.plugin = {
    install: FeatureFlags2.install
  };
})(FeatureFlags || (FeatureFlags = {}));
function shouldShowForPlatform(operator, platforms) {
  const currentPlatform = PLATFORM || "web";
  if (!operator || operator === "only") {
    return platforms.includes(currentPlatform);
  } else if (operator === "exclude") {
    return !platforms.includes(currentPlatform);
  }
}
const PlatformPlugin = {
  install: (vueApp) => {
    vueApp.directive("if-platform", {
      mounted(el, binding) {
        console.log("if-platform");
        const platforms = binding.value instanceof Array ? binding.value : [binding.value];
        const operator = binding.arg ? binding.arg : "only";
        const cachedStyle = el.style.display;
        if (shouldShowForPlatform(operator, platforms)) {
          el.style.display = cachedStyle;
        } else {
          el.style.display = "none";
        }
      }
    });
  }
};
var re$2 = { exports: {} };
const SEMVER_SPEC_VERSION = "2.0.0";
const MAX_LENGTH$1 = 256;
const MAX_SAFE_INTEGER$1 = Number.MAX_SAFE_INTEGER || /* istanbul ignore next */
9007199254740991;
const MAX_SAFE_COMPONENT_LENGTH = 16;
const MAX_SAFE_BUILD_LENGTH = MAX_LENGTH$1 - 6;
const RELEASE_TYPES = [
  "major",
  "premajor",
  "minor",
  "preminor",
  "patch",
  "prepatch",
  "prerelease"
];
var constants$1 = {
  MAX_LENGTH: MAX_LENGTH$1,
  MAX_SAFE_COMPONENT_LENGTH,
  MAX_SAFE_BUILD_LENGTH,
  MAX_SAFE_INTEGER: MAX_SAFE_INTEGER$1,
  RELEASE_TYPES,
  SEMVER_SPEC_VERSION,
  FLAG_INCLUDE_PRERELEASE: 1,
  FLAG_LOOSE: 2
};
const debug$1 = typeof process === "object" && process.env && process.env.NODE_DEBUG && /\bsemver\b/i.test(process.env.NODE_DEBUG) ? (...args) => console.error("SEMVER", ...args) : () => {
};
var debug_1 = debug$1;
(function(module, exports) {
  const {
    MAX_SAFE_COMPONENT_LENGTH: MAX_SAFE_COMPONENT_LENGTH2,
    MAX_SAFE_BUILD_LENGTH: MAX_SAFE_BUILD_LENGTH2,
    MAX_LENGTH: MAX_LENGTH2
  } = constants$1;
  const debug2 = debug_1;
  exports = module.exports = {};
  const re2 = exports.re = [];
  const safeRe = exports.safeRe = [];
  const src = exports.src = [];
  const t2 = exports.t = {};
  let R = 0;
  const LETTERDASHNUMBER = "[a-zA-Z0-9-]";
  const safeRegexReplacements = [
    ["\\s", 1],
    ["\\d", MAX_LENGTH2],
    [LETTERDASHNUMBER, MAX_SAFE_BUILD_LENGTH2]
  ];
  const makeSafeRegex = (value) => {
    for (const [token, max] of safeRegexReplacements) {
      value = value.split(`${token}*`).join(`${token}{0,${max}}`).split(`${token}+`).join(`${token}{1,${max}}`);
    }
    return value;
  };
  const createToken = (name2, value, isGlobal) => {
    const safe = makeSafeRegex(value);
    const index = R++;
    debug2(name2, index, value);
    t2[name2] = index;
    src[index] = value;
    re2[index] = new RegExp(value, isGlobal ? "g" : void 0);
    safeRe[index] = new RegExp(safe, isGlobal ? "g" : void 0);
  };
  createToken("NUMERICIDENTIFIER", "0|[1-9]\\d*");
  createToken("NUMERICIDENTIFIERLOOSE", "\\d+");
  createToken("NONNUMERICIDENTIFIER", `\\d*[a-zA-Z-]${LETTERDASHNUMBER}*`);
  createToken("MAINVERSION", `(${src[t2.NUMERICIDENTIFIER]})\\.(${src[t2.NUMERICIDENTIFIER]})\\.(${src[t2.NUMERICIDENTIFIER]})`);
  createToken("MAINVERSIONLOOSE", `(${src[t2.NUMERICIDENTIFIERLOOSE]})\\.(${src[t2.NUMERICIDENTIFIERLOOSE]})\\.(${src[t2.NUMERICIDENTIFIERLOOSE]})`);
  createToken("PRERELEASEIDENTIFIER", `(?:${src[t2.NUMERICIDENTIFIER]}|${src[t2.NONNUMERICIDENTIFIER]})`);
  createToken("PRERELEASEIDENTIFIERLOOSE", `(?:${src[t2.NUMERICIDENTIFIERLOOSE]}|${src[t2.NONNUMERICIDENTIFIER]})`);
  createToken("PRERELEASE", `(?:-(${src[t2.PRERELEASEIDENTIFIER]}(?:\\.${src[t2.PRERELEASEIDENTIFIER]})*))`);
  createToken("PRERELEASELOOSE", `(?:-?(${src[t2.PRERELEASEIDENTIFIERLOOSE]}(?:\\.${src[t2.PRERELEASEIDENTIFIERLOOSE]})*))`);
  createToken("BUILDIDENTIFIER", `${LETTERDASHNUMBER}+`);
  createToken("BUILD", `(?:\\+(${src[t2.BUILDIDENTIFIER]}(?:\\.${src[t2.BUILDIDENTIFIER]})*))`);
  createToken("FULLPLAIN", `v?${src[t2.MAINVERSION]}${src[t2.PRERELEASE]}?${src[t2.BUILD]}?`);
  createToken("FULL", `^${src[t2.FULLPLAIN]}$`);
  createToken("LOOSEPLAIN", `[v=\\s]*${src[t2.MAINVERSIONLOOSE]}${src[t2.PRERELEASELOOSE]}?${src[t2.BUILD]}?`);
  createToken("LOOSE", `^${src[t2.LOOSEPLAIN]}$`);
  createToken("GTLT", "((?:<|>)?=?)");
  createToken("XRANGEIDENTIFIERLOOSE", `${src[t2.NUMERICIDENTIFIERLOOSE]}|x|X|\\*`);
  createToken("XRANGEIDENTIFIER", `${src[t2.NUMERICIDENTIFIER]}|x|X|\\*`);
  createToken("XRANGEPLAIN", `[v=\\s]*(${src[t2.XRANGEIDENTIFIER]})(?:\\.(${src[t2.XRANGEIDENTIFIER]})(?:\\.(${src[t2.XRANGEIDENTIFIER]})(?:${src[t2.PRERELEASE]})?${src[t2.BUILD]}?)?)?`);
  createToken("XRANGEPLAINLOOSE", `[v=\\s]*(${src[t2.XRANGEIDENTIFIERLOOSE]})(?:\\.(${src[t2.XRANGEIDENTIFIERLOOSE]})(?:\\.(${src[t2.XRANGEIDENTIFIERLOOSE]})(?:${src[t2.PRERELEASELOOSE]})?${src[t2.BUILD]}?)?)?`);
  createToken("XRANGE", `^${src[t2.GTLT]}\\s*${src[t2.XRANGEPLAIN]}$`);
  createToken("XRANGELOOSE", `^${src[t2.GTLT]}\\s*${src[t2.XRANGEPLAINLOOSE]}$`);
  createToken("COERCEPLAIN", `${"(^|[^\\d])(\\d{1,"}${MAX_SAFE_COMPONENT_LENGTH2}})(?:\\.(\\d{1,${MAX_SAFE_COMPONENT_LENGTH2}}))?(?:\\.(\\d{1,${MAX_SAFE_COMPONENT_LENGTH2}}))?`);
  createToken("COERCE", `${src[t2.COERCEPLAIN]}(?:$|[^\\d])`);
  createToken("COERCEFULL", src[t2.COERCEPLAIN] + `(?:${src[t2.PRERELEASE]})?(?:${src[t2.BUILD]})?(?:$|[^\\d])`);
  createToken("COERCERTL", src[t2.COERCE], true);
  createToken("COERCERTLFULL", src[t2.COERCEFULL], true);
  createToken("LONETILDE", "(?:~>?)");
  createToken("TILDETRIM", `(\\s*)${src[t2.LONETILDE]}\\s+`, true);
  exports.tildeTrimReplace = "$1~";
  createToken("TILDE", `^${src[t2.LONETILDE]}${src[t2.XRANGEPLAIN]}$`);
  createToken("TILDELOOSE", `^${src[t2.LONETILDE]}${src[t2.XRANGEPLAINLOOSE]}$`);
  createToken("LONECARET", "(?:\\^)");
  createToken("CARETTRIM", `(\\s*)${src[t2.LONECARET]}\\s+`, true);
  exports.caretTrimReplace = "$1^";
  createToken("CARET", `^${src[t2.LONECARET]}${src[t2.XRANGEPLAIN]}$`);
  createToken("CARETLOOSE", `^${src[t2.LONECARET]}${src[t2.XRANGEPLAINLOOSE]}$`);
  createToken("COMPARATORLOOSE", `^${src[t2.GTLT]}\\s*(${src[t2.LOOSEPLAIN]})$|^$`);
  createToken("COMPARATOR", `^${src[t2.GTLT]}\\s*(${src[t2.FULLPLAIN]})$|^$`);
  createToken("COMPARATORTRIM", `(\\s*)${src[t2.GTLT]}\\s*(${src[t2.LOOSEPLAIN]}|${src[t2.XRANGEPLAIN]})`, true);
  exports.comparatorTrimReplace = "$1$2$3";
  createToken("HYPHENRANGE", `^\\s*(${src[t2.XRANGEPLAIN]})\\s+-\\s+(${src[t2.XRANGEPLAIN]})\\s*$`);
  createToken("HYPHENRANGELOOSE", `^\\s*(${src[t2.XRANGEPLAINLOOSE]})\\s+-\\s+(${src[t2.XRANGEPLAINLOOSE]})\\s*$`);
  createToken("STAR", "(<|>)?=?\\s*\\*");
  createToken("GTE0", "^\\s*>=\\s*0\\.0\\.0\\s*$");
  createToken("GTE0PRE", "^\\s*>=\\s*0\\.0\\.0-0\\s*$");
})(re$2, re$2.exports);
var reExports = re$2.exports;
const looseOption = Object.freeze({ loose: true });
const emptyOpts = Object.freeze({});
const parseOptions$1 = (options) => {
  if (!options) {
    return emptyOpts;
  }
  if (typeof options !== "object") {
    return looseOption;
  }
  return options;
};
var parseOptions_1 = parseOptions$1;
const numeric = /^[0-9]+$/;
const compareIdentifiers$1 = (a, b) => {
  const anum = numeric.test(a);
  const bnum = numeric.test(b);
  if (anum && bnum) {
    a = +a;
    b = +b;
  }
  return a === b ? 0 : anum && !bnum ? -1 : bnum && !anum ? 1 : a < b ? -1 : 1;
};
const rcompareIdentifiers = (a, b) => compareIdentifiers$1(b, a);
var identifiers$1 = {
  compareIdentifiers: compareIdentifiers$1,
  rcompareIdentifiers
};
const debug = debug_1;
const { MAX_LENGTH, MAX_SAFE_INTEGER } = constants$1;
const { safeRe: re$1, t: t$1 } = reExports;
const parseOptions = parseOptions_1;
const { compareIdentifiers } = identifiers$1;
let SemVer$d = class SemVer {
  constructor(version, options) {
    options = parseOptions(options);
    if (version instanceof SemVer) {
      if (version.loose === !!options.loose && version.includePrerelease === !!options.includePrerelease) {
        return version;
      } else {
        version = version.version;
      }
    } else if (typeof version !== "string") {
      throw new TypeError(`Invalid version. Must be a string. Got type "${typeof version}".`);
    }
    if (version.length > MAX_LENGTH) {
      throw new TypeError(
        `version is longer than ${MAX_LENGTH} characters`
      );
    }
    debug("SemVer", version, options);
    this.options = options;
    this.loose = !!options.loose;
    this.includePrerelease = !!options.includePrerelease;
    const m = version.trim().match(options.loose ? re$1[t$1.LOOSE] : re$1[t$1.FULL]);
    if (!m) {
      throw new TypeError(`Invalid Version: ${version}`);
    }
    this.raw = version;
    this.major = +m[1];
    this.minor = +m[2];
    this.patch = +m[3];
    if (this.major > MAX_SAFE_INTEGER || this.major < 0) {
      throw new TypeError("Invalid major version");
    }
    if (this.minor > MAX_SAFE_INTEGER || this.minor < 0) {
      throw new TypeError("Invalid minor version");
    }
    if (this.patch > MAX_SAFE_INTEGER || this.patch < 0) {
      throw new TypeError("Invalid patch version");
    }
    if (!m[4]) {
      this.prerelease = [];
    } else {
      this.prerelease = m[4].split(".").map((id) => {
        if (/^[0-9]+$/.test(id)) {
          const num = +id;
          if (num >= 0 && num < MAX_SAFE_INTEGER) {
            return num;
          }
        }
        return id;
      });
    }
    this.build = m[5] ? m[5].split(".") : [];
    this.format();
  }
  format() {
    this.version = `${this.major}.${this.minor}.${this.patch}`;
    if (this.prerelease.length) {
      this.version += `-${this.prerelease.join(".")}`;
    }
    return this.version;
  }
  toString() {
    return this.version;
  }
  compare(other) {
    debug("SemVer.compare", this.version, this.options, other);
    if (!(other instanceof SemVer)) {
      if (typeof other === "string" && other === this.version) {
        return 0;
      }
      other = new SemVer(other, this.options);
    }
    if (other.version === this.version) {
      return 0;
    }
    return this.compareMain(other) || this.comparePre(other);
  }
  compareMain(other) {
    if (!(other instanceof SemVer)) {
      other = new SemVer(other, this.options);
    }
    return compareIdentifiers(this.major, other.major) || compareIdentifiers(this.minor, other.minor) || compareIdentifiers(this.patch, other.patch);
  }
  comparePre(other) {
    if (!(other instanceof SemVer)) {
      other = new SemVer(other, this.options);
    }
    if (this.prerelease.length && !other.prerelease.length) {
      return -1;
    } else if (!this.prerelease.length && other.prerelease.length) {
      return 1;
    } else if (!this.prerelease.length && !other.prerelease.length) {
      return 0;
    }
    let i = 0;
    do {
      const a = this.prerelease[i];
      const b = other.prerelease[i];
      debug("prerelease compare", i, a, b);
      if (a === void 0 && b === void 0) {
        return 0;
      } else if (b === void 0) {
        return 1;
      } else if (a === void 0) {
        return -1;
      } else if (a === b) {
        continue;
      } else {
        return compareIdentifiers(a, b);
      }
    } while (++i);
  }
  compareBuild(other) {
    if (!(other instanceof SemVer)) {
      other = new SemVer(other, this.options);
    }
    let i = 0;
    do {
      const a = this.build[i];
      const b = other.build[i];
      debug("build compare", i, a, b);
      if (a === void 0 && b === void 0) {
        return 0;
      } else if (b === void 0) {
        return 1;
      } else if (a === void 0) {
        return -1;
      } else if (a === b) {
        continue;
      } else {
        return compareIdentifiers(a, b);
      }
    } while (++i);
  }
  // preminor will bump the version up to the next minor release, and immediately
  // down to pre-release. premajor and prepatch work the same way.
  inc(release, identifier, identifierBase) {
    switch (release) {
      case "premajor":
        this.prerelease.length = 0;
        this.patch = 0;
        this.minor = 0;
        this.major++;
        this.inc("pre", identifier, identifierBase);
        break;
      case "preminor":
        this.prerelease.length = 0;
        this.patch = 0;
        this.minor++;
        this.inc("pre", identifier, identifierBase);
        break;
      case "prepatch":
        this.prerelease.length = 0;
        this.inc("patch", identifier, identifierBase);
        this.inc("pre", identifier, identifierBase);
        break;
      case "prerelease":
        if (this.prerelease.length === 0) {
          this.inc("patch", identifier, identifierBase);
        }
        this.inc("pre", identifier, identifierBase);
        break;
      case "major":
        if (this.minor !== 0 || this.patch !== 0 || this.prerelease.length === 0) {
          this.major++;
        }
        this.minor = 0;
        this.patch = 0;
        this.prerelease = [];
        break;
      case "minor":
        if (this.patch !== 0 || this.prerelease.length === 0) {
          this.minor++;
        }
        this.patch = 0;
        this.prerelease = [];
        break;
      case "patch":
        if (this.prerelease.length === 0) {
          this.patch++;
        }
        this.prerelease = [];
        break;
      case "pre": {
        const base = Number(identifierBase) ? 1 : 0;
        if (!identifier && identifierBase === false) {
          throw new Error("invalid increment argument: identifier is empty");
        }
        if (this.prerelease.length === 0) {
          this.prerelease = [base];
        } else {
          let i = this.prerelease.length;
          while (--i >= 0) {
            if (typeof this.prerelease[i] === "number") {
              this.prerelease[i]++;
              i = -2;
            }
          }
          if (i === -1) {
            if (identifier === this.prerelease.join(".") && identifierBase === false) {
              throw new Error("invalid increment argument: identifier already exists");
            }
            this.prerelease.push(base);
          }
        }
        if (identifier) {
          let prerelease2 = [identifier, base];
          if (identifierBase === false) {
            prerelease2 = [identifier];
          }
          if (compareIdentifiers(this.prerelease[0], identifier) === 0) {
            if (isNaN(this.prerelease[1])) {
              this.prerelease = prerelease2;
            }
          } else {
            this.prerelease = prerelease2;
          }
        }
        break;
      }
      default:
        throw new Error(`invalid increment argument: ${release}`);
    }
    this.raw = this.format();
    if (this.build.length) {
      this.raw += `+${this.build.join(".")}`;
    }
    return this;
  }
};
var semver$2 = SemVer$d;
const SemVer$c = semver$2;
const parse$6 = (version, options, throwErrors = false) => {
  if (version instanceof SemVer$c) {
    return version;
  }
  try {
    return new SemVer$c(version, options);
  } catch (er) {
    if (!throwErrors) {
      return null;
    }
    throw er;
  }
};
var parse_1 = parse$6;
const parse$5 = parse_1;
const valid$2 = (version, options) => {
  const v = parse$5(version, options);
  return v ? v.version : null;
};
var valid_1 = valid$2;
const parse$4 = parse_1;
const clean$1 = (version, options) => {
  const s = parse$4(version.trim().replace(/^[=v]+/, ""), options);
  return s ? s.version : null;
};
var clean_1 = clean$1;
const SemVer$b = semver$2;
const inc$1 = (version, release, options, identifier, identifierBase) => {
  if (typeof options === "string") {
    identifierBase = identifier;
    identifier = options;
    options = void 0;
  }
  try {
    return new SemVer$b(
      version instanceof SemVer$b ? version.version : version,
      options
    ).inc(release, identifier, identifierBase).version;
  } catch (er) {
    return null;
  }
};
var inc_1 = inc$1;
const parse$3 = parse_1;
const diff$1 = (version1, version2) => {
  const v1 = parse$3(version1, null, true);
  const v2 = parse$3(version2, null, true);
  const comparison = v1.compare(v2);
  if (comparison === 0) {
    return null;
  }
  const v1Higher = comparison > 0;
  const highVersion = v1Higher ? v1 : v2;
  const lowVersion = v1Higher ? v2 : v1;
  const highHasPre = !!highVersion.prerelease.length;
  const lowHasPre = !!lowVersion.prerelease.length;
  if (lowHasPre && !highHasPre) {
    if (!lowVersion.patch && !lowVersion.minor) {
      return "major";
    }
    if (highVersion.patch) {
      return "patch";
    }
    if (highVersion.minor) {
      return "minor";
    }
    return "major";
  }
  const prefix = highHasPre ? "pre" : "";
  if (v1.major !== v2.major) {
    return prefix + "major";
  }
  if (v1.minor !== v2.minor) {
    return prefix + "minor";
  }
  if (v1.patch !== v2.patch) {
    return prefix + "patch";
  }
  return "prerelease";
};
var diff_1 = diff$1;
const SemVer$a = semver$2;
const major$1 = (a, loose) => new SemVer$a(a, loose).major;
var major_1 = major$1;
const SemVer$9 = semver$2;
const minor$1 = (a, loose) => new SemVer$9(a, loose).minor;
var minor_1 = minor$1;
const SemVer$8 = semver$2;
const patch$1 = (a, loose) => new SemVer$8(a, loose).patch;
var patch_1 = patch$1;
const parse$2 = parse_1;
const prerelease$1 = (version, options) => {
  const parsed = parse$2(version, options);
  return parsed && parsed.prerelease.length ? parsed.prerelease : null;
};
var prerelease_1 = prerelease$1;
const SemVer$7 = semver$2;
const compare$b = (a, b, loose) => new SemVer$7(a, loose).compare(new SemVer$7(b, loose));
var compare_1 = compare$b;
const compare$a = compare_1;
const rcompare$1 = (a, b, loose) => compare$a(b, a, loose);
var rcompare_1 = rcompare$1;
const compare$9 = compare_1;
const compareLoose$1 = (a, b) => compare$9(a, b, true);
var compareLoose_1 = compareLoose$1;
const SemVer$6 = semver$2;
const compareBuild$3 = (a, b, loose) => {
  const versionA = new SemVer$6(a, loose);
  const versionB = new SemVer$6(b, loose);
  return versionA.compare(versionB) || versionA.compareBuild(versionB);
};
var compareBuild_1 = compareBuild$3;
const compareBuild$2 = compareBuild_1;
const sort$1 = (list, loose) => list.sort((a, b) => compareBuild$2(a, b, loose));
var sort_1 = sort$1;
const compareBuild$1 = compareBuild_1;
const rsort$1 = (list, loose) => list.sort((a, b) => compareBuild$1(b, a, loose));
var rsort_1 = rsort$1;
const compare$8 = compare_1;
const gt$4 = (a, b, loose) => compare$8(a, b, loose) > 0;
var gt_1 = gt$4;
const compare$7 = compare_1;
const lt$3 = (a, b, loose) => compare$7(a, b, loose) < 0;
var lt_1 = lt$3;
const compare$6 = compare_1;
const eq$2 = (a, b, loose) => compare$6(a, b, loose) === 0;
var eq_1 = eq$2;
const compare$5 = compare_1;
const neq$2 = (a, b, loose) => compare$5(a, b, loose) !== 0;
var neq_1 = neq$2;
const compare$4 = compare_1;
const gte$3 = (a, b, loose) => compare$4(a, b, loose) >= 0;
var gte_1 = gte$3;
const compare$3 = compare_1;
const lte$3 = (a, b, loose) => compare$3(a, b, loose) <= 0;
var lte_1 = lte$3;
const eq$1 = eq_1;
const neq$1 = neq_1;
const gt$3 = gt_1;
const gte$2 = gte_1;
const lt$2 = lt_1;
const lte$2 = lte_1;
const cmp$1 = (a, op, b, loose) => {
  switch (op) {
    case "===":
      if (typeof a === "object") {
        a = a.version;
      }
      if (typeof b === "object") {
        b = b.version;
      }
      return a === b;
    case "!==":
      if (typeof a === "object") {
        a = a.version;
      }
      if (typeof b === "object") {
        b = b.version;
      }
      return a !== b;
    case "":
    case "=":
    case "==":
      return eq$1(a, b, loose);
    case "!=":
      return neq$1(a, b, loose);
    case ">":
      return gt$3(a, b, loose);
    case ">=":
      return gte$2(a, b, loose);
    case "<":
      return lt$2(a, b, loose);
    case "<=":
      return lte$2(a, b, loose);
    default:
      throw new TypeError(`Invalid operator: ${op}`);
  }
};
var cmp_1 = cmp$1;
const SemVer$5 = semver$2;
const parse$1 = parse_1;
const { safeRe: re, t } = reExports;
const coerce$1 = (version, options) => {
  if (version instanceof SemVer$5) {
    return version;
  }
  if (typeof version === "number") {
    version = String(version);
  }
  if (typeof version !== "string") {
    return null;
  }
  options = options || {};
  let match = null;
  if (!options.rtl) {
    match = version.match(options.includePrerelease ? re[t.COERCEFULL] : re[t.COERCE]);
  } else {
    const coerceRtlRegex = options.includePrerelease ? re[t.COERCERTLFULL] : re[t.COERCERTL];
    let next;
    while ((next = coerceRtlRegex.exec(version)) && (!match || match.index + match[0].length !== version.length)) {
      if (!match || next.index + next[0].length !== match.index + match[0].length) {
        match = next;
      }
      coerceRtlRegex.lastIndex = next.index + next[1].length + next[2].length;
    }
    coerceRtlRegex.lastIndex = -1;
  }
  if (match === null) {
    return null;
  }
  const major2 = match[2];
  const minor2 = match[3] || "0";
  const patch2 = match[4] || "0";
  const prerelease2 = options.includePrerelease && match[5] ? `-${match[5]}` : "";
  const build = options.includePrerelease && match[6] ? `+${match[6]}` : "";
  return parse$1(`${major2}.${minor2}.${patch2}${prerelease2}${build}`, options);
};
var coerce_1 = coerce$1;
class LRUCache {
  constructor() {
    this.max = 1e3;
    this.map = /* @__PURE__ */ new Map();
  }
  get(key) {
    const value = this.map.get(key);
    if (value === void 0) {
      return void 0;
    } else {
      this.map.delete(key);
      this.map.set(key, value);
      return value;
    }
  }
  delete(key) {
    return this.map.delete(key);
  }
  set(key, value) {
    const deleted = this.delete(key);
    if (!deleted && value !== void 0) {
      if (this.map.size >= this.max) {
        const firstKey = this.map.keys().next().value;
        this.delete(firstKey);
      }
      this.map.set(key, value);
    }
    return this;
  }
}
var lrucache = LRUCache;
var range;
var hasRequiredRange;
function requireRange() {
  if (hasRequiredRange) return range;
  hasRequiredRange = 1;
  const SPACE_CHARACTERS = /\s+/g;
  class Range2 {
    constructor(range2, options) {
      options = parseOptions2(options);
      if (range2 instanceof Range2) {
        if (range2.loose === !!options.loose && range2.includePrerelease === !!options.includePrerelease) {
          return range2;
        } else {
          return new Range2(range2.raw, options);
        }
      }
      if (range2 instanceof Comparator2) {
        this.raw = range2.value;
        this.set = [[range2]];
        this.formatted = void 0;
        return this;
      }
      this.options = options;
      this.loose = !!options.loose;
      this.includePrerelease = !!options.includePrerelease;
      this.raw = range2.trim().replace(SPACE_CHARACTERS, " ");
      this.set = this.raw.split("||").map((r) => this.parseRange(r.trim())).filter((c) => c.length);
      if (!this.set.length) {
        throw new TypeError(`Invalid SemVer Range: ${this.raw}`);
      }
      if (this.set.length > 1) {
        const first = this.set[0];
        this.set = this.set.filter((c) => !isNullSet(c[0]));
        if (this.set.length === 0) {
          this.set = [first];
        } else if (this.set.length > 1) {
          for (const c of this.set) {
            if (c.length === 1 && isAny(c[0])) {
              this.set = [c];
              break;
            }
          }
        }
      }
      this.formatted = void 0;
    }
    get range() {
      if (this.formatted === void 0) {
        this.formatted = "";
        for (let i = 0; i < this.set.length; i++) {
          if (i > 0) {
            this.formatted += "||";
          }
          const comps = this.set[i];
          for (let k = 0; k < comps.length; k++) {
            if (k > 0) {
              this.formatted += " ";
            }
            this.formatted += comps[k].toString().trim();
          }
        }
      }
      return this.formatted;
    }
    format() {
      return this.range;
    }
    toString() {
      return this.range;
    }
    parseRange(range2) {
      const memoOpts = (this.options.includePrerelease && FLAG_INCLUDE_PRERELEASE) | (this.options.loose && FLAG_LOOSE);
      const memoKey = memoOpts + ":" + range2;
      const cached = cache.get(memoKey);
      if (cached) {
        return cached;
      }
      const loose = this.options.loose;
      const hr = loose ? re2[t2.HYPHENRANGELOOSE] : re2[t2.HYPHENRANGE];
      range2 = range2.replace(hr, hyphenReplace(this.options.includePrerelease));
      debug2("hyphen replace", range2);
      range2 = range2.replace(re2[t2.COMPARATORTRIM], comparatorTrimReplace);
      debug2("comparator trim", range2);
      range2 = range2.replace(re2[t2.TILDETRIM], tildeTrimReplace);
      debug2("tilde trim", range2);
      range2 = range2.replace(re2[t2.CARETTRIM], caretTrimReplace);
      debug2("caret trim", range2);
      let rangeList = range2.split(" ").map((comp) => parseComparator(comp, this.options)).join(" ").split(/\s+/).map((comp) => replaceGTE0(comp, this.options));
      if (loose) {
        rangeList = rangeList.filter((comp) => {
          debug2("loose invalid filter", comp, this.options);
          return !!comp.match(re2[t2.COMPARATORLOOSE]);
        });
      }
      debug2("range list", rangeList);
      const rangeMap = /* @__PURE__ */ new Map();
      const comparators = rangeList.map((comp) => new Comparator2(comp, this.options));
      for (const comp of comparators) {
        if (isNullSet(comp)) {
          return [comp];
        }
        rangeMap.set(comp.value, comp);
      }
      if (rangeMap.size > 1 && rangeMap.has("")) {
        rangeMap.delete("");
      }
      const result = [...rangeMap.values()];
      cache.set(memoKey, result);
      return result;
    }
    intersects(range2, options) {
      if (!(range2 instanceof Range2)) {
        throw new TypeError("a Range is required");
      }
      return this.set.some((thisComparators) => {
        return isSatisfiable(thisComparators, options) && range2.set.some((rangeComparators) => {
          return isSatisfiable(rangeComparators, options) && thisComparators.every((thisComparator) => {
            return rangeComparators.every((rangeComparator) => {
              return thisComparator.intersects(rangeComparator, options);
            });
          });
        });
      });
    }
    // if ANY of the sets match ALL of its comparators, then pass
    test(version) {
      if (!version) {
        return false;
      }
      if (typeof version === "string") {
        try {
          version = new SemVer3(version, this.options);
        } catch (er) {
          return false;
        }
      }
      for (let i = 0; i < this.set.length; i++) {
        if (testSet(this.set[i], version, this.options)) {
          return true;
        }
      }
      return false;
    }
  }
  range = Range2;
  const LRU = lrucache;
  const cache = new LRU();
  const parseOptions2 = parseOptions_1;
  const Comparator2 = requireComparator();
  const debug2 = debug_1;
  const SemVer3 = semver$2;
  const {
    safeRe: re2,
    t: t2,
    comparatorTrimReplace,
    tildeTrimReplace,
    caretTrimReplace
  } = reExports;
  const { FLAG_INCLUDE_PRERELEASE, FLAG_LOOSE } = constants$1;
  const isNullSet = (c) => c.value === "<0.0.0-0";
  const isAny = (c) => c.value === "";
  const isSatisfiable = (comparators, options) => {
    let result = true;
    const remainingComparators = comparators.slice();
    let testComparator = remainingComparators.pop();
    while (result && remainingComparators.length) {
      result = remainingComparators.every((otherComparator) => {
        return testComparator.intersects(otherComparator, options);
      });
      testComparator = remainingComparators.pop();
    }
    return result;
  };
  const parseComparator = (comp, options) => {
    debug2("comp", comp, options);
    comp = replaceCarets(comp, options);
    debug2("caret", comp);
    comp = replaceTildes(comp, options);
    debug2("tildes", comp);
    comp = replaceXRanges(comp, options);
    debug2("xrange", comp);
    comp = replaceStars(comp, options);
    debug2("stars", comp);
    return comp;
  };
  const isX = (id) => !id || id.toLowerCase() === "x" || id === "*";
  const replaceTildes = (comp, options) => {
    return comp.trim().split(/\s+/).map((c) => replaceTilde(c, options)).join(" ");
  };
  const replaceTilde = (comp, options) => {
    const r = options.loose ? re2[t2.TILDELOOSE] : re2[t2.TILDE];
    return comp.replace(r, (_, M, m, p, pr) => {
      debug2("tilde", comp, _, M, m, p, pr);
      let ret;
      if (isX(M)) {
        ret = "";
      } else if (isX(m)) {
        ret = `>=${M}.0.0 <${+M + 1}.0.0-0`;
      } else if (isX(p)) {
        ret = `>=${M}.${m}.0 <${M}.${+m + 1}.0-0`;
      } else if (pr) {
        debug2("replaceTilde pr", pr);
        ret = `>=${M}.${m}.${p}-${pr} <${M}.${+m + 1}.0-0`;
      } else {
        ret = `>=${M}.${m}.${p} <${M}.${+m + 1}.0-0`;
      }
      debug2("tilde return", ret);
      return ret;
    });
  };
  const replaceCarets = (comp, options) => {
    return comp.trim().split(/\s+/).map((c) => replaceCaret(c, options)).join(" ");
  };
  const replaceCaret = (comp, options) => {
    debug2("caret", comp, options);
    const r = options.loose ? re2[t2.CARETLOOSE] : re2[t2.CARET];
    const z = options.includePrerelease ? "-0" : "";
    return comp.replace(r, (_, M, m, p, pr) => {
      debug2("caret", comp, _, M, m, p, pr);
      let ret;
      if (isX(M)) {
        ret = "";
      } else if (isX(m)) {
        ret = `>=${M}.0.0${z} <${+M + 1}.0.0-0`;
      } else if (isX(p)) {
        if (M === "0") {
          ret = `>=${M}.${m}.0${z} <${M}.${+m + 1}.0-0`;
        } else {
          ret = `>=${M}.${m}.0${z} <${+M + 1}.0.0-0`;
        }
      } else if (pr) {
        debug2("replaceCaret pr", pr);
        if (M === "0") {
          if (m === "0") {
            ret = `>=${M}.${m}.${p}-${pr} <${M}.${m}.${+p + 1}-0`;
          } else {
            ret = `>=${M}.${m}.${p}-${pr} <${M}.${+m + 1}.0-0`;
          }
        } else {
          ret = `>=${M}.${m}.${p}-${pr} <${+M + 1}.0.0-0`;
        }
      } else {
        debug2("no pr");
        if (M === "0") {
          if (m === "0") {
            ret = `>=${M}.${m}.${p}${z} <${M}.${m}.${+p + 1}-0`;
          } else {
            ret = `>=${M}.${m}.${p}${z} <${M}.${+m + 1}.0-0`;
          }
        } else {
          ret = `>=${M}.${m}.${p} <${+M + 1}.0.0-0`;
        }
      }
      debug2("caret return", ret);
      return ret;
    });
  };
  const replaceXRanges = (comp, options) => {
    debug2("replaceXRanges", comp, options);
    return comp.split(/\s+/).map((c) => replaceXRange(c, options)).join(" ");
  };
  const replaceXRange = (comp, options) => {
    comp = comp.trim();
    const r = options.loose ? re2[t2.XRANGELOOSE] : re2[t2.XRANGE];
    return comp.replace(r, (ret, gtlt, M, m, p, pr) => {
      debug2("xRange", comp, ret, gtlt, M, m, p, pr);
      const xM = isX(M);
      const xm = xM || isX(m);
      const xp = xm || isX(p);
      const anyX = xp;
      if (gtlt === "=" && anyX) {
        gtlt = "";
      }
      pr = options.includePrerelease ? "-0" : "";
      if (xM) {
        if (gtlt === ">" || gtlt === "<") {
          ret = "<0.0.0-0";
        } else {
          ret = "*";
        }
      } else if (gtlt && anyX) {
        if (xm) {
          m = 0;
        }
        p = 0;
        if (gtlt === ">") {
          gtlt = ">=";
          if (xm) {
            M = +M + 1;
            m = 0;
            p = 0;
          } else {
            m = +m + 1;
            p = 0;
          }
        } else if (gtlt === "<=") {
          gtlt = "<";
          if (xm) {
            M = +M + 1;
          } else {
            m = +m + 1;
          }
        }
        if (gtlt === "<") {
          pr = "-0";
        }
        ret = `${gtlt + M}.${m}.${p}${pr}`;
      } else if (xm) {
        ret = `>=${M}.0.0${pr} <${+M + 1}.0.0-0`;
      } else if (xp) {
        ret = `>=${M}.${m}.0${pr} <${M}.${+m + 1}.0-0`;
      }
      debug2("xRange return", ret);
      return ret;
    });
  };
  const replaceStars = (comp, options) => {
    debug2("replaceStars", comp, options);
    return comp.trim().replace(re2[t2.STAR], "");
  };
  const replaceGTE0 = (comp, options) => {
    debug2("replaceGTE0", comp, options);
    return comp.trim().replace(re2[options.includePrerelease ? t2.GTE0PRE : t2.GTE0], "");
  };
  const hyphenReplace = (incPr) => ($0, from, fM, fm, fp, fpr, fb, to, tM, tm, tp, tpr) => {
    if (isX(fM)) {
      from = "";
    } else if (isX(fm)) {
      from = `>=${fM}.0.0${incPr ? "-0" : ""}`;
    } else if (isX(fp)) {
      from = `>=${fM}.${fm}.0${incPr ? "-0" : ""}`;
    } else if (fpr) {
      from = `>=${from}`;
    } else {
      from = `>=${from}${incPr ? "-0" : ""}`;
    }
    if (isX(tM)) {
      to = "";
    } else if (isX(tm)) {
      to = `<${+tM + 1}.0.0-0`;
    } else if (isX(tp)) {
      to = `<${tM}.${+tm + 1}.0-0`;
    } else if (tpr) {
      to = `<=${tM}.${tm}.${tp}-${tpr}`;
    } else if (incPr) {
      to = `<${tM}.${tm}.${+tp + 1}-0`;
    } else {
      to = `<=${to}`;
    }
    return `${from} ${to}`.trim();
  };
  const testSet = (set, version, options) => {
    for (let i = 0; i < set.length; i++) {
      if (!set[i].test(version)) {
        return false;
      }
    }
    if (version.prerelease.length && !options.includePrerelease) {
      for (let i = 0; i < set.length; i++) {
        debug2(set[i].semver);
        if (set[i].semver === Comparator2.ANY) {
          continue;
        }
        if (set[i].semver.prerelease.length > 0) {
          const allowed = set[i].semver;
          if (allowed.major === version.major && allowed.minor === version.minor && allowed.patch === version.patch) {
            return true;
          }
        }
      }
      return false;
    }
    return true;
  };
  return range;
}
var comparator;
var hasRequiredComparator;
function requireComparator() {
  if (hasRequiredComparator) return comparator;
  hasRequiredComparator = 1;
  const ANY2 = Symbol("SemVer ANY");
  class Comparator2 {
    static get ANY() {
      return ANY2;
    }
    constructor(comp, options) {
      options = parseOptions2(options);
      if (comp instanceof Comparator2) {
        if (comp.loose === !!options.loose) {
          return comp;
        } else {
          comp = comp.value;
        }
      }
      comp = comp.trim().split(/\s+/).join(" ");
      debug2("comparator", comp, options);
      this.options = options;
      this.loose = !!options.loose;
      this.parse(comp);
      if (this.semver === ANY2) {
        this.value = "";
      } else {
        this.value = this.operator + this.semver.version;
      }
      debug2("comp", this);
    }
    parse(comp) {
      const r = this.options.loose ? re2[t2.COMPARATORLOOSE] : re2[t2.COMPARATOR];
      const m = comp.match(r);
      if (!m) {
        throw new TypeError(`Invalid comparator: ${comp}`);
      }
      this.operator = m[1] !== void 0 ? m[1] : "";
      if (this.operator === "=") {
        this.operator = "";
      }
      if (!m[2]) {
        this.semver = ANY2;
      } else {
        this.semver = new SemVer3(m[2], this.options.loose);
      }
    }
    toString() {
      return this.value;
    }
    test(version) {
      debug2("Comparator.test", version, this.options.loose);
      if (this.semver === ANY2 || version === ANY2) {
        return true;
      }
      if (typeof version === "string") {
        try {
          version = new SemVer3(version, this.options);
        } catch (er) {
          return false;
        }
      }
      return cmp2(version, this.operator, this.semver, this.options);
    }
    intersects(comp, options) {
      if (!(comp instanceof Comparator2)) {
        throw new TypeError("a Comparator is required");
      }
      if (this.operator === "") {
        if (this.value === "") {
          return true;
        }
        return new Range2(comp.value, options).test(this.value);
      } else if (comp.operator === "") {
        if (comp.value === "") {
          return true;
        }
        return new Range2(this.value, options).test(comp.semver);
      }
      options = parseOptions2(options);
      if (options.includePrerelease && (this.value === "<0.0.0-0" || comp.value === "<0.0.0-0")) {
        return false;
      }
      if (!options.includePrerelease && (this.value.startsWith("<0.0.0") || comp.value.startsWith("<0.0.0"))) {
        return false;
      }
      if (this.operator.startsWith(">") && comp.operator.startsWith(">")) {
        return true;
      }
      if (this.operator.startsWith("<") && comp.operator.startsWith("<")) {
        return true;
      }
      if (this.semver.version === comp.semver.version && this.operator.includes("=") && comp.operator.includes("=")) {
        return true;
      }
      if (cmp2(this.semver, "<", comp.semver, options) && this.operator.startsWith(">") && comp.operator.startsWith("<")) {
        return true;
      }
      if (cmp2(this.semver, ">", comp.semver, options) && this.operator.startsWith("<") && comp.operator.startsWith(">")) {
        return true;
      }
      return false;
    }
  }
  comparator = Comparator2;
  const parseOptions2 = parseOptions_1;
  const { safeRe: re2, t: t2 } = reExports;
  const cmp2 = cmp_1;
  const debug2 = debug_1;
  const SemVer3 = semver$2;
  const Range2 = requireRange();
  return comparator;
}
const Range$9 = requireRange();
const satisfies$4 = (version, range2, options) => {
  try {
    range2 = new Range$9(range2, options);
  } catch (er) {
    return false;
  }
  return range2.test(version);
};
var satisfies_1 = satisfies$4;
const Range$8 = requireRange();
const toComparators$1 = (range2, options) => new Range$8(range2, options).set.map((comp) => comp.map((c) => c.value).join(" ").trim().split(" "));
var toComparators_1 = toComparators$1;
const SemVer$4 = semver$2;
const Range$7 = requireRange();
const maxSatisfying$1 = (versions, range2, options) => {
  let max = null;
  let maxSV = null;
  let rangeObj = null;
  try {
    rangeObj = new Range$7(range2, options);
  } catch (er) {
    return null;
  }
  versions.forEach((v) => {
    if (rangeObj.test(v)) {
      if (!max || maxSV.compare(v) === -1) {
        max = v;
        maxSV = new SemVer$4(max, options);
      }
    }
  });
  return max;
};
var maxSatisfying_1 = maxSatisfying$1;
const SemVer$3 = semver$2;
const Range$6 = requireRange();
const minSatisfying$1 = (versions, range2, options) => {
  let min = null;
  let minSV = null;
  let rangeObj = null;
  try {
    rangeObj = new Range$6(range2, options);
  } catch (er) {
    return null;
  }
  versions.forEach((v) => {
    if (rangeObj.test(v)) {
      if (!min || minSV.compare(v) === 1) {
        min = v;
        minSV = new SemVer$3(min, options);
      }
    }
  });
  return min;
};
var minSatisfying_1 = minSatisfying$1;
const SemVer$2 = semver$2;
const Range$5 = requireRange();
const gt$2 = gt_1;
const minVersion$1 = (range2, loose) => {
  range2 = new Range$5(range2, loose);
  let minver = new SemVer$2("0.0.0");
  if (range2.test(minver)) {
    return minver;
  }
  minver = new SemVer$2("0.0.0-0");
  if (range2.test(minver)) {
    return minver;
  }
  minver = null;
  for (let i = 0; i < range2.set.length; ++i) {
    const comparators = range2.set[i];
    let setMin = null;
    comparators.forEach((comparator2) => {
      const compver = new SemVer$2(comparator2.semver.version);
      switch (comparator2.operator) {
        case ">":
          if (compver.prerelease.length === 0) {
            compver.patch++;
          } else {
            compver.prerelease.push(0);
          }
          compver.raw = compver.format();
        case "":
        case ">=":
          if (!setMin || gt$2(compver, setMin)) {
            setMin = compver;
          }
          break;
        case "<":
        case "<=":
          break;
        default:
          throw new Error(`Unexpected operation: ${comparator2.operator}`);
      }
    });
    if (setMin && (!minver || gt$2(minver, setMin))) {
      minver = setMin;
    }
  }
  if (minver && range2.test(minver)) {
    return minver;
  }
  return null;
};
var minVersion_1 = minVersion$1;
const Range$4 = requireRange();
const validRange$1 = (range2, options) => {
  try {
    return new Range$4(range2, options).range || "*";
  } catch (er) {
    return null;
  }
};
var valid$1 = validRange$1;
const SemVer$1 = semver$2;
const Comparator$2 = requireComparator();
const { ANY: ANY$1 } = Comparator$2;
const Range$3 = requireRange();
const satisfies$3 = satisfies_1;
const gt$1 = gt_1;
const lt$1 = lt_1;
const lte$1 = lte_1;
const gte$1 = gte_1;
const outside$3 = (version, range2, hilo, options) => {
  version = new SemVer$1(version, options);
  range2 = new Range$3(range2, options);
  let gtfn, ltefn, ltfn, comp, ecomp;
  switch (hilo) {
    case ">":
      gtfn = gt$1;
      ltefn = lte$1;
      ltfn = lt$1;
      comp = ">";
      ecomp = ">=";
      break;
    case "<":
      gtfn = lt$1;
      ltefn = gte$1;
      ltfn = gt$1;
      comp = "<";
      ecomp = "<=";
      break;
    default:
      throw new TypeError('Must provide a hilo val of "<" or ">"');
  }
  if (satisfies$3(version, range2, options)) {
    return false;
  }
  for (let i = 0; i < range2.set.length; ++i) {
    const comparators = range2.set[i];
    let high = null;
    let low = null;
    comparators.forEach((comparator2) => {
      if (comparator2.semver === ANY$1) {
        comparator2 = new Comparator$2(">=0.0.0");
      }
      high = high || comparator2;
      low = low || comparator2;
      if (gtfn(comparator2.semver, high.semver, options)) {
        high = comparator2;
      } else if (ltfn(comparator2.semver, low.semver, options)) {
        low = comparator2;
      }
    });
    if (high.operator === comp || high.operator === ecomp) {
      return false;
    }
    if ((!low.operator || low.operator === comp) && ltefn(version, low.semver)) {
      return false;
    } else if (low.operator === ecomp && ltfn(version, low.semver)) {
      return false;
    }
  }
  return true;
};
var outside_1 = outside$3;
const outside$2 = outside_1;
const gtr$1 = (version, range2, options) => outside$2(version, range2, ">", options);
var gtr_1 = gtr$1;
const outside$1 = outside_1;
const ltr$1 = (version, range2, options) => outside$1(version, range2, "<", options);
var ltr_1 = ltr$1;
const Range$2 = requireRange();
const intersects$1 = (r1, r2, options) => {
  r1 = new Range$2(r1, options);
  r2 = new Range$2(r2, options);
  return r1.intersects(r2, options);
};
var intersects_1 = intersects$1;
const satisfies$2 = satisfies_1;
const compare$2 = compare_1;
var simplify = (versions, range2, options) => {
  const set = [];
  let first = null;
  let prev = null;
  const v = versions.sort((a, b) => compare$2(a, b, options));
  for (const version of v) {
    const included = satisfies$2(version, range2, options);
    if (included) {
      prev = version;
      if (!first) {
        first = version;
      }
    } else {
      if (prev) {
        set.push([first, prev]);
      }
      prev = null;
      first = null;
    }
  }
  if (first) {
    set.push([first, null]);
  }
  const ranges = [];
  for (const [min, max] of set) {
    if (min === max) {
      ranges.push(min);
    } else if (!max && min === v[0]) {
      ranges.push("*");
    } else if (!max) {
      ranges.push(`>=${min}`);
    } else if (min === v[0]) {
      ranges.push(`<=${max}`);
    } else {
      ranges.push(`${min} - ${max}`);
    }
  }
  const simplified = ranges.join(" || ");
  const original = typeof range2.raw === "string" ? range2.raw : String(range2);
  return simplified.length < original.length ? simplified : range2;
};
const Range$1 = requireRange();
const Comparator$1 = requireComparator();
const { ANY } = Comparator$1;
const satisfies$1 = satisfies_1;
const compare$1 = compare_1;
const subset$1 = (sub, dom, options = {}) => {
  if (sub === dom) {
    return true;
  }
  sub = new Range$1(sub, options);
  dom = new Range$1(dom, options);
  let sawNonNull = false;
  OUTER: for (const simpleSub of sub.set) {
    for (const simpleDom of dom.set) {
      const isSub = simpleSubset(simpleSub, simpleDom, options);
      sawNonNull = sawNonNull || isSub !== null;
      if (isSub) {
        continue OUTER;
      }
    }
    if (sawNonNull) {
      return false;
    }
  }
  return true;
};
const minimumVersionWithPreRelease = [new Comparator$1(">=0.0.0-0")];
const minimumVersion = [new Comparator$1(">=0.0.0")];
const simpleSubset = (sub, dom, options) => {
  if (sub === dom) {
    return true;
  }
  if (sub.length === 1 && sub[0].semver === ANY) {
    if (dom.length === 1 && dom[0].semver === ANY) {
      return true;
    } else if (options.includePrerelease) {
      sub = minimumVersionWithPreRelease;
    } else {
      sub = minimumVersion;
    }
  }
  if (dom.length === 1 && dom[0].semver === ANY) {
    if (options.includePrerelease) {
      return true;
    } else {
      dom = minimumVersion;
    }
  }
  const eqSet = /* @__PURE__ */ new Set();
  let gt2, lt2;
  for (const c of sub) {
    if (c.operator === ">" || c.operator === ">=") {
      gt2 = higherGT(gt2, c, options);
    } else if (c.operator === "<" || c.operator === "<=") {
      lt2 = lowerLT(lt2, c, options);
    } else {
      eqSet.add(c.semver);
    }
  }
  if (eqSet.size > 1) {
    return null;
  }
  let gtltComp;
  if (gt2 && lt2) {
    gtltComp = compare$1(gt2.semver, lt2.semver, options);
    if (gtltComp > 0) {
      return null;
    } else if (gtltComp === 0 && (gt2.operator !== ">=" || lt2.operator !== "<=")) {
      return null;
    }
  }
  for (const eq2 of eqSet) {
    if (gt2 && !satisfies$1(eq2, String(gt2), options)) {
      return null;
    }
    if (lt2 && !satisfies$1(eq2, String(lt2), options)) {
      return null;
    }
    for (const c of dom) {
      if (!satisfies$1(eq2, String(c), options)) {
        return false;
      }
    }
    return true;
  }
  let higher, lower;
  let hasDomLT, hasDomGT;
  let needDomLTPre = lt2 && !options.includePrerelease && lt2.semver.prerelease.length ? lt2.semver : false;
  let needDomGTPre = gt2 && !options.includePrerelease && gt2.semver.prerelease.length ? gt2.semver : false;
  if (needDomLTPre && needDomLTPre.prerelease.length === 1 && lt2.operator === "<" && needDomLTPre.prerelease[0] === 0) {
    needDomLTPre = false;
  }
  for (const c of dom) {
    hasDomGT = hasDomGT || c.operator === ">" || c.operator === ">=";
    hasDomLT = hasDomLT || c.operator === "<" || c.operator === "<=";
    if (gt2) {
      if (needDomGTPre) {
        if (c.semver.prerelease && c.semver.prerelease.length && c.semver.major === needDomGTPre.major && c.semver.minor === needDomGTPre.minor && c.semver.patch === needDomGTPre.patch) {
          needDomGTPre = false;
        }
      }
      if (c.operator === ">" || c.operator === ">=") {
        higher = higherGT(gt2, c, options);
        if (higher === c && higher !== gt2) {
          return false;
        }
      } else if (gt2.operator === ">=" && !satisfies$1(gt2.semver, String(c), options)) {
        return false;
      }
    }
    if (lt2) {
      if (needDomLTPre) {
        if (c.semver.prerelease && c.semver.prerelease.length && c.semver.major === needDomLTPre.major && c.semver.minor === needDomLTPre.minor && c.semver.patch === needDomLTPre.patch) {
          needDomLTPre = false;
        }
      }
      if (c.operator === "<" || c.operator === "<=") {
        lower = lowerLT(lt2, c, options);
        if (lower === c && lower !== lt2) {
          return false;
        }
      } else if (lt2.operator === "<=" && !satisfies$1(lt2.semver, String(c), options)) {
        return false;
      }
    }
    if (!c.operator && (lt2 || gt2) && gtltComp !== 0) {
      return false;
    }
  }
  if (gt2 && hasDomLT && !lt2 && gtltComp !== 0) {
    return false;
  }
  if (lt2 && hasDomGT && !gt2 && gtltComp !== 0) {
    return false;
  }
  if (needDomGTPre || needDomLTPre) {
    return false;
  }
  return true;
};
const higherGT = (a, b, options) => {
  if (!a) {
    return b;
  }
  const comp = compare$1(a.semver, b.semver, options);
  return comp > 0 ? a : comp < 0 ? b : b.operator === ">" && a.operator === ">=" ? b : a;
};
const lowerLT = (a, b, options) => {
  if (!a) {
    return b;
  }
  const comp = compare$1(a.semver, b.semver, options);
  return comp < 0 ? a : comp > 0 ? b : b.operator === "<" && a.operator === "<=" ? b : a;
};
var subset_1 = subset$1;
const internalRe = reExports;
const constants = constants$1;
const SemVer2 = semver$2;
const identifiers = identifiers$1;
const parse = parse_1;
const valid = valid_1;
const clean = clean_1;
const inc = inc_1;
const diff = diff_1;
const major = major_1;
const minor = minor_1;
const patch = patch_1;
const prerelease = prerelease_1;
const compare = compare_1;
const rcompare = rcompare_1;
const compareLoose = compareLoose_1;
const compareBuild = compareBuild_1;
const sort = sort_1;
const rsort = rsort_1;
const gt = gt_1;
const lt = lt_1;
const eq = eq_1;
const neq = neq_1;
const gte = gte_1;
const lte = lte_1;
const cmp = cmp_1;
const coerce = coerce_1;
const Comparator = requireComparator();
const Range = requireRange();
const satisfies = satisfies_1;
const toComparators = toComparators_1;
const maxSatisfying = maxSatisfying_1;
const minSatisfying = minSatisfying_1;
const minVersion = minVersion_1;
const validRange = valid$1;
const outside = outside_1;
const gtr = gtr_1;
const ltr = ltr_1;
const intersects = intersects_1;
const simplifyRange = simplify;
const subset = subset_1;
var semver = {
  parse,
  valid,
  clean,
  inc,
  diff,
  major,
  minor,
  patch,
  prerelease,
  compare,
  rcompare,
  compareLoose,
  compareBuild,
  sort,
  rsort,
  gt,
  lt,
  eq,
  neq,
  gte,
  lte,
  cmp,
  coerce,
  Comparator,
  Range,
  satisfies,
  toComparators,
  maxSatisfying,
  minSatisfying,
  minVersion,
  validRange,
  outside,
  gtr,
  ltr,
  intersects,
  simplifyRange,
  subset,
  SemVer: SemVer2,
  re: internalRe.re,
  src: internalRe.src,
  tokens: internalRe.t,
  SEMVER_SPEC_VERSION: constants.SEMVER_SPEC_VERSION,
  RELEASE_TYPES: constants.RELEASE_TYPES,
  compareIdentifiers: identifiers.compareIdentifiers,
  rcompareIdentifiers: identifiers.rcompareIdentifiers
};
const semver$1 = /* @__PURE__ */ getDefaultExportFromCjs(semver);
const _hoisted_1$D = { class: "login-component" };
const _hoisted_2$r = ["disabled"];
function render$b(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("form", _hoisted_1$D, [
    _cache[7] || (_cache[7] = createElementVNode("h2", null, "Login", -1)),
    createElementVNode("fieldset", {
      class: normalizeClass({ invalid: _ctx.validation.username === false, valid: _ctx.validation.username === true })
    }, [
      withDirectives(createElementVNode("input", {
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => _ctx.username = $event),
        type: "email",
        placeholder: "Username",
        onBlur: _cache[1] || (_cache[1] = (...args) => _ctx.checkValidInput && _ctx.checkValidInput(...args)),
        onKeyup: _cache[2] || (_cache[2] = ($event) => _ctx.validateWithDelay(_ctx.validateUsername))
      }, null, 544), [
        [vModelText, _ctx.username]
      ])
    ], 2),
    createElementVNode("fieldset", {
      class: normalizeClass({ invalid: _ctx.validation.password === false, valid: _ctx.validation.password === true })
    }, [
      withDirectives(createElementVNode("input", {
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => _ctx.password = $event),
        type: "password",
        placeholder: "Password",
        onBlur: _cache[4] || (_cache[4] = (...args) => _ctx.checkValidInput && _ctx.checkValidInput(...args)),
        onKeyup: _cache[5] || (_cache[5] = ($event) => _ctx.validateWithDelay(_ctx.validatePassword))
      }, null, 544), [
        [vModelText, _ctx.password]
      ])
    ], 2),
    withDirectives(createElementVNode("div", { class: "error-message" }, toDisplayString(_ctx.error), 513), [
      [vShow, _ctx.showError]
    ]),
    createElementVNode("button", {
      class: "primary",
      type: "button",
      disabled: !_ctx.formValid || _ctx.loggingIn,
      onClick: _cache[6] || (_cache[6] = (...args) => _ctx.onTapLogin && _ctx.onTapLogin(...args))
    }, toDisplayString(_ctx.loginButtonLabel), 9, _hoisted_2$r)
  ]);
}
const _export_sfc = (sfc, props) => {
  const target = sfc.__vccOpts || sfc;
  for (const [key, val] of props) {
    target[key] = val;
  }
  return target;
};
const _sfc_main$K = defineComponent({
  props: {
    getAuth: {
      type: Function
    }
  },
  setup(props, { emit }) {
    return {
      ...useAuth(props.getAuth(), { mode: "login", emit, minPasswordScore: props.minPasswordScore })
    };
  }
});
const LoginComponent = /* @__PURE__ */ _export_sfc(_sfc_main$K, [["render", render$b]]);
const _hoisted_1$C = { class: "signup-component-container" };
const _hoisted_2$q = { key: 0 };
const _hoisted_3$a = {
  key: 0,
  class: "mb-40"
};
const _hoisted_4$a = { key: 0 };
const _hoisted_5$a = ["disabled"];
function render$a(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("form", _hoisted_1$C, [
    _cache[9] || (_cache[9] = createElementVNode("h2", null, "Signup", -1)),
    createElementVNode("div", {
      class: normalizeClass(["fieldset", { invalid: _ctx.validation.username === false, valid: _ctx.validation.username === true }])
    }, [
      withDirectives(createElementVNode("input", {
        type: "email",
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => _ctx.username = $event),
        placeholder: "Username",
        onBlur: _cache[1] || (_cache[1] = (...args) => _ctx.validateUsername && _ctx.validateUsername(...args)),
        onKeyup: _cache[2] || (_cache[2] = ($event) => _ctx.validateWithDelay(_ctx.validateUsername))
      }, null, 544), [
        [vModelText, _ctx.username]
      ]),
      _ctx.validation.username ? (openBlock(), createElementBlock("i", _hoisted_2$q, "✓")) : createCommentVNode("", true)
    ], 2),
    createElementVNode("div", {
      class: normalizeClass(["fieldset", { invalid: _ctx.validation.password === false, valid: _ctx.validation.password === true }])
    }, [
      withDirectives(createElementVNode("input", {
        type: "password",
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => _ctx.password = $event),
        placeholder: "Password",
        onKeyup: _cache[4] || (_cache[4] = ($event) => _ctx.validatePassword(_ctx.password))
      }, null, 544), [
        [vModelText, _ctx.password]
      ]),
      _ctx.validation.password ? (openBlock(), createElementBlock("i", _hoisted_3$a, "✓")) : createCommentVNode("", true)
    ], 2),
    createElementVNode("div", {
      class: normalizeClass(["fieldset", { invalid: _ctx.validation.confirmPassword === false, valid: _ctx.validation.confirmPassword === true }])
    }, [
      withDirectives(createElementVNode("input", {
        type: "password",
        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => _ctx.confirmPassword = $event),
        placeholder: "Confirm Password",
        onBlur: _cache[6] || (_cache[6] = (...args) => _ctx.validateConfirmPassword && _ctx.validateConfirmPassword(...args)),
        onKeyup: _cache[7] || (_cache[7] = ($event) => _ctx.validateWithDelay(_ctx.validateConfirmPassword))
      }, null, 544), [
        [vModelText, _ctx.confirmPassword]
      ]),
      _ctx.validation.confirmPassword ? (openBlock(), createElementBlock("i", _hoisted_4$a, "✓")) : createCommentVNode("", true)
    ], 2),
    createElementVNode("button", {
      class: "primary",
      type: "button",
      onClick: _cache[8] || (_cache[8] = (...args) => _ctx.onTapCreate && _ctx.onTapCreate(...args)),
      disabled: !_ctx.formValid
    }, "Create Account", 8, _hoisted_5$a)
  ]);
}
const _sfc_main$J = defineComponent({
  props: {
    getAuth: {
      type: Function
    },
    minPasswordScore: {
      type: Number,
      default: 3
    }
  },
  methods: {
    goBack() {
      this.$emit("back");
    }
  },
  setup(props, { emit }) {
    return Object.assign(
      {},
      useAuth(props.getAuth(), { mode: "signup", emit, minPasswordScore: props.minPasswordScore })
    );
  }
});
const SignupComponent = /* @__PURE__ */ _export_sfc(_sfc_main$J, [["render", render$a]]);
const _hoisted_1$B = { class: "reset-credentials-component" };
const _hoisted_2$p = ["disabled"];
const _sfc_main$I = /* @__PURE__ */ defineComponent({
  __name: "reset-credentials",
  props: {
    auth: {},
    minPasswordScore: {}
  },
  emits: ["reset", "error"],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const { username, validateUsername, validateWithDelay, validation, processing, onTapReset } = useAuth(props.auth, { emit, mode: "reset", minPasswordScore: props.minPasswordScore });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("form", _hoisted_1$B, [
        _cache[4] || (_cache[4] = createElementVNode("h2", null, "Reset Credentials", -1)),
        withDirectives(createElementVNode("input", {
          class: "email-input",
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => isRef(username) ? username.value = $event : null),
          type: "email",
          placeholder: "Email",
          onBlur: _cache[1] || (_cache[1] = //@ts-ignore
          (...args) => unref(validateUsername) && unref(validateUsername)(...args)),
          onKeyup: _cache[2] || (_cache[2] = ($event) => unref(validateWithDelay)(unref(validateUsername)))
        }, null, 544), [
          [vModelText, unref(username)]
        ]),
        createElementVNode("button", {
          class: "reset-button primary",
          type: "button",
          disabled: !unref(validation).username || unref(processing),
          onClick: _cache[3] || (_cache[3] = //@ts-ignore
          (...args) => unref(onTapReset) && unref(onTapReset)(...args))
        }, "Send Reset Email ", 8, _hoisted_2$p)
      ]);
    };
  }
});
const _hoisted_1$A = { class: "auth-component" };
const _hoisted_2$o = { class: "auth-menu" };
var AUTH_MODES = /* @__PURE__ */ ((AUTH_MODES2) => {
  AUTH_MODES2[AUTH_MODES2["login"] = 1] = "login";
  AUTH_MODES2[AUTH_MODES2["signup"] = 2] = "signup";
  AUTH_MODES2[AUTH_MODES2["reset"] = 3] = "reset";
  return AUTH_MODES2;
})(AUTH_MODES || {});
const _sfc_main$H = /* @__PURE__ */ defineComponent({
  __name: "auth",
  props: {
    minPasswordScore: { default: 3 },
    features: { default: () => ["login", "signup", "reset"] },
    auth: {}
  },
  emits: ["signout", "signup", "login", "reset", "error"],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const mode = ref(
      1
      /* login */
    );
    const useLogin = computed(() => props.features.includes("login"));
    const useSignup = computed(() => props.features.includes("signup"));
    const useReset = computed(() => props.features.includes("reset"));
    const currentUser = ref(props.auth.currentUser);
    const authUnsub = ref(null);
    function coerceToValidMode() {
      if (!useSignup.value && mode.value === 2) {
        mode.value = 1;
      }
      if (!useLogin.value && mode.value === 1) {
        mode.value = 3;
      }
      if (!useReset.value && mode.value === 3) {
        mode.value = 2;
      }
    }
    onMounted(() => {
      authUnsub.value = onAuthStateChanged(props.auth, (user) => {
        if (user) currentUser.value = user;
      });
      coerceToValidMode();
    });
    onUnmounted(() => {
      if (authUnsub.value) authUnsub.value();
    });
    async function signOut() {
      await props.auth.signOut();
      emit("signout");
    }
    const onSignup = () => emit("signup");
    const onLogin = () => emit("login");
    const onReset = () => emit("reset");
    const onError = (err) => emit("error", err);
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", _hoisted_1$A, [
        currentUser.value ? renderSlot(_ctx.$slots, "loggedin", { key: 0 }, () => [
          _cache[3] || (_cache[3] = createElementVNode("h2", null, "Logged In", -1)),
          createElementVNode("p", null, "Logged in as " + toDisplayString(currentUser.value.email), 1),
          createElementVNode("button", { onClick: signOut }, "Log Out")
        ]) : createCommentVNode("", true),
        !currentUser.value && mode.value === 1 && useLogin.value ? renderSlot(_ctx.$slots, "login", { key: 1 }, () => [
          createVNode(LoginComponent, {
            onLogin,
            onError
          })
        ]) : createCommentVNode("", true),
        !currentUser.value && mode.value === 2 && useSignup.value ? renderSlot(_ctx.$slots, "signup", {
          key: 2,
          minPasswordScore: _ctx.minPasswordScore
        }, () => [
          createVNode(SignupComponent, {
            "min-password-score": _ctx.minPasswordScore,
            onSignup
          }, null, 8, ["min-password-score"])
        ]) : createCommentVNode("", true),
        !currentUser.value && mode.value === 3 && useReset.value ? renderSlot(_ctx.$slots, "reset", { key: 3 }, () => [
          createVNode(_sfc_main$I, {
            auth: _ctx.auth,
            "min-password-score": _ctx.minPasswordScore,
            onReset
          }, null, 8, ["auth", "min-password-score"])
        ]) : createCommentVNode("", true),
        !currentUser.value ? renderSlot(_ctx.$slots, "menu", {
          key: 4,
          mode: mode.value,
          modes: AUTH_MODES
        }, () => [
          createElementVNode("nav", _hoisted_2$o, [
            createElementVNode("li", null, [
              mode.value !== 1 && useLogin.value ? (openBlock(), createElementBlock("button", {
                key: 0,
                class: "auth-login",
                onClick: _cache[0] || (_cache[0] = ($event) => mode.value = 1)
              }, "Log In")) : createCommentVNode("", true)
            ]),
            createElementVNode("li", null, [
              mode.value !== 2 && useSignup.value ? (openBlock(), createElementBlock("button", {
                key: 0,
                class: "auth-signup",
                onClick: _cache[1] || (_cache[1] = ($event) => mode.value = 2)
              }, "Sign Up")) : createCommentVNode("", true)
            ]),
            createElementVNode("li", null, [
              mode.value !== 3 && useReset.value ? (openBlock(), createElementBlock("button", {
                key: 0,
                class: "auth-reset",
                onClick: _cache[2] || (_cache[2] = ($event) => mode.value = 3)
              }, "Reset Password")) : createCommentVNode("", true)
            ])
          ])
        ]) : createCommentVNode("", true)
      ]);
    };
  }
});
const _hoisted_1$z = {
  key: 0,
  class: "prefix"
};
const _hoisted_2$n = ["value"];
const _hoisted_3$9 = {
  disabled: "",
  value: ""
};
const _hoisted_4$9 = ["value"];
const _hoisted_5$9 = ["type", "value", "placeholder", "maxLength"];
const _hoisted_6$4 = {
  key: 2,
  class: "value"
};
const _hoisted_7$4 = { key: 0 };
const _hoisted_8$3 = { key: 1 };
function render$9(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", {
    class: normalizeClass(["fire-input", _ctx.resolvedClasses])
  }, [
    _ctx.prefix ? (openBlock(), createElementBlock("sub", _hoisted_1$z, toDisplayString(_ctx.prefix), 1)) : createCommentVNode("", true),
    _ctx.editing ? (openBlock(), createElementBlock(Fragment, { key: 1 }, [
      _ctx.type === "select" ? (openBlock(), createElementBlock("select", {
        key: 0,
        value: _ctx.workingValue,
        onInput: _cache[0] || (_cache[0] = (...args) => _ctx.onInput && _ctx.onInput(...args))
      }, [
        createElementVNode("option", _hoisted_3$9, toDisplayString(_ctx.placeholder || "Select one"), 1),
        (openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.resolvedOptions, (option) => {
          return openBlock(), createElementBlock("option", {
            key: option.value,
            value: option.value
          }, toDisplayString(option.label), 9, _hoisted_4$9);
        }), 128))
      ], 40, _hoisted_2$n)) : (openBlock(), createElementBlock("input", {
        key: 1,
        type: _ctx.type,
        value: _ctx.workingValue,
        placeholder: _ctx.placeholder,
        maxLength: _ctx.maxLength,
        onInput: _cache[1] || (_cache[1] = (...args) => _ctx.onInput && _ctx.onInput(...args))
      }, null, 40, _hoisted_5$9))
    ], 64)) : createCommentVNode("", true),
    !_ctx.editing ? (openBlock(), createElementBlock("div", _hoisted_6$4, toDisplayString(_ctx.resolvedValue), 1)) : createCommentVNode("", true),
    createVNode(Transition, { name: "fade" }, {
      default: withCtx(() => [
        _ctx.status ? (openBlock(), createElementBlock("div", {
          key: 0,
          class: normalizeClass(["status", _ctx.status])
        }, [
          _ctx.status === "save-success" ? (openBlock(), createElementBlock("span", _hoisted_7$4, "✓")) : createCommentVNode("", true),
          _ctx.status === "save-pending" ? (openBlock(), createElementBlock("span", _hoisted_8$3, "...")) : createCommentVNode("", true)
        ], 2)) : createCommentVNode("", true)
      ]),
      _: 1
    })
  ], 2);
}
const TYPING_END_TIMEOUT = 1500;
const SUCCESS_STATUS_TIMEOUT = 2500;
function sentenceCase(str) {
  return str[0].toUpperCase() + str.substr(1);
}
const _sfc_main$G = defineComponent({
  props: {
    model: {
      type: Object
    },
    editing: {
      default: true
    },
    field: {
      type: String
    },
    type: {
      default: "text"
    },
    options: {
      default: null
    },
    maxLength: {
      default: null
    },
    placeholder: {
      default: null
    },
    transformer: {
      default: null,
      type: Function
    },
    validation: {
      default: null,
      type: Function
    },
    prefix: {
      default: null,
      type: String
    }
  },
  data() {
    return {
      workingModel: null,
      workingValue: null,
      status: null,
      valid: true
    };
  },
  computed: {
    resolvedOptions() {
      if (this.options) {
        const mapped = this.options.map((opt) => {
          if (typeof opt === "string" || typeof opt === "number") {
            return {
              label: sentenceCase(`${opt}`),
              value: opt
            };
          } else {
            return opt;
          }
        });
        return mapped;
      } else {
        return [];
      }
    },
    resolvedValue() {
      if (this.type !== "select") {
        return this.workingValue;
      } else {
        const match = this.resolvedOptions.find((opt) => opt.value === this.workingValue);
        if (match) {
          return match.label;
        } else {
          return this.workingValue;
        }
      }
    },
    resolvedClasses() {
      const classes = {};
      if (this.prefix) {
        classes["has-prefix"] = true;
      }
      if (!this.valid) {
        classes.invalid = true;
      }
      return classes;
    }
    // resolvedMaxLength(){
    // }
  },
  watch: {
    model(newVal, oldVal) {
      if (newVal !== oldVal && this.workingModel !== newVal && newVal) {
        this.setWorkingModel(newVal);
      }
    }
  },
  mounted() {
    this.setWorkingModel(this.model);
  },
  methods: {
    setWorkingModel(model) {
      this.workingModel = model;
      let value = this.workingModel.$get(this.field);
      value = this.coerceValue(value, "in");
      this.workingValue = value;
    },
    onInput(newVal) {
      if (this.validation) {
        this.valid = this.validation(newVal.target.value);
      } else {
        this.valid = true;
      }
      if (this.valid) {
        this.workingValue = newVal.target.value;
        this.status = "save-pending";
        this.delayedEmitInput();
      }
    },
    coerceValue(value, direction) {
      if (this.transformer) {
        value = this.transformer(value, direction, this.workingModel);
      } else if (this.type === "number") {
        value = parseFloat(value);
      }
      return value;
    },
    delayedEmitInput() {
      if (this.emitTimeout) {
        clearTimeout(this.emitTimeout);
      }
      this.emitTimeout = setTimeout(() => {
        let value = this.workingValue;
        value = this.coerceValue(value, "out");
        this.workingModel.$set(this.field, value);
        this.$emit("input", value);
        this.showSaveSuccess();
      }, TYPING_END_TIMEOUT);
    },
    showSaveSuccess() {
      if (this.successTimeout) {
        clearTimeout(this.successTimeout);
      }
      this.status = "save-success";
      this.successTimeout = setTimeout(() => {
        if (this.status === "save-success") {
          this.status = null;
        }
        this.successTimeout = null;
      }, SUCCESS_STATUS_TIMEOUT);
    }
  }
});
const fireInput = /* @__PURE__ */ _export_sfc(_sfc_main$G, [["render", render$9]]);
function render$8(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createBlock(resolveDynamicComponent(_ctx.tag), null, {
    default: withCtx(() => [
      renderSlot(_ctx.$slots, "empty"),
      _ctx.items && _ctx.items.length > 0 ? (openBlock(true), createElementBlock(Fragment, { key: 0 }, renderList(_ctx.items, (item) => {
        return openBlock(), createElementBlock("div", {
          class: "repeater-item",
          key: item
        }, [
          renderSlot(_ctx.$slots, "default", { item }, () => [
            createElementVNode("div", null, toDisplayString(item.id), 1)
          ])
        ]);
      }), 128)) : createCommentVNode("", true)
    ]),
    _: 3
  });
}
const _sfc_main$F = defineComponent({
  props: {
    subset: {
      default: null
    },
    collection: {
      default: null
    },
    listen: {
      default: false
    },
    tag: {
      default: "div"
    }
  },
  data() {
    return {
      items: [],
      workingSubset: null
    };
  },
  watch: {
    subset(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.workingSubset = newVal;
      }
    },
    workingSubset(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.onSubsetSwitch();
      }
    },
    collection(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.onCollectionSwitch();
      }
    }
  },
  mounted() {
    if (this.collection && this.subset) {
      throw new Error("Apply only collection or subset prop, not both");
    }
    if (this.collection) {
      this.onCollectionSwitch();
    } else {
      this.workingSubset = this.subset;
      this.onSubsetSwitch();
    }
  },
  methods: {
    onCollectionSwitch() {
      this.workingSubset = this.collection.subset();
      if (this.listen) {
        this.workingSubset.listen();
      } else {
        this.workingSubset.fetch();
      }
    },
    onSubsetSwitch() {
      if (this.workingSubset) {
        this.items = this.workingSubset.items;
      } else {
        this.items = [];
      }
    }
  }
});
const fireRepeater = /* @__PURE__ */ _export_sfc(_sfc_main$F, [["render", render$8]]);
const globals = {
  emitter: null,
  initialized: false
};
function requireInitialization() {
  if (!globals.initialized) {
    throw new Error(`You must call initEvents one time before calling this function`);
  }
}
function setEmitter(emitter) {
  requireInitialization();
  globals.emitter = emitter;
}
function initEvents(emitter) {
  if (globals.initialized) {
    throw new Error(`initEvents should not be called more than once in application`);
  }
  globals.initialized = true;
  if (emitter) {
    setEmitter(emitter);
  } else {
    globals.emitter = mitt();
  }
}
function getEmitter() {
  requireInitialization();
  return globals.emitter;
}
const tracker = {};
function newId(prefix) {
  if (!tracker[prefix]) {
    tracker[prefix] = 0;
  }
  tracker[prefix] += 1;
  return `${prefix}${tracker[prefix]}`;
}
const _hoisted_1$y = { key: 0 };
const _hoisted_2$m = { key: 0 };
const _hoisted_3$8 = { class: "modal-content" };
const _hoisted_4$8 = { key: 1 };
const _hoisted_5$8 = ["disabled"];
const _hoisted_6$3 = ["disabled"];
const _hoisted_7$3 = { key: 1 };
function render$7(_ctx, _cache, $props, $setup, $data, $options) {
  return _ctx.active ? (openBlock(), createElementBlock("aside", {
    key: 0,
    class: "scrim fixed",
    onClick: _cache[3] || (_cache[3] = withModifiers((...args) => _ctx.close && _ctx.close(...args), ["self"]))
  }, [
    createElementVNode("div", {
      class: normalizeClass(["modal", _ctx.resolvedClasses])
    }, [
      _ctx.useHeader ? (openBlock(), createElementBlock("header", _hoisted_1$y, [
        _ctx.title ? (openBlock(), createElementBlock("h3", _hoisted_2$m, toDisplayString(_ctx.title), 1)) : createCommentVNode("", true),
        !_ctx.hideCloseButton ? (openBlock(), createElementBlock("button", {
          key: 1,
          class: "close",
          onClick: _cache[0] || (_cache[0] = (...args) => _ctx.close && _ctx.close(...args))
        }, _cache[4] || (_cache[4] = [
          createElementVNode("div", { class: "icon" }, [
            createElementVNode("i", { class: "fas fa-times" })
          ], -1)
        ]))) : createCommentVNode("", true)
      ])) : createCommentVNode("", true),
      createElementVNode("div", _hoisted_3$8, [
        renderSlot(_ctx.$slots, "default")
      ]),
      _ctx.useFooter ? (openBlock(), createElementBlock("footer", _hoisted_4$8, [
        renderSlot(_ctx.$slots, "footer", {}, () => [
          _ctx.secondButtonText ? (openBlock(), createElementBlock("button", {
            key: 0,
            class: "cancel",
            disabled: _ctx.locked,
            onClick: _cache[1] || (_cache[1] = (...args) => _ctx.cancel && _ctx.cancel(...args))
          }, toDisplayString(_ctx.secondButtonText), 9, _hoisted_5$8)) : createCommentVNode("", true),
          createElementVNode("button", {
            class: "text",
            disabled: _ctx.disabledButton || _ctx.locked,
            onClick: _cache[2] || (_cache[2] = (...args) => _ctx.confirm && _ctx.confirm(...args))
          }, toDisplayString(_ctx.buttonText ? _ctx.buttonText : "OK"), 9, _hoisted_6$3),
          _ctx.working ? (openBlock(), createElementBlock("label", _hoisted_7$3, "Working...")) : createCommentVNode("", true)
        ])
      ])) : createCommentVNode("", true)
    ], 2)
  ])) : createCommentVNode("", true);
}
const _sfc_main$E = defineComponent({
  props: {
    title: "",
    locked: false,
    working: false,
    useHeader: {
      default: true
    },
    useFooter: {
      default: true
    },
    size: {
      default: "small"
    },
    guid: {
      default: newId("modal")
    },
    buttonText: "",
    secondButtonText: "",
    disabledButton: false,
    hideCloseButton: false
  },
  data() {
    return {
      active: false
    };
  },
  created() {
    this.emitter = getEmitter();
    this.emitter.on(`modal:${this.guid}:show`, () => {
      this.active = true;
    });
    this.emitter.on(`modal:*:hide`, () => {
      this.active = false;
    });
  },
  computed: {
    resolvedClasses() {
      var classes = {};
      classes[`${this.size}`] = true;
      return classes;
    }
  },
  methods: {
    close(force = false) {
      if (!this.locked || force) {
        this.active = false;
        this.$emit("close", "close");
      }
    },
    confirm() {
      this.$emit("action", "confirm");
    },
    cancel() {
      this.$emit("cancel", "cancel");
    }
  }
});
const legacyModal = /* @__PURE__ */ _export_sfc(_sfc_main$E, [["render", render$7]]);
const _hoisted_1$x = { key: 0 };
const _hoisted_2$l = { class: "modal-content" };
const _sfc_main$D = /* @__PURE__ */ defineComponent({
  __name: "modal",
  props: {
    title: {},
    closeOnEscape: { type: Boolean },
    closeOnScrim: { type: Boolean },
    showCancel: { type: Boolean },
    teleportTo: {},
    confirmButtonLabel: {}
  },
  emits: ["close"],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const confirmButtonLabel = ref(props.confirmButtonLabel || "Done");
    const showCancel = ref(props.showCancel || true);
    const teleportTo = ref(props.teleportTo || "body");
    const closeOnScrim = ref(props.closeOnScrim || true);
    const emit = __emit;
    function cancelModal() {
      emit("close", false);
    }
    function confirmModal() {
      emit("close", true);
    }
    return (_ctx, _cache) => {
      return openBlock(), createBlock(Teleport, { to: teleportTo.value }, [
        createElementVNode("div", {
          class: normalizeClass(["scrim", _ctx.$attrs.class]),
          onClick: _cache[1] || (_cache[1] = ($event) => closeOnScrim.value && cancelModal()),
          onKeyup: _cache[2] || (_cache[2] = withKeys(($event) => props.closeOnEscape && cancelModal(), ["esc"]))
        }, [
          createElementVNode("aside", {
            class: "modal",
            onClick: _cache[0] || (_cache[0] = withModifiers(() => {
            }, ["stop"]))
          }, [
            createElementVNode("header", null, [
              props.title ? (openBlock(), createElementBlock("h2", _hoisted_1$x, toDisplayString(props.title), 1)) : createCommentVNode("", true)
            ]),
            createElementVNode("div", _hoisted_2$l, [
              renderSlot(_ctx.$slots, "default", {}, () => [
                _cache[3] || (_cache[3] = createElementVNode("p", null, "Modal Content", -1))
              ])
            ]),
            createElementVNode("footer", null, [
              showCancel.value ? (openBlock(), createElementBlock("button", {
                key: 0,
                class: "btn-secondary",
                onClick: cancelModal
              }, "Cancel")) : createCommentVNode("", true),
              createElementVNode("button", {
                class: "btn-primary",
                onClick: confirmModal
              }, toDisplayString(confirmButtonLabel.value), 1)
            ])
          ])
        ], 34)
      ], 8, ["to"]);
    };
  }
});
var DialogMode = /* @__PURE__ */ ((DialogMode2) => {
  DialogMode2[DialogMode2["message"] = 0] = "message";
  DialogMode2[DialogMode2["confirm"] = 1] = "confirm";
  return DialogMode2;
})(DialogMode || {});
const dialogMode = ref(
  0
  /* message */
);
const dialogTitle = ref("");
const dialogMessage = ref("");
const showDialog = ref(false);
const dialogCloseOnScrim = ref(false);
const dialogConfirmButtonText = ref("OK");
const dialogCallback = ref(void 0);
const defaultDialogOptions = {
  title: "",
  message: "",
  confirmButtonText: "OK",
  cancelButtonText: "Cancel",
  mode: 0,
  closeOnScrim: false
};
function showDialogInternal(options) {
  const mergedOptions = { ...defaultDialogOptions, ...options };
  dialogTitle.value = mergedOptions.title;
  dialogMessage.value = mergedOptions.message;
  dialogConfirmButtonText.value = mergedOptions.confirmButtonText;
  showDialog.value = true;
  dialogMode.value = mergedOptions.mode;
  dialogCallback.value = mergedOptions.callback;
  dialogCloseOnScrim.value = mergedOptions.closeOnScrim || false;
}
const showMessage = function(options) {
  options.mode = 0;
  showDialogInternal(options);
};
const getConfirmation = function(options) {
  options.mode = 1;
  showDialogInternal(options);
};
const _hoisted_1$w = { class: "dialog-content" };
const _sfc_main$C = /* @__PURE__ */ defineComponent({
  __name: "dialog",
  setup(__props) {
    function cancelDialog() {
      showDialog.value = false;
      if (dialogCallback.value) {
        dialogCallback.value(false);
      }
    }
    function confirmDialog() {
      showDialog.value = false;
      if (dialogCallback.value) {
        dialogCallback.value(true);
      }
    }
    return (_ctx, _cache) => {
      return unref(showDialog) ? (openBlock(), createElementBlock("div", {
        key: 0,
        class: "scrim",
        onClick: _cache[1] || (_cache[1] = ($event) => unref(dialogCloseOnScrim) && cancelDialog)
      }, [
        createElementVNode("aside", {
          class: "dialog",
          onClick: _cache[0] || (_cache[0] = withModifiers(() => {
          }, ["stop"]))
        }, [
          createElementVNode("header", null, [
            createElementVNode("h2", null, toDisplayString(unref(dialogTitle)), 1)
          ]),
          createElementVNode("div", _hoisted_1$w, [
            createElementVNode("p", null, toDisplayString(unref(dialogMessage)), 1)
          ]),
          createElementVNode("footer", null, [
            createElementVNode("button", { onClick: confirmDialog }, toDisplayString(unref(dialogConfirmButtonText)), 1)
          ])
        ])
      ])) : createCommentVNode("", true);
    };
  }
});
const _hoisted_1$v = { class: "dropdown-wrapper" };
const _hoisted_2$k = {
  key: 0,
  class: "dropdown"
};
const _hoisted_3$7 = ["onClick"];
const _hoisted_4$7 = { class: "trigger" };
const _hoisted_5$7 = ["disabled"];
function render$6(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", _hoisted_1$v, [
    _ctx.active && !_ctx.disabled ? (openBlock(), createElementBlock("div", _hoisted_2$k, [
      createElementVNode("ul", null, [
        (openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.parsedOptions, (opt) => {
          return openBlock(), createElementBlock("li", {
            key: opt,
            class: normalizeClass(opt.selected ? "selected" : "unselected"),
            onClick: ($event) => _ctx.onSelect(opt)
          }, toDisplayString(opt.label), 11, _hoisted_3$7);
        }), 128))
      ])
    ])) : createCommentVNode("", true),
    createElementVNode("div", _hoisted_4$7, [
      renderSlot(_ctx.$slots, "default", {}, () => [
        _ctx.buttonLabel ? (openBlock(), createElementBlock("button", {
          key: 0,
          disabled: _ctx.disabled,
          onClick: _cache[0] || (_cache[0] = ($event) => _ctx.active = !_ctx.active)
        }, toDisplayString(_ctx.buttonLabel), 9, _hoisted_5$7)) : createCommentVNode("", true)
      ])
    ])
  ]);
}
const _sfc_main$B = defineComponent({
  props: [
    "options",
    "buttonLabel",
    "context",
    "disabled",
    "selected"
  ],
  emits: ["select"],
  data() {
    return {
      active: false
    };
  },
  onClickOut() {
    this.active = false;
  },
  computed: {
    parsedOptions() {
      return this.options.map((opt) => {
        if (typeof opt === "string" || typeof opt === "number") {
          return {
            value: opt,
            label: opt
          };
        } else {
          return opt;
        }
      });
    }
  },
  mounted() {
    console.log(" IF SELECTED? : ", this.props.selected);
  },
  methods: {
    onSelect(option) {
      this.active = false;
      this.$emit("select", { context: this.context, value: option.value });
    }
  }
});
const dropdown = /* @__PURE__ */ _export_sfc(_sfc_main$B, [["render", render$6]]);
const _hoisted_1$u = {
  key: 0,
  class: "tag-creator"
};
const _hoisted_2$j = { class: "controls" };
function render$5(_ctx, _cache, $props, $setup, $data, $options) {
  return _ctx.active ? (openBlock(), createElementBlock("div", _hoisted_1$u, [
    withDirectives(createElementVNode("input", {
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => _ctx.value = $event),
      onBlur: _cache[1] || (_cache[1] = (...args) => _ctx.onDone && _ctx.onDone(...args)),
      ref: "input",
      onKeyup: _cache[2] || (_cache[2] = withKeys((...args) => _ctx.onDone && _ctx.onDone(...args), ["enter"]))
    }, null, 544), [
      [vModelText, _ctx.value]
    ]),
    createElementVNode("div", _hoisted_2$j, [
      createElementVNode("div", {
        class: "icon",
        onClick: _cache[3] || (_cache[3] = (...args) => _ctx.onDone && _ctx.onDone(...args))
      }, _cache[5] || (_cache[5] = [
        createElementVNode("i", { class: "fas fa-check" }, null, -1)
      ])),
      createElementVNode("div", {
        class: "icon",
        onClick: _cache[4] || (_cache[4] = (...args) => _ctx.onCancel && _ctx.onCancel(...args))
      }, _cache[6] || (_cache[6] = [
        createElementVNode("i", { class: "fas fa-times" }, null, -1)
      ]))
    ])
  ])) : createCommentVNode("", true);
}
const _sfc_main$A = defineComponent({
  props: ["active"],
  data() {
    return {
      value: ""
    };
  },
  watch: {
    active(newVal, oldVal) {
      if (newVal && newVal !== oldVal && this.$refs.input) {
        this.focus();
      }
    }
  },
  mounted() {
    if (this.active && this.$refs.input) {
      this.focus();
    }
  },
  methods: {
    focus() {
      this.$refs.input.focus();
    },
    onDone() {
      if (this.value && this.value.length > 1) {
        var value = this.value;
        this.value = "";
        this.$emit("done", value);
      }
    },
    onCancel() {
      this.value = "";
      this.$emit("cancel");
    }
  }
});
const tagCreator = /* @__PURE__ */ _export_sfc(_sfc_main$A, [["render", render$5]]);
const _hoisted_1$t = { class: "tag-list-editor" };
const _hoisted_2$i = { class: "tag-list" };
const _hoisted_3$6 = { class: "tag" };
const _hoisted_4$6 = ["onClick"];
const _hoisted_5$6 = {
  key: 0,
  class: "add-tag"
};
function render$4(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_tag_creator = resolveComponent("tag-creator");
  return openBlock(), createElementBlock("div", _hoisted_1$t, [
    createElementVNode("ul", _hoisted_2$i, [
      (openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.tags, (tag) => {
        return openBlock(), createElementBlock("li", _hoisted_3$6, [
          createElementVNode("label", null, toDisplayString(_ctx.getLabel(tag)), 1),
          !_ctx.disabled ? (openBlock(), createElementBlock("button", {
            key: 0,
            onClick: ($event) => _ctx.deleteTag(tag)
          }, _cache[2] || (_cache[2] = [
            createElementVNode("div", { class: "icon" }, [
              createElementVNode("i", { class: "fas fa-times" })
            ], -1)
          ]), 8, _hoisted_4$6)) : createCommentVNode("", true)
        ]);
      }), 256)),
      createElementVNode("li", null, [
        createVNode(_component_tag_creator, {
          onDone: _ctx.addTag,
          onCancel: _cache[0] || (_cache[0] = ($event) => _ctx.addingNewTag = false),
          active: _ctx.addingNewTag
        }, null, 8, ["onDone", "active"])
      ]),
      !_ctx.addingNewTag ? (openBlock(), createElementBlock("li", _hoisted_5$6, [
        !_ctx.disabled ? (openBlock(), createElementBlock("button", {
          key: 0,
          onClick: _cache[1] || (_cache[1] = (...args) => _ctx.startNewKeyword && _ctx.startNewKeyword(...args))
        }, [
          _cache[3] || (_cache[3] = createElementVNode("div", { class: "icon" }, [
            createElementVNode("i", { class: "fas fa-plus" })
          ], -1)),
          createElementVNode("span", null, toDisplayString(_ctx.addLabel), 1)
        ])) : createCommentVNode("", true)
      ])) : createCommentVNode("", true)
    ])
  ]);
}
const _sfc_main$z = defineComponent({
  props: {
    addLabel: {
      default: "Add",
      type: String
    },
    tags: {
      type: Array,
      default: []
    },
    displayField: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      addingNewTag: false
    };
  },
  methods: {
    startNewKeyword() {
      this.addingNewTag = true;
    },
    addTag(tag) {
      this.addingNewTag = false;
      this.tags.push(tag);
      this.$emit("change", this.tags);
    },
    deleteTag(tag) {
      var index = -1;
      for (let i = 0; i < this.tags.length; i++) {
        if (tag === this.tags[i]) {
          index = i;
          break;
        }
      }
      if (index >= 0) {
        this.tags.splice(index, 1);
        this.$emit("change", this.tags);
      }
    },
    getLabel(tag) {
      if (this.displayField) {
        return tag[this.displayField];
      } else {
        return tag;
      }
    }
  }
});
const tagListEditor = /* @__PURE__ */ _export_sfc(_sfc_main$z, [["render", render$4]]);
const _hoisted_1$s = {
  key: 0,
  class: "snackbar-container"
};
const _hoisted_2$h = { class: "snackbar-message" };
const _sfc_main$y = /* @__PURE__ */ defineComponent({
  __name: "snackbar",
  props: {
    active: { type: Boolean, default: false },
    message: { default: "" },
    secondsActive: { default: 3 }
  },
  setup(__props) {
    const props = __props;
    const display = ref(false);
    watch(() => props.active, () => {
      display.value = true;
      setTimeout(() => {
        display.value = false;
      }, props.secondsActive * 1e3);
    });
    return (_ctx, _cache) => {
      return display.value ? (openBlock(), createElementBlock("div", _hoisted_1$s, [
        createElementVNode("div", _hoisted_2$h, toDisplayString(_ctx.message), 1)
      ])) : createCommentVNode("", true);
    };
  }
});
const _hoisted_1$r = { class: "v-checkbox-container" };
const _hoisted_2$g = {
  key: 0,
  class: "icon"
};
function render$3(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", _hoisted_1$r, [
    createElementVNode("div", {
      class: normalizeClass(["checkbox", _ctx.classes]),
      onClick: _cache[0] || (_cache[0] = ($event) => _ctx.handleClick(!_ctx.checked))
    }, [
      _ctx.checked ? (openBlock(), createElementBlock("div", _hoisted_2$g, _cache[1] || (_cache[1] = [
        createElementVNode("i", { class: "fas fa-check" }, null, -1)
      ]))) : createCommentVNode("", true)
    ], 2)
  ]);
}
const _sfc_main$x = defineComponent({
  props: {
    checked: { type: Boolean, default: false },
    background: { type: String, default: "#FFA502" },
    disabled: { type: Boolean, default: false }
  },
  emits: ["action"],
  computed: {
    classes() {
      const classes = {};
      if (this.disabled) {
        classes.disabled = true;
      }
      if (this.checked) {
        classes.checked = true;
      } else {
        classes.outline = true;
      }
      return classes;
    }
  },
  methods: {
    handleClick(bool) {
      if (!this.disabled) {
        this.$emit("action", bool);
      }
    }
  }
});
const checkbox = /* @__PURE__ */ _export_sfc(_sfc_main$x, [["render", render$3]]);
const _hoisted_1$q = { class: "radioset" };
const _hoisted_2$f = { class: "radioset-label" };
const _hoisted_3$5 = { class: "radioset-option" };
const _hoisted_4$5 = ["id", "value", "disabled"];
const _hoisted_5$5 = ["for"];
function render$2(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", _hoisted_1$q, [
    createElementVNode("div", _hoisted_2$f, toDisplayString(_ctx.label) + ":", 1),
    createElementVNode("div", {
      class: normalizeClass(["radioset-options", { row: _ctx.row }])
    }, [
      (openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.options, (option, idx) => {
        return openBlock(), createElementBlock("div", _hoisted_3$5, [
          withDirectives(createElementVNode("input", {
            class: "radio",
            type: "radio",
            id: idx,
            value: option.value,
            disabled: _ctx.disabled || option.disabled,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => _ctx.selectedOption = $event)
          }, null, 8, _hoisted_4$5), [
            [vModelRadio, _ctx.selectedOption]
          ]),
          createElementVNode("label", {
            class: "label",
            for: idx
          }, toDisplayString(option.label), 9, _hoisted_5$5)
        ]);
      }), 256))
    ], 2)
  ]);
}
const _sfc_main$w = defineComponent({
  props: {
    label: {
      type: String,
      default: "Please Select an Option"
    },
    options: {
      type: Array,
      validator: (prop) => prop.every((option) => {
        let keys2 = Object.keys(option);
        return typeof option === "object" && keys2.length >= 2 && keys2.length <= 3 && keys2.includes("label") && keys2.includes("value");
      })
    },
    disabled: Boolean,
    row: Boolean,
    modelValue: {
      default: null
    }
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const selectedOption = ref(props.modelValue);
    watch(() => selectedOption.value, (newVal, oldVal) => {
      emit("update:modelValue", selectedOption.value);
    });
    return {
      selectedOption
    };
  }
});
const radioset = /* @__PURE__ */ _export_sfc(_sfc_main$w, [["render", render$2]]);
const _hoisted_1$p = { class: "twoPointSlider" };
const _hoisted_2$e = { class: "values" };
const _hoisted_3$4 = { class: "slider-container" };
const _hoisted_4$4 = ["min", "max", "oninput", "step"];
const _hoisted_5$4 = ["min", "max", "oninput", "step"];
const _sfc_main$v = {
  __name: "twoPointSlider",
  props: {
    min: String,
    max: String,
    minGap: Number,
    inputType: String,
    incrementBy: Number,
    color: String,
    modelValue: {
      type: Object
    }
  },
  emits: ["update:modelValue"],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const minVal = ref(0);
    const maxVal = ref(100);
    const input1 = ref(props.min);
    const input2 = ref(props.max);
    const leftSlider = ref();
    const rightSlider = ref();
    const lineColor = ref("#DA3A34");
    const atMin = ref(true);
    const atMax = ref(true);
    const stepVal = ref(0.5);
    const get_lWidth = () => parseFloat(window.getComputedStyle(document.querySelector(".range1")).width.replace("px", ""));
    const lWidth = ref(7);
    const get_rWidth = () => parseFloat(window.getComputedStyle(document.querySelector(".range2")).width.replace("px", ""));
    const rWidth = ref(7);
    let get_sliderTrack = () => parseFloat(window.getComputedStyle(document.querySelector(".slider-container")).width.replace("px", ""));
    const trackWidth = ref(0);
    const leftPosition = ref(0);
    const rightPosition = ref(0);
    const set_lPos = (trackWidth2) => {
      let minPos = -lWidth.value / 2;
      let maxPos = trackWidth2 - lWidth.value - rWidth.value;
      let percent = (leftSlider.value - minVal.value) / maxVal.value;
      let pos = `left: ${Math.min(Math.max(minPos, percent * trackWidth2), maxPos)}px;`;
      return leftPosition.value = pos;
    };
    const set_rPos = (trackWidth2) => {
      -rWidth.value / 2;
      let percent = (maxVal.value - rightSlider.value) / maxVal.value;
      let maxPos = trackWidth2 - lWidth.value - rWidth.value;
      let pos = Math.min(Math.max(0, percent * (trackWidth2 - rWidth.value / 2)), maxPos);
      return rightPosition.value = "right:" + pos + "px;";
    };
    const model = ref({ start: props.min, end: props.max });
    if (props.modelValue) {
      model.value = props.modelValue;
    }
    onMounted(() => {
      if (props.inputType == "time") {
        minVal.value = timeToNum(props.min);
        maxVal.value = timeToNum(props.max);
      } else {
        minVal.value = props.min;
        maxVal.value = props.max;
      }
      if (props.modelValue) {
        let startTime;
        let endTime;
        if (props.inputType == "time") {
          startTime = timeToNum(model.value["start"]);
          endTime = timeToNum(model.value["end"]);
        } else {
          startTime = model.value["start"];
          endTime = model.value["end"];
        }
        input1.value = model.value["start"];
        leftSlider.value = startTime;
        input2.value = model.value["end"];
        rightSlider.value = endTime;
      } else {
        leftSlider.value = minVal.value;
        rightSlider.value = maxVal.value;
      }
      if (props.incrementBy) {
        stepVal.value = props.incrementBy;
      }
      if (props.color) {
        lineColor.value = props.color;
      }
      let totalWidth = get_sliderTrack();
      trackWidth.value = totalWidth;
      lWidth.value = get_lWidth();
      rWidth.value = get_rWidth();
      set_lPos(totalWidth);
      set_rPos(totalWidth);
    });
    const checkForDecimal = (num) => {
      let result = num - Math.floor(num) !== 0;
      if (result) {
        return true;
      } else {
        return false;
      }
    };
    const timeToNum = (time) => {
      const tmp = time.split(":");
      const tmp2 = tmp[1].split(" ");
      let amOrPm = tmp2[1];
      let hour = parseFloat(tmp[0]);
      let minute = tmp2[0] / 60;
      if (amOrPm == "am") {
        if (hour == 12) {
          hour = 0;
        }
      }
      if (amOrPm == "pm") {
        if (hour < 12) {
          hour += 12;
        }
      }
      let timeAsNumber = hour + minute;
      return timeAsNumber;
    };
    const numToTime = (num) => {
      let isAm = false;
      let isDecimal = checkForDecimal(num);
      if (num < 12) {
        isAm = true;
        if (num < 1) {
          num += 12;
        }
      }
      if (num >= 12) {
        num -= 12;
      }
      let tmpString = num.toString();
      if (!isDecimal) {
        tmpString = `${tmpString}.00`;
      }
      const tmp = tmpString.split(".");
      let hour = tmp[0];
      let minute = tmp[1];
      minute = parseFloat(minute);
      minute = minute * 60 / 100;
      if (minute.toString().length == 1) {
        minute = `${minute}0`;
      }
      let time = `${hour}:${minute}`;
      if (isAm) {
        time = `${time} am`;
      } else {
        time = `${time} pm`;
      }
      if (time.includes(".")) {
        time = time.replace(".", "");
      }
      if (time.includes("pm")) {
        let tmp2 = time.split(":");
        if (tmp2[0] == "0") {
          tmp2[0] = "12";
        }
        time = `${tmp2[0]}:${tmp2[1]}`;
      }
      return time;
    };
    let slideLeft = () => {
      if (rightSlider.value - leftSlider.value <= props.minGap) {
        leftSlider.value = parseFloat(rightSlider.value) - props.minGap;
      }
      if (leftSlider.value == minVal.value) {
        atMin.value = true;
      } else {
        atMin.value = false;
      }
      if (props.inputType == "time") {
        input1.value = numToTime(leftSlider.value);
      } else {
        input1.value = leftSlider.value;
      }
      model.value["start"] = input1.value;
      emit("update:modelValue", model);
      set_lPos(trackWidth.value);
    };
    let slideRight = () => {
      if (rightSlider.value - leftSlider.value <= props.minGap) {
        rightSlider.value = parseFloat(leftSlider.value) + props.minGap;
      }
      if (rightSlider.value == maxVal.value) {
        atMax.value = true;
      } else {
        atMax.value = false;
      }
      if (props.inputType == "time") {
        input2.value = numToTime(rightSlider.value);
      } else {
        input2.value = rightSlider.value;
      }
      model.value["end"] = input2.value;
      emit("update:modelValue", model);
      set_rPos(trackWidth.value);
    };
    let sliderColor = computed(() => {
      let percent1 = (leftSlider.value - minVal.value) / (maxVal.value - minVal.value) * 100;
      let percent2 = (rightSlider.value - minVal.value) / (maxVal.value - minVal.value) * 100;
      let gradient = `linear-gradient(to right, #dadae5 ${percent1}% , ${lineColor.value} ${percent1}% , ${lineColor.value} ${percent2}%, #dadae5 ${percent2}%)`;
      return gradient;
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", _hoisted_1$p, [
        createElementVNode("div", _hoisted_2$e, [
          createElementVNode("span", {
            class: "range1",
            style: normalizeStyle(leftPosition.value)
          }, toDisplayString(input1.value), 5),
          createElementVNode("span", {
            class: "range2",
            style: normalizeStyle(rightPosition.value)
          }, toDisplayString(input2.value), 5)
        ]),
        createElementVNode("div", _hoisted_3$4, [
          _cache[2] || (_cache[2] = createElementVNode("div", { class: "start" }, null, -1)),
          createElementVNode("div", {
            class: "slider-track",
            style: normalizeStyle({ background: unref(sliderColor) })
          }, null, 4),
          withDirectives(createElementVNode("input", {
            class: "left-slider slider",
            type: "range",
            min: minVal.value,
            max: maxVal.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => leftSlider.value = $event),
            oninput: unref(slideLeft),
            step: stepVal.value
          }, null, 8, _hoisted_4$4), [
            [vModelText, leftSlider.value]
          ]),
          withDirectives(createElementVNode("input", {
            class: "right-slider slider",
            type: "range",
            min: minVal.value,
            max: maxVal.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => rightSlider.value = $event),
            oninput: unref(slideRight),
            step: stepVal.value
          }, null, 8, _hoisted_5$4), [
            [vModelText, rightSlider.value]
          ]),
          _cache[3] || (_cache[3] = createElementVNode("div", { class: "end" }, null, -1))
        ])
      ]);
    };
  }
};
const _sfc_main$u = {
  props: {
    label: String,
    disabled: Boolean,
    modelValue: String,
    placeholder: String,
    required: Boolean,
    errorMsg: String,
    helperText: String
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const inputField = ref("");
    const errorMessage = ref("");
    const helperText = ref("");
    watch(() => props.errorMsg, () => {
      errorMessage.value = props.errorMsg || "";
    });
    watch(() => props.helperText, () => {
      helperText.value = props.helperText || "";
    });
    onMounted(() => {
      inputField.value = props.modelValue || "";
    });
    watch(() => props.modelValue, () => {
      inputField.value = props.modelValue || "";
    });
    function updateValue() {
      emit("update:modelValue", inputField.value);
    }
    const errorState = ref(computed(() => {
      let isValidInput = false;
      if (props.required) {
        if (inputField.value && inputField.value.length) {
          isValidInput = true;
        }
      } else {
        isValidInput = true;
      }
      const noErrMsg = !!errorMessage.value && errorMessage.value.length;
      return isValidInput && noErrMsg;
    }));
    return {
      inputField,
      errorMessage,
      errorState,
      updateValue
    };
  }
};
const _hoisted_1$o = ["disabled"];
const _hoisted_2$d = {
  key: 0,
  class: "caption input-label"
};
const _hoisted_3$3 = ["placeholder", "required", "disabled"];
const _hoisted_4$3 = {
  key: 1,
  class: "caption helper-text error-message"
};
const _hoisted_5$3 = {
  key: 2,
  class: "caption helper-text"
};
function render$1(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", {
    class: normalizeClass(["custom-input", { "disabled": $props.disabled }]),
    disabled: $props.disabled
  }, [
    $props.label ? (openBlock(), createElementBlock("label", _hoisted_2$d, toDisplayString($props.label), 1)) : createCommentVNode("", true),
    withDirectives(createElementVNode("input", {
      class: normalizeClass(["input", { "error-state": $setup.errorState }]),
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => $setup.inputField = $event),
      type: "text",
      placeholder: $props.placeholder,
      required: $props.required,
      disabled: $props.disabled,
      onInput: _cache[1] || (_cache[1] = (...args) => $setup.updateValue && $setup.updateValue(...args))
    }, null, 42, _hoisted_3$3), [
      [vModelText, $setup.inputField]
    ]),
    $setup.errorMessage ? (openBlock(), createElementBlock("div", _hoisted_4$3, "* " + toDisplayString($setup.errorMessage), 1)) : createCommentVNode("", true),
    $props.helperText ? (openBlock(), createElementBlock("div", _hoisted_5$3, toDisplayString($props.helperText), 1)) : createCommentVNode("", true)
  ], 10, _hoisted_1$o);
}
const textField = /* @__PURE__ */ _export_sfc(_sfc_main$u, [["render", render$1]]);
const _hoisted_1$n = { class: "date-picker" };
const _hoisted_2$c = { class: "top-container" };
const _hoisted_3$2 = { class: "selected-date" };
const _hoisted_4$2 = { class: "bottom-container" };
const _hoisted_5$2 = { class: "month-select" };
const _hoisted_6$2 = { class: "month" };
const _hoisted_7$2 = { class: "dates-container" };
const _hoisted_8$2 = { class: "dayOfWeek" };
const _hoisted_9$2 = ["onClick"];
const _sfc_main$t = /* @__PURE__ */ defineComponent({
  __name: "date-picker",
  props: {
    popup: { type: Boolean },
    disabledDates: {},
    disabledDayOfWeek: {}
  },
  emits: ["close"],
  setup(__props, { emit: __emit }) {
    const emit = __emit;
    const props = __props;
    const Months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const DayLabel = ["S", "M", "T", "W", "T", "F", "S"];
    const disabledDays = ref([]);
    const now = /* @__PURE__ */ new Date();
    const selectedYear = ref(now.getFullYear());
    const selectedMonth = ref(now.getMonth());
    const month = ref(selectedMonth.value);
    const year = ref(selectedYear.value);
    const date = ref(now.getDate());
    const selectedDate = ref(now);
    let dayAsString = ref(now.toDateString());
    let leftChevy = "<";
    let rightChevy = ">";
    onMounted(() => {
      parseDisabled();
    });
    const parseDisabled = () => {
      var _a;
      if (props.disabledDates) {
        for (let i = 0; i < ((_a = props.disabledDates) == null ? void 0 : _a.length); i++) {
          if (typeof props.disabledDates[i] === "object") {
            let tmpDate = new Date(props.disabledDates[i]);
            console.log("Date", tmpDate);
            let day = tmpDate.getDate();
            let month2 = tmpDate.getMonth();
            let year2 = tmpDate.getFullYear();
            let dateObject = {
              day,
              month: month2,
              year: year2
            };
            disabledDays.value.push(dateObject);
          } else if (typeof props.disabledDates[i] === "string") {
            if (props.disabledDates[i].includes("T")) {
              let tmpDate = new Date(props.disabledDates[i]);
              let day = tmpDate.getDate();
              console.log(day);
              let month2 = tmpDate.getMonth();
              let year2 = tmpDate.getFullYear();
              let dateObject = {
                day,
                month: month2,
                year: year2
              };
              disabledDays.value.push(dateObject);
            } else {
              let splitDate = props.disabledDates[i].split("/");
              let dateObject = {
                day: parseInt(splitDate[1]),
                month: parseInt(splitDate[0]),
                year: parseInt(splitDate[2])
              };
              disabledDays.value.push(dateObject);
            }
          } else {
            console.log(`${props.disabledDates[i]} is not supported please look at documentation for supported values.`);
          }
        }
      }
      return false;
    };
    const setDate = (year2, month2, day) => {
      let tmp = new Date(year2, month2, day);
      selectedDate.value = tmp;
      updateValues(day);
      let date2 = {
        date: selectedDate.value,
        text: dayAsString.value
      };
      emit("close", date2);
    };
    const daysInMonth = (year2, month2) => {
      return new Date(year2, month2 + 1, 0).getDate();
    };
    const changeMonth = async (val) => {
      month.value = month.value + val;
      if (month.value > 11) {
        month.value = 1;
        year.value += 1;
      } else if (month.value <= -1) {
        month.value = 11;
        year.value -= 1;
      }
      console.log(Months[month.value], month.value);
    };
    const dayOfWeek = (year2, month2, day) => {
      return new Date(year2, month2, day).getDay();
    };
    const updateValues = (day) => {
      date.value = day;
      selectedYear.value = year.value;
      selectedMonth.value = month.value;
      dayAsString.value = selectedDate.value.toDateString();
    };
    const checkForSelected = (d, m, y) => {
      if (d === date.value && m === selectedMonth.value && y === selectedYear.value) {
        return true;
      } else {
        return false;
      }
    };
    const checkForDisabled = (year2, month2, day) => {
      var _a;
      let nameOfDay = new Date(year2, month2, day).toLocaleString("default", { weekday: "long" });
      if ((_a = props.disabledDayOfWeek) == null ? void 0 : _a.includes(nameOfDay)) {
        return true;
      }
      if (disabledDays.value.length > 0) {
        for (let i = 0; i < disabledDays.value.length; i++) {
          if (disabledDays.value[i].day === day && disabledDays.value[i].month === month2 && disabledDays.value[i].year === year2) {
            return true;
          }
        }
      }
      return false;
    };
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        class: normalizeClass(["date-picker-container", { popup: _ctx.popup, embeded: !_ctx.popup }])
      }, [
        createElementVNode("div", _hoisted_1$n, [
          createElementVNode("div", _hoisted_2$c, [
            _cache[2] || (_cache[2] = createElementVNode("div", { class: "title" }, "SELECT DATE", -1)),
            createElementVNode("div", _hoisted_3$2, toDisplayString(unref(dayAsString)), 1)
          ]),
          createElementVNode("div", _hoisted_4$2, [
            createElementVNode("div", _hoisted_5$2, [
              createElementVNode("span", {
                class: "left-chevy",
                onClick: _cache[0] || (_cache[0] = ($event) => changeMonth(-1))
              }, toDisplayString(unref(leftChevy)), 1),
              createElementVNode("div", _hoisted_6$2, toDisplayString(Months[month.value]), 1),
              createElementVNode("span", {
                class: "right-chevy",
                onClick: _cache[1] || (_cache[1] = ($event) => changeMonth(1))
              }, toDisplayString(unref(rightChevy)), 1)
            ]),
            createElementVNode("div", _hoisted_7$2, [
              (openBlock(), createElementBlock(Fragment, null, renderList(DayLabel, (day) => {
                return createElementVNode("div", _hoisted_8$2, toDisplayString(day), 1);
              }), 64)),
              (openBlock(true), createElementBlock(Fragment, null, renderList(daysInMonth(year.value, month.value), (date2) => {
                return openBlock(), createElementBlock("div", {
                  class: normalizeClass(["days", { "selected": checkForSelected(date2, month.value, year.value), "disabled": checkForDisabled(year.value, month.value, date2) }]),
                  style: normalizeStyle({ "--column": dayOfWeek(year.value, month.value, date2) + 1 }),
                  onClick: ($event) => !checkForDisabled(year.value, month.value, date2) && setDate(year.value, month.value, date2)
                }, toDisplayString(date2), 15, _hoisted_9$2);
              }), 256))
            ])
          ])
        ])
      ], 2);
    };
  }
});
const _hoisted_1$m = ["disabled"];
const _hoisted_2$b = { key: 0 };
const _sfc_main$s = /* @__PURE__ */ defineComponent({
  __name: "button",
  props: {
    label: {},
    loadingLabel: {},
    action: { type: Function }
  },
  emits: ["action"],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const attrs = useAttrs();
    const isLoading = ref(false);
    const state = computed(() => {
      if (attrs.disabled) {
        return 1;
      } else if (isLoading.value) {
        return 2;
      } else {
        return 0;
      }
    });
    const disabled = computed(
      () => state.value !== 0
      /* Active */
    );
    async function onClick() {
      if (state.value === 0) {
        isLoading.value = true;
        try {
          await Promise.resolve(props.action());
        } catch (err) {
          console.error(err);
        }
        isLoading.value = false;
      }
    }
    const resolvedLabel = computed(() => {
      switch (state.value) {
        case 0:
        case 1:
          return props.label || "";
        case 2:
        default:
          return props.loadingLabel || "Loading...";
      }
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("button", {
        class: normalizeClass(unref(attrs).class),
        disabled: disabled.value,
        onClick
      }, [
        renderSlot(_ctx.$slots, "default"),
        resolvedLabel.value ? (openBlock(), createElementBlock("span", _hoisted_2$b, toDisplayString(resolvedLabel.value), 1)) : createCommentVNode("", true)
      ], 10, _hoisted_1$m);
    };
  }
});
const _hoisted_1$l = {
  key: 0,
  id: "version-update-banner"
};
const _sfc_main$r = /* @__PURE__ */ defineComponent({
  __name: "version-update-banner",
  setup(__props) {
    const ignored = ref(false);
    const { applicationName: applicationName2 } = useProjectMetadata();
    const { remoteVersionRequirements: remoteVersionRequirements2, localVersion: localVersion2, meetsMinimumRequirement: meetsMinimumRequirement2, isAtLeastLatestStable: isAtLeastLatestStable2, stableVersionUpdateNotificationRegistered: stableVersionUpdateNotificationRegistered2, minVersionUpdateNotificationRegistered: minVersionUpdateNotificationRegistered2 } = useVersionTracking();
    onMounted(() => {
      stableVersionUpdateNotificationRegistered2.value = true;
    });
    const versionString = computed(() => {
      let str = "";
      if (remoteVersionRequirements2.value) {
        str += `Version ${remoteVersionRequirements2.value.latestStableVersion} for `;
      } else {
        str += `An update for `;
      }
      if (applicationName2.value) {
        str += `${applicationName2.value} is available`;
      } else {
        str += "this website is available";
      }
      return str;
    });
    const minimumNotificationShowing = computed(() => {
      if (!minVersionUpdateNotificationRegistered2.value) {
        return false;
      }
      return !meetsMinimumRequirement2.value;
    });
    function updateApp() {
      window.location.reload();
    }
    return (_ctx, _cache) => {
      return !unref(isAtLeastLatestStable2) && !ignored.value && !minimumNotificationShowing.value ? (openBlock(), createElementBlock("div", _hoisted_1$l, [
        createElementVNode("span", null, toDisplayString(versionString.value), 1),
        createElementVNode("button", {
          class: "primary",
          type: "button",
          onClick: updateApp
        }, "Update Now"),
        createElementVNode("button", {
          class: "later",
          type: "button",
          onClick: _cache[0] || (_cache[0] = ($event) => ignored.value = true)
        }, "Later")
      ])) : createCommentVNode("", true);
    };
  }
});
const _hoisted_1$k = {
  key: 0,
  id: "version-update-fullscreen"
};
const _sfc_main$q = /* @__PURE__ */ defineComponent({
  __name: "version-update-fullscreen",
  setup(__props) {
    const ignored = ref(false);
    const { applicationName: applicationName2 } = useProjectMetadata();
    const {
      remoteVersionRequirements: remoteVersionRequirements2,
      localVersion: localVersion2,
      meetsMinimumRequirement: meetsMinimumRequirement2,
      isAtLeastLatestStable: isAtLeastLatestStable2,
      stableVersionUpdateNotificationRegistered: stableVersionUpdateNotificationRegistered2,
      minVersionUpdateNotificationRegistered: minVersionUpdateNotificationRegistered2
    } = useVersionTracking();
    onMounted(() => {
      minVersionUpdateNotificationRegistered2.value = true;
    });
    const versionString = computed(() => {
      let str = "";
      if (remoteVersionRequirements2.value) {
        str += `Version ${remoteVersionRequirements2.value.latestStableVersion} for `;
      } else {
        str += `An update for `;
      }
      if (applicationName2.value) {
        str += `${applicationName2.value} is available. `;
      } else {
        str += "this website is available. ";
      }
      str += "Updating to the latest version is required to continue.";
      return str;
    });
    function updateApp() {
      window.location.reload();
    }
    return (_ctx, _cache) => {
      return !unref(meetsMinimumRequirement2) && !ignored.value ? (openBlock(), createElementBlock("div", _hoisted_1$k, [
        createElementVNode("aside", null, [
          createElementVNode("p", null, toDisplayString(versionString.value), 1),
          createElementVNode("button", {
            class: "primary",
            type: "button",
            onClick: updateApp
          }, "Update Now")
        ])
      ])) : createCommentVNode("", true);
    };
  }
});
const _sfc_main$p = /* @__PURE__ */ defineComponent({
  __name: "version-update-manager",
  setup(__props) {
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock(Fragment, null, [
        createVNode(_sfc_main$r),
        createVNode(_sfc_main$q)
      ], 64);
    };
  }
});
const _hoisted_1$j = {
  key: 0,
  id: "app-lockdown-gate"
};
const _sfc_main$o = /* @__PURE__ */ defineComponent({
  __name: "lockdown-gate",
  setup(__props) {
    const { applicationName: applicationName2 } = useProjectMetadata();
    const { lockdownConfig: lockdownConfig2 } = useLockdown();
    const heading = computed(() => {
      if (applicationName2.value) {
        return `${applicationName2.value} temporarily unavailable`;
      } else {
        return "Application temporarily unavailable";
      }
    });
    return (_ctx, _cache) => {
      return unref(lockdownConfig2) && unref(lockdownConfig2).lockdownActive && unref(lockdownConfig2).lockdownMessage ? (openBlock(), createElementBlock("div", _hoisted_1$j, [
        createElementVNode("aside", null, [
          createElementVNode("h1", null, toDisplayString(heading.value), 1),
          createElementVNode("p", null, toDisplayString(unref(lockdownConfig2).lockdownMessage), 1)
        ])
      ])) : createCommentVNode("", true);
    };
  }
});
function render(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", {
    class: normalizeClass(["feature-flag", _ctx.classNames])
  }, [
    _ctx.enabled || _ctx.ifDisabled === "disable" ? renderSlot(_ctx.$slots, "default", { key: 0 }) : createCommentVNode("", true),
    _cache[0] || (_cache[0] = createElementVNode("div", { class: "icon" }, "X", -1))
  ], 2);
}
const _sfc_main$n = defineComponent({
  props: {
    feature: {
      type: String
    },
    ifDisabled: {
      type: String,
      default: "disable"
    }
  },
  computed: {
    enabled() {
      return FeatureFlags.isFeatureEnabled(this.feature);
    },
    classNames() {
      if (this.enabled) {
        return "feature-flag-enabled";
      } else {
        return "feature-flag-disabled";
      }
    }
  }
});
const featureFlag = /* @__PURE__ */ _export_sfc(_sfc_main$n, [["render", render]]);
const _sfc_main$m = /* @__PURE__ */ defineComponent({
  __name: "main-sidebar",
  props: {
    useSlide: { type: Boolean, default: false },
    compactMode: { type: Boolean, default: false },
    fixedSlide: { type: Boolean, default: false }
  },
  setup(__props, { expose: __expose }) {
    useCssVars((_ctx) => ({
      "ec010cc4": EXPANDED_SIZE.value
    }));
    const props = __props;
    const isExpanded = ref(false);
    const EXPANDED_SIZE = computed(() => {
      if (props.compactMode) {
        return "300px";
      } else {
        return "500px";
      }
    });
    function slideMenu() {
      isExpanded.value = !isExpanded.value;
    }
    __expose({
      isExpanded,
      slideMenu
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("nav", {
        class: normalizeClass(["app-sidebar", { "expanded": isExpanded.value, "compact": _ctx.compactMode, "fixedSlide": _ctx.fixedSlide }])
      }, [
        renderSlot(_ctx.$slots, "default")
      ], 2);
    };
  }
});
const _hoisted_1$i = { class: "version-container" };
const _sfc_main$l = /* @__PURE__ */ defineComponent({
  __name: "version",
  props: {
    versionString: {}
  },
  setup(__props) {
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("aside", _hoisted_1$i, toDisplayString(_ctx.versionString), 1);
    };
  }
});
const _hoisted_1$h = { class: "app-footer" };
const _hoisted_2$a = { class: "details" };
const _sfc_main$k = /* @__PURE__ */ defineComponent({
  __name: "footer",
  props: {
    year: {},
    versionString: {}
  },
  setup(__props) {
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("footer", _hoisted_1$h, [
        createElementVNode("div", _hoisted_2$a, [
          createElementVNode("span", null, "Copyright ©" + toDisplayString(_ctx.year) + " Busy Human LLC, All Rights Reserved", 1),
          createVNode(_sfc_main$l, { "version-string": _ctx.versionString }, null, 8, ["version-string"])
        ])
      ]);
    };
  }
});
const _hoisted_1$g = { class: "phone-hidden" };
const _sfc_main$j = /* @__PURE__ */ defineComponent({
  __name: "route-nav",
  props: {
    navRoutes: {},
    navType: {},
    filterFunc: { type: Function }
  },
  setup(__props) {
    const props = __props;
    const availableRoutes = computed(() => {
      let visableRoutes = props.navRoutes.filter((r) => {
        var _a, _b;
        return (_b = (_a = r.meta) == null ? void 0 : _a.includedNavbars) == null ? void 0 : _b.includes(props.navType);
      });
      if (props.filterFunc) {
        visableRoutes = visableRoutes.filter(props.filterFunc);
      }
      return visableRoutes;
    });
    return (_ctx, _cache) => {
      const _component_router_link = resolveComponent("router-link");
      return openBlock(), createElementBlock("nav", _hoisted_1$g, [
        (openBlock(true), createElementBlock(Fragment, null, renderList(availableRoutes.value, (route2) => {
          return openBlock(), createElementBlock("li", {
            key: route2.path
          }, [
            createVNode(_component_router_link, {
              exact: "",
              to: route2.path
            }, {
              default: withCtx(() => [
                createTextVNode(toDisplayString(route2.name), 1)
              ]),
              _: 2
            }, 1032, ["to"])
          ]);
        }), 128))
      ]);
    };
  }
});
const _hoisted_1$f = { class: "app-header" };
const _hoisted_2$9 = ["src"];
const _sfc_main$i = /* @__PURE__ */ defineComponent({
  __name: "header",
  props: {
    appTitle: {},
    routeName: {},
    navRoutes: {},
    logo: {}
  },
  setup(__props) {
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("header", _hoisted_1$f, [
        createElementVNode("img", {
          class: "logo",
          src: _ctx.logo
        }, null, 8, _hoisted_2$9),
        createElementVNode("h1", null, toDisplayString(_ctx.appTitle) + ": " + toDisplayString(_ctx.routeName), 1),
        _cache[0] || (_cache[0] = createElementVNode("div", { class: "spacer" }, null, -1)),
        createVNode(_sfc_main$j, {
          "nav-type": "baseHeader",
          "nav-routes": _ctx.navRoutes
        }, null, 8, ["nav-routes"])
      ]);
    };
  }
});
const _hoisted_1$e = { class: "mobile-app-header" };
const _hoisted_2$8 = ["src"];
const _sfc_main$h = /* @__PURE__ */ defineComponent({
  __name: "mobile-header",
  props: {
    appTitle: {},
    logo: {}
  },
  setup(__props) {
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("header", _hoisted_1$e, [
        createElementVNode("img", {
          class: "logo",
          src: _ctx.logo
        }, null, 8, _hoisted_2$8),
        createElementVNode("h1", null, toDisplayString(_ctx.appTitle), 1)
      ]);
    };
  }
});
const _hoisted_1$d = { class: "mobile-nav-footer" };
const _hoisted_2$7 = { class: "option-name" };
const _sfc_main$g = /* @__PURE__ */ defineComponent({
  __name: "mobile-nav-footer",
  props: {
    navOptions: {}
  },
  setup(__props) {
    return (_ctx, _cache) => {
      const _component_router_link = resolveComponent("router-link");
      return openBlock(), createElementBlock("footer", _hoisted_1$d, [
        (openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.navOptions, (option) => {
          return openBlock(), createBlock(_component_router_link, {
            class: "nav-option",
            key: option.name,
            to: option.path
          }, {
            default: withCtx(() => [
              createElementVNode("div", {
                class: "option-image",
                style: normalizeStyle({ backgroundImage: `url(${option.image})` })
              }, null, 4),
              createElementVNode("div", _hoisted_2$7, toDisplayString(option.name), 1)
            ]),
            _: 2
          }, 1032, ["to"]);
        }), 128))
      ]);
    };
  }
});
var AppVersionManager;
((AppVersionManager2) => {
  let getDocFn;
  let onSnapshotFn;
  let versionField;
  const VERSION_MANAGER_ELEMENT_NAME = "VersionManager";
  const BANNER_TAG_NAME = "v-version-banner";
  const { remoteVersionRequirements: remoteVersionRequirements2, localVersion: localVersion2, meetsMinimumRequirement: meetsMinimumRequirement2, isAtLeastLatestStable: isAtLeastLatestStable2 } = useVersionTracking();
  function readRemoteVersion(data) {
    const reqs = versionField ? data[versionField] : data;
    try {
      if (!reqs) {
        throw new Error("[VCL] No version requirements found in the provided data.");
      }
      const { minRequiredVersion, latestStableVersion } = reqs;
      if (!semver$1.valid(minRequiredVersion)) {
        throw new Error(`Invalid remote minRequiredVersion "${minRequiredVersion}"`);
      }
      if (!semver$1.valid(latestStableVersion)) {
        throw new Error(`Invalid remote latestStableVersion "${latestStableVersion}"`);
      }
      remoteVersionRequirements2.value = reqs;
    } catch (err) {
      console.log("[VCL] Failed to read remote version requirements:");
      console.error(err);
    }
  }
  AppVersionManager2.readRemoteVersion = readRemoteVersion;
  async function watchVersion() {
    if (!getDocFn || !onSnapshotFn || !localVersion2.value) {
      throw new Error("AppVersionManager has not been initialized. Please call install() before using and define the required options.");
    }
    onSnapshotFn((snapshot) => {
      readRemoteVersion(snapshot);
      compareVersions();
    });
  }
  AppVersionManager2.watchVersion = watchVersion;
  function compareVersions() {
    if (!remoteVersionRequirements2.value || !localVersion2.value) {
      throw new Error(`Both a remote version and local version are required to compare versions. Please call install() before using and define the required options.`);
    }
    const { minRequiredVersion, latestStableVersion } = remoteVersionRequirements2.value;
    meetsMinimumRequirement2.value = semver$1.lte(minRequiredVersion, localVersion2.value);
    isAtLeastLatestStable2.value = semver$1.lte(latestStableVersion, localVersion2.value);
    console.log(`localVersion: ${localVersion2.value}, minRequiredVersion: ${minRequiredVersion}, latestStableVersion: ${latestStableVersion}`);
    console.log(`meetsMinimumRequirement: ${meetsMinimumRequirement2.value}, isAtLeastLatestStable: ${isAtLeastLatestStable2.value}`);
  }
  AppVersionManager2.compareVersions = compareVersions;
  function injectBanner(target) {
    const el = document.createElement(BANNER_TAG_NAME);
    target.appendChild(el);
  }
  AppVersionManager2.injectBanner = injectBanner;
  function install(vueApp, options) {
    getDocFn = options.getDoc;
    onSnapshotFn = options.onSnapshot;
    if (!semver$1.valid(options.localVersion)) {
      throw new Error("The localVersion option must be a valid semver string.");
    }
    localVersion2.value = options.localVersion;
    console.log(`Local app version: ${localVersion2.value}`);
    versionField = options.versionField;
    vueApp.component(VERSION_MANAGER_ELEMENT_NAME, _sfc_main$p);
    watchVersion();
  }
  AppVersionManager2.install = install;
  AppVersionManager2.plugin = {
    install
  };
})(AppVersionManager || (AppVersionManager = {}));
var Lockdown;
((Lockdown2) => {
  let getDocFn;
  let onSnapshotFn;
  let lockdownField;
  const LOCKDOWN_CONTROLLER_ELEMENT = "LockdownGate";
  const { lockdownConfig: lockdownConfig2 } = useLockdown();
  function readLockdown(data) {
    const config = lockdownField ? data[lockdownField] : data;
    lockdownConfig2.value = config;
  }
  Lockdown2.readLockdown = readLockdown;
  async function watchLockdown() {
    if (!getDocFn || !onSnapshotFn) {
      throw new Error("Lockdown has not been initialized. Please call install() before using and define the required options.");
    }
    onSnapshotFn((snapshot) => {
      readLockdown(snapshot);
    });
  }
  Lockdown2.watchLockdown = watchLockdown;
  function install(vueApp, options) {
    getDocFn = options.getDoc;
    onSnapshotFn = options.onSnapshot;
    vueApp.component(LOCKDOWN_CONTROLLER_ELEMENT, _sfc_main$o);
    lockdownField = options.lockdownField;
    watchLockdown();
  }
  Lockdown2.install = install;
  Lockdown2.plugin = {
    install
  };
})(Lockdown || (Lockdown = {}));
const _hoisted_1$c = {
  key: 0,
  class: "announcement-date"
};
const _hoisted_2$6 = { class: "announcement-card" };
const _sfc_main$f = /* @__PURE__ */ defineComponent({
  __name: "AnnouncementCard",
  props: {
    announcement: {},
    showDate: { type: Boolean, default: false },
    unread: { type: Boolean, default: false }
  },
  setup(__props) {
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("section", {
        class: normalizeClass(["announcement", { unread: _ctx.unread, urgent: _ctx.announcement.urgent }])
      }, [
        _ctx.showDate ? (openBlock(), createElementBlock("div", _hoisted_1$c, toDisplayString(_ctx.announcement.createdAt.toLocaleDateString("default", { "month": "short", "day": "numeric" })), 1)) : createCommentVNode("", true),
        createElementVNode("div", _hoisted_2$6, toDisplayString(_ctx.announcement.text), 1)
      ], 2);
    };
  }
});
const _hoisted_1$b = { class: "announcement-container" };
const _hoisted_2$5 = {
  key: 0,
  class: "announcement-list"
};
const _hoisted_3$1 = { class: "text" };
const _hoisted_4$1 = {
  key: 1,
  style: { "width": "100%", "display": "flex", "justify-content": "center" }
};
const _hoisted_5$1 = { class: "text" };
const _hoisted_6$1 = {
  key: 0,
  class: "important"
};
const _hoisted_7$1 = {
  key: 0,
  class: "more"
};
const _hoisted_8$1 = ["onClick"];
const _hoisted_9$1 = {
  key: 0,
  class: "more"
};
const AUTO_DISMISS_TIMEOUT = 7e3;
const _sfc_main$e = /* @__PURE__ */ defineComponent({
  __name: "AnnouncementContainer",
  setup(__props) {
    const { announcements, dismissAnnouncement, allAnnouncements, isUnread, dismissAll } = useAnnouncements();
    const urgentAnnouncements = computed(() => announcements.value.filter((a) => a.urgent && isUnread(a.$id)));
    const nextAnnouncement = computed(() => [(urgentAnnouncements.value.length > 0 ? urgentAnnouncements.value[0] : announcements.value[0]) || null]);
    const showAll = ref(false);
    const hide = ref(false);
    const empty = computed(() => announcements.value.length <= 0);
    function close(force = false) {
      if (urgentAnnouncements.value.length > 0 && !force) return;
      dismissAll();
      showAll.value = false;
    }
    const dismissTimeout = ref(null);
    watch(nextAnnouncement, ([a]) => {
      if (dismissTimeout.value) clearTimeout(dismissTimeout.value);
      if (a && !a.urgent) {
        dismissTimeout.value = setTimeout(() => {
          dismissAnnouncement(a.$id);
          dismissTimeout.value = null;
        }, AUTO_DISMISS_TIMEOUT, null);
      }
    }, { immediate: true });
    function showFullList() {
      showAll.value = true;
      if (dismissTimeout.value) clearTimeout(dismissTimeout.value);
    }
    return (_ctx, _cache) => {
      return openBlock(), createBlock(Teleport, { to: "body" }, [
        createElementVNode("aside", _hoisted_1$b, [
          createVNode(Transition, { name: "an-scrim-fadein" }, {
            default: withCtx(() => [
              showAll.value || urgentAnnouncements.value.length > 0 ? (openBlock(), createElementBlock("div", {
                key: 0,
                class: "announcement-scrim",
                onClick: _cache[0] || (_cache[0] = ($event) => close())
              })) : createCommentVNode("", true)
            ]),
            _: 1
          }),
          createVNode(Transition, {
            name: "announcement-shift",
            mode: "out-in"
          }, {
            default: withCtx(() => [
              showAll.value ? (openBlock(), createElementBlock("div", _hoisted_2$5, [
                createElementVNode("header", null, [
                  createElementVNode("span", _hoisted_3$1, toDisplayString(unref(allAnnouncements).length) + " Announcements", 1),
                  createElementVNode("button", {
                    onClick: _cache[1] || (_cache[1] = withModifiers(($event) => close(true), ["stop"]))
                  }, "X")
                ]),
                (openBlock(true), createElementBlock(Fragment, null, renderList(unref(allAnnouncements), (a) => {
                  return openBlock(), createBlock(_sfc_main$f, {
                    key: a.$id,
                    announcement: a,
                    "show-date": true,
                    unread: unref(isUnread)(a.$id)
                  }, null, 8, ["announcement", "unread"]);
                }), 128))
              ])) : !empty.value ? (openBlock(), createElementBlock("div", _hoisted_4$1, [
                createVNode(TransitionGroup, { name: "announcement-preview" }, {
                  default: withCtx(() => [
                    (openBlock(true), createElementBlock(Fragment, null, renderList(nextAnnouncement.value, (a) => {
                      return openBlock(), createElementBlock("div", {
                        class: normalizeClass(["announcement-preview-container", { urgent: !!a.urgent }]),
                        key: a.$id,
                        onClick: showFullList
                      }, [
                        createElementVNode("header", null, [
                          createElementVNode("span", _hoisted_5$1, [
                            a.urgent ? (openBlock(), createElementBlock("span", _hoisted_6$1, "Important ")) : createCommentVNode("", true),
                            _cache[3] || (_cache[3] = createTextVNode("Announcement"))
                          ]),
                          unref(announcements).length > 1 ? (openBlock(), createElementBlock("span", _hoisted_7$1, toDisplayString(unref(announcements).length - 1) + " more", 1)) : createCommentVNode("", true),
                          createElementVNode("button", {
                            onClick: withModifiers(($event) => unref(dismissAnnouncement)(a.$id), ["stop"])
                          }, "X", 8, _hoisted_8$1)
                        ]),
                        createVNode(_sfc_main$f, { announcement: a }, null, 8, ["announcement"])
                      ], 2);
                    }), 128))
                  ]),
                  _: 1
                })
              ])) : !hide.value ? (openBlock(), createElementBlock("div", {
                key: 2,
                class: "announcement-empty",
                onClick: showFullList
              }, [
                createElementVNode("header", null, [
                  _cache[4] || (_cache[4] = createElementVNode("span", { class: "text" }, "No new announcements", -1)),
                  unref(allAnnouncements).length > 0 ? (openBlock(), createElementBlock("span", _hoisted_9$1, "View All ")) : createCommentVNode("", true),
                  createElementVNode("button", {
                    onClick: _cache[2] || (_cache[2] = withModifiers(($event) => hide.value = true, ["stop"]))
                  }, "X")
                ])
              ])) : createCommentVNode("", true)
            ]),
            _: 1
          })
        ])
      ]);
    };
  }
});
class AnnouncementSource {
}
function firestoreToDate(fsDate) {
  if (fsDate instanceof Timestamp) {
    return fsDate.toDate();
  } else if (typeof fsDate === "string") {
    return new Date(fsDate);
  } else {
    throw new Error(`Unable to parse date from firestore ${fsDate}`);
  }
}
class MockAnnouncementSource extends AnnouncementSource {
  initialize(manager) {
    for (let i = 0; i < 10; i++) {
      const now = /* @__PURE__ */ new Date();
      const year = now.getFullYear();
      const month = now.getMonth();
      const date = now.getDate();
      manager.addAnnouncement(`TestAnnounce${i}`, {
        createdAt: new Date(year, month, date - 10 + i),
        text: `Test Announcement ${i}`,
        expires: new Date(year, month, date - 3 + i),
        urgent: Math.random() < 0.5
      });
    }
    return Promise.resolve();
  }
  cleanup() {
    return Promise.resolve();
  }
}
class FirestoreAnnouncementSource extends AnnouncementSource {
  constructor(db, collectionName = "announcements") {
    super();
    __publicField(this, "db");
    __publicField(this, "collectionName");
    __publicField(this, "cleanupFunc");
    __publicField(this, "firestoreConverter", {
      toFirestore(modelObject) {
        return modelObject;
      },
      fromFirestore(snapshot) {
        const snapData = snapshot.data();
        const data = {
          createdAt: firestoreToDate(snapData.createdAt),
          text: snapData.text
        };
        if (snapData.expires) data.expires = firestoreToDate(snapData.expires);
        return data;
      }
    });
    this.db = db;
    this.collectionName = collectionName;
  }
  async initialize(manager) {
    const col = collection(this.db, this.collectionName).withConverter(this.firestoreConverter);
    let resolvedOnce = false;
    console.log("[Announcements] Initializing firestore announcements");
    await new Promise((res) => {
      if (this.cleanupFunc) this.cleanupFunc();
      this.cleanupFunc = onSnapshot(col, (snap) => {
        for (const change of snap.docChanges()) {
          const { id } = change.doc;
          try {
            const data = change.doc.data();
            switch (change.type) {
              case "added":
                manager.addAnnouncement(id, data);
                break;
              case "removed":
                manager.deleteAnnouncement(id);
                break;
              case "modified":
                manager.updateAnnouncement(id, data);
                break;
            }
          } catch (e) {
            console.error(`Error while handling announcement document ${id}`, e);
          }
        }
        if (!resolvedOnce) {
          res();
          resolvedOnce = true;
        }
      });
    });
  }
  cleanup() {
    var _a;
    (_a = this.cleanupFunc) == null ? void 0 : _a.call(this);
    return Promise.resolve();
  }
}
const AnnouncementInjectKey = Symbol("BH_Announcements");
class AnnouncementManager {
  constructor() {
    __publicField(this, "events", mitt());
    __publicField(this, "sources", []);
    __publicField(this, "announcements", ref(/* @__PURE__ */ new Map()));
    __publicField(this, "acknowledgedAnnouncements", reactive([]));
    __publicField(this, "STORAGE_KEY", "announcements_dismissed");
    __publicField(this, "install", async (app) => {
      app.provide(AnnouncementInjectKey, this);
      const localApp = createApp(_sfc_main$e);
      const containerElement = document.createElement("div");
      localApp.provide(AnnouncementInjectKey, this);
      await this.initializeSources();
      nextTick(() => localApp.mount(containerElement));
      console.log("[Announcements] Plugin installed");
    });
  }
  addAnnouncement(id, announcement) {
    const now = Date.now();
    if (announcement.expires && now > announcement.expires.getTime()) return;
    this.announcements.value.set(id, announcement);
    this.events.emit("ANNOUNCEMENT:ADD", { ...announcement, $id: id });
  }
  updateAnnouncement(id, announcement) {
    const now = Date.now();
    if (announcement.expires && now > announcement.expires.getTime()) {
      if (this.announcements.value.has(id)) this.announcements.value.delete(id);
    } else {
      this.announcements.value.set(id, announcement);
      this.events.emit("ANNOUNCEMENT:UPDATE", { ...announcement, $id: id });
    }
  }
  deleteAnnouncement(id) {
    this.announcements.value.delete(id);
    this.events.emit("ANNOUNCEMENT:DELETE", id);
  }
  addSource(source) {
    this.sources.push(source);
  }
  dismissAnnouncement(id) {
    this.acknowledgedAnnouncements.push(id);
    this.saveStorage();
  }
  dismissAll() {
    const existingSet = new Set(this.acknowledgedAnnouncements);
    for (const a of this.announcements.value) {
      if (!existingSet.has(a[0])) this.acknowledgedAnnouncements.push(a[0]);
    }
    this.saveStorage();
  }
  /** Loads acknowledged announcements from localstorage. Unused announcements will be ignored */
  loadStorage() {
    const existing = localStorage.getItem(this.STORAGE_KEY) || "[]";
    const ack = JSON.parse(existing);
    const fil = ack.filter((a) => this.announcements.value.has(a));
    this.acknowledgedAnnouncements.length = 0;
    for (const f of fil) this.acknowledgedAnnouncements.push(f);
  }
  /** Saves acknowledged announcements to localstorage */
  saveStorage() {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.acknowledgedAnnouncements));
  }
  async initializeSources() {
    console.log("[Announcements] Initializing sources");
    const res = await Promise.allSettled(this.sources.map((s) => s.initialize(this)));
    for (const r of res) {
      if (r.status === "rejected") {
        console.error("[Announcements] Error initializing a source: ", r.reason);
      }
    }
    this.loadStorage();
    this.events.emit("ANNOUNCEMENT:READY");
  }
}
const useAnnouncements = () => {
  const vue = getCurrentInstance();
  const inst = vue ? inject(AnnouncementInjectKey, void 0) : void 0;
  if (!inst) throw new Error("[Announcements] Plugin was not installed");
  const isUnread = (id) => !inst.acknowledgedAnnouncements.includes(id);
  return {
    announcements: computed(() => [...inst.announcements.value.entries()].filter(([id]) => isUnread(id)).sort((a, b) => b[1].createdAt.getTime() - a[1].createdAt.getTime()).map(([id, a]) => ({ $id: id, ...a }))),
    dismissAnnouncement: (id) => inst.dismissAnnouncement(id),
    dismissAll: () => inst.dismissAll(),
    allAnnouncements: computed(() => [...inst.announcements.value.entries()].sort((a, b) => b[1].createdAt.getTime() - a[1].createdAt.getTime()).map(([id, a]) => ({ $id: id, ...a }))),
    isUnread
    // addAnnouncement: (...args: Parameters<AnnouncementInterface['addAnnouncement']>) => inst.addAnnouncement(...args),
  };
};
const ConditionalRouting = {
  install(app, { router, conditions }) {
    const globalConditions = conditions;
    function isRouteSubjectToCondition(to, obj) {
      if (obj.exclude && obj.exclude.length > 0) {
        let isExcluded = false;
        for (let i = 0; i < obj.exclude.length; i++) {
          const excludedPath = obj.exclude[i];
          if (to.path.indexOf(excludedPath) >= 0) {
            isExcluded = true;
            break;
          }
        }
        if (isExcluded) {
          return false;
        }
      }
      return true;
    }
    async function evaluateGlobalConditions(to, from) {
      let globalChecksSatisfied = true;
      let redirect = true;
      await Promise.all(globalConditions.map(async (obj) => {
        if (!globalChecksSatisfied) {
          return false;
        } else if (isRouteSubjectToCondition(to, obj)) {
          const result = await obj.allowNextIf(to, from);
          if (result !== true) {
            globalChecksSatisfied = false;
            redirect = result === false ? obj.redirect : result;
            if (to.path.indexOf(redirect) >= 0) {
              redirect = true;
              return false;
            } else {
              return redirect;
            }
          } else {
            return true;
          }
        } else {
          return true;
        }
      }));
      if (!globalChecksSatisfied) {
        return redirect;
      } else {
        return true;
      }
    }
    router.beforeEach(async (to, from, next) => {
      if (to === from) {
        next();
        return;
      }
      const result = await evaluateGlobalConditions(to, from);
      if (result === true) {
        if (to.meta && "allowNextIf" in to.meta && typeof to.meta.allowNextIf === "function") {
          const permitted = to.meta.allowNextIf(to, from, next);
          if (permitted) {
            next();
          }
        } else {
          next();
        }
      } else {
        next(result);
      }
    });
  }
};
const usStates = {
  "AK": "Alaska",
  "AL": "Alabama",
  "AR": "Arkansas",
  "AZ": "Arizona",
  "CA": "California",
  "CO": "Colorado",
  "CT": "Connecticut",
  "DE": "Delaware",
  "DC": "District of Columbia",
  "FL": "Florida",
  "GA": "Georgia",
  "HI": "Hawaii",
  "IA": "Iowa",
  "ID": "Idaho",
  "IL": "Illinois",
  "IN": "Indiana",
  "KS": "Kansas",
  "KY": "Kentucky",
  "LA": "Louisiana",
  "MA": "Massachusetts",
  "MD": "Maryland",
  "ME": "Maine",
  "MI": "Michigan",
  "MN": "Minnesota",
  "MO": "Missouri",
  "MS": "Mississippi",
  "MT": "Montana",
  "NC": "North Carolina",
  "ND": "North Dakota",
  "NE": "Nebraska",
  "NH": "New Hampshire",
  "NJ": "New Jersey",
  "NM": "New Mexico",
  "NV": "Nevada",
  "NY": "New York",
  "OH": "Ohio",
  "OK": "Oklahoma",
  "OR": "Oregon",
  "PA": "Pennsylvania",
  "RI": "Rhode Island",
  "SC": "South Carolina",
  "SD": "South Dakota",
  "TN": "Tennessee",
  "TX": "Texas",
  "UT": "Utah",
  "VA": "Virginia",
  "VT": "Vermont",
  "WA": "Washington",
  "WI": "Wisconsin",
  "WV": "West Virginia",
  "WY": "Wyoming"
};
const usTerritories = {
  "AS": "American Samoa",
  "GU": "Guam",
  "MP": "Northern Mariana Islands",
  "PR": "Puerto Rico",
  "VI": "U.S. Virgin Islands"
};
const usStatesTerritories = { ...usStates, ...usTerritories };
const usStatesAbbrivOnly = Object.keys(usStates);
const usStatesTerrAbbrivOnly = Object.keys(usStatesTerritories);
const MissingRoutes = {
  install(Vue, { router, redirect }) {
    if (!router) {
      throw new Error("router must be provided in options for MissingRoutes plugin in Vue.use(...)");
    }
    if (!redirect) {
      throw new Error("redirect must be defined in options for MissingRoutes plugin in Vue.use(...)");
    }
    router.beforeEach((to, from, next) => {
      if (to.matched.length === 0) {
        if (typeof redirect === "string") {
          next(redirect);
        } else {
          redirect(to, from, next);
        }
      } else {
        next();
      }
    });
  }
};
class Paginator {
  constructor(items, { pageSize = 50 } = {}) {
    __publicField(this, "items");
    __publicField(this, "pageSize");
    __publicField(this, "activePageNumber");
    __publicField(this, "lastPage");
    __publicField(this, "pageFirstItemIndex");
    __publicField(this, "pageLastItemIndex");
    __publicField(this, "emitter");
    __publicField(this, "totalPages", 0);
    __publicField(this, "activePageItems", []);
    this.items = items;
    this.pageSize = pageSize;
    this.activePageNumber = 1;
    this.lastPage = 1;
    this.pageFirstItemIndex = -1;
    this.pageLastItemIndex = -1;
    this.emitter = mitt();
    this.calculate();
  }
  get empty() {
    return this.items.length === 0;
  }
  submitPageChange() {
    const event = {
      activePageItems: this.activePageItems,
      activePageNumber: this.activePageNumber,
      totalPages: this.totalPages
    };
    this.emitter.emit("page:change", event);
  }
  changeActivePage(targetPage) {
    this.activePageNumber = targetPage;
    this.calculate();
    this.submitPageChange();
  }
  updateItems(items) {
    this.items = items;
    this.calculate();
    this.submitPageChange();
  }
  updatePageSize(pageSize) {
    this.pageSize = pageSize;
    this.calculate();
    this.submitPageChange();
  }
  calculate() {
    this.totalPages = Math.ceil(this.items.length / this.pageSize);
    this.activePageNumber = this.clampPageIndex(this.activePageNumber);
    this.pageFirstItemIndex = (this.activePageNumber - 1) * this.pageSize;
    this.pageLastItemIndex = this.activePageNumber * this.pageSize;
    this.lastPage = this.totalPages;
    this.activePageItems = this.items.slice(this.pageFirstItemIndex, this.pageLastItemIndex);
  }
  relativePageChange(delta) {
    const targetPage = this.activePageNumber + delta;
    this.goToPage(targetPage);
  }
  pageIndexValid(pageIndex) {
    return pageIndex <= this.totalPages && pageIndex >= 1;
  }
  clampPageIndex(pageIndex) {
    if (pageIndex < 1) {
      return 1;
    } else if (pageIndex > this.totalPages) {
      return this.totalPages;
    } else {
      return pageIndex;
    }
  }
  goToPage(targetPage) {
    if (this.pageIndexValid(targetPage)) {
      this.changeActivePage(targetPage);
      this.submitPageChange();
    }
  }
  nextPage() {
    this.relativePageChange(1);
  }
  previousPage() {
    this.relativePageChange(-1);
  }
  onPageChange(fn) {
    this.emitter.on("page:change", fn);
  }
  offPageChange(fn) {
    this.emitter.off("page:change", fn);
  }
}
const DEFAULT_APPROXIMATE_SECONDS = 5;
class ProgressTracker {
  /**
   * Constructs a new ProgressTracker object.
   * @param {ProgressTrackerOptions} options
   */
  constructor({ steps, mode, seconds = Infinity, autoDestroy = true }) {
    __publicField(this, "autoDestroy");
    __publicField(this, "stepsCompleted");
    __publicField(this, "timerInterval");
    __publicField(this, "timerTimeout");
    __publicField(this, "progress");
    __publicField(this, "done");
    __publicField(this, "seconds");
    __publicField(this, "running");
    __publicField(this, "isTimed");
    __publicField(this, "mode");
    __publicField(this, "steps");
    __publicField(this, "totalSteps");
    __publicField(this, "emitter");
    this.autoDestroy = autoDestroy;
    this.stepsCompleted = 0;
    this.timerInterval = null;
    this.timerTimeout = null;
    this.progress = 0;
    this.done = false;
    this.seconds = seconds;
    this.running = false;
    this.isTimed = this.seconds !== Infinity && this.seconds > 0;
    this.emitter = mitt();
    if (steps === Infinity || mode === "infinite") {
      this.mode = "infinite";
      this.steps = null;
      this.totalSteps = Infinity;
    } else if (typeof steps === "number") {
      this.mode = "finite";
      this.steps = null;
      this.totalSteps = this.steps;
    } else if (steps instanceof Array) {
      this.mode = "finite";
      this.steps = steps.map((step) => ({ completed: false, value: step }));
      this.totalSteps = steps.length;
    } else if (mode === "approximate" || this.isTimed && !steps) {
      if (!this.isTimed) {
        this.seconds = DEFAULT_APPROXIMATE_SECONDS;
      }
      this.mode = "approximate";
      this.totalSteps = this.seconds;
      this.steps = null;
      this.mode = "approximate";
    } else {
      console.log("ProgressTrackerOptions: ", { steps, mode, seconds, autoDestroy });
      throw new Error(`Unsupported values provided to ProgressTracker`);
    }
  }
  get currentStep() {
    if (this.totalSteps === Infinity) {
      return null;
    }
    if (this.steps && this.steps.length > 0) {
      return this.steps[this.stepsCompleted];
    } else {
      return this.stepsCompleted + 1;
    }
  }
  start() {
    var _a;
    this.running = true;
    (_a = this.emitter) == null ? void 0 : _a.emit("start");
    if (this.isTimed) {
      this.startTimer();
    }
  }
  startTimer() {
    if (!this.seconds) {
      throw new Error(`ProgressTracker cannot start a timer if seconds is unset`);
    }
    if (this.totalSteps !== Infinity) {
      this.timerInterval = setInterval(() => {
        this.incrementProgress(1);
      }, 1e3);
    }
    this.timerTimeout = setTimeout(() => {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
      }
      this.setDone();
    }, 1e3 * this.seconds);
  }
  destroy() {
    if (this.emitter) {
      this.emitter.off("start");
      this.emitter.off("done");
      this.emitter.off("progress");
    }
    delete this.emitter;
    this.totalSteps = null;
  }
  onStart(callback) {
    if (!this.emitter) {
      this.emitter = mitt();
    }
    this.emitter.on("start", callback);
  }
  onProgress(callback) {
    if (!this.emitter) {
      this.emitter = mitt();
    }
    this.emitter.on("progress", callback);
  }
  onDone(callback) {
    if (!this.emitter) {
      this.emitter = mitt();
    }
    this.emitter.on("done", callback);
  }
  recalculate() {
    if (this.mode === "finite") {
      this.progress = this.stepsCompleted / (this.totalSteps ?? 0);
    }
    if (this.done || this.stepsCompleted >= (this.totalSteps ?? 0)) {
      this.emitDone();
    } else if (this.emitter) {
      this.emitter.emit("progress", { progress: this.progress, seconds: this.seconds });
    }
  }
  incrementProgress(val = 1) {
    this.stepsCompleted += val;
    this.recalculate();
  }
  updateProgress(val) {
    this.stepsCompleted = val;
    this.recalculate();
  }
  completeStep(val) {
    var _a;
    if (this.mode !== "finite") {
      throw new Error(`Cannot call completeStep unless the ProgressTracker.mode is finite`);
    }
    const match = (_a = this.steps) == null ? void 0 : _a.find((step) => step.value === val);
    if (!match) {
      throw new Error(`ProgressTracker step not found: "${val}"`);
    }
    if (!match.completed) {
      match.completed = true;
      this.incrementProgress(1);
    }
  }
  emitDone() {
    this.done = true;
    this.running = false;
    if (this.emitter) {
      this.emitter.emit("done");
    }
    if (this.timerTimeout) {
      clearTimeout(this.timerTimeout);
    }
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    if (this.autoDestroy) {
      this.destroy();
    }
  }
  setDone() {
    this.done = true;
    if (this.mode === "finite") {
      this.stepsCompleted = this.totalSteps || 0;
    }
    this.recalculate();
  }
}
const LoopTracker = {
  loopTime: 1e3,
  stack: [],
  repeatCount: 0,
  timerTimeout: null,
  stopImmediately: false,
  // Letting the loop run a few times before stopping it can help 
  timeLimitReached: false,
  stackLimitReached: false,
  cleanupTimeout: null,
  delayedCleanup() {
    if (!this.cleanupTimeout) {
      this.cleanupTimeout = setTimeout(() => {
        this.reset();
        this.cleanupTimeout = null;
      }, 100);
    }
  },
  startTimer() {
    this.timeLimitReached = false;
    this.repeatCount = 0;
    this.timerTimeout = setTimeout(() => {
      this.timeLimitReached = true;
      this.checkStack();
      this.timerTimeout = null;
      this.delayedCleanup();
    }, LoopTracker.loopTime);
  },
  checkStack() {
    this.repeatCount = 0;
    for (let i = 0; i < this.stack.length; i++) {
      const route2 = this.stack[i];
      const count = this.countRoutesInStack(route2);
      if (count > 1) {
        this.repeatCount += 1;
      }
    }
    return this.repeatCount;
  },
  countRoutesInStack(route2) {
    let count = 0;
    this.stack.forEach((otherRoute) => {
      if (route2.path === otherRoute.path) {
        count += 1;
      }
    });
    return count;
  },
  reset() {
    this.stackLimitReached = false;
    this.timeLimitReached = false;
    this.stack = [];
  },
  add(route2) {
    if (!this.timerTimeout && !this.timeLimitReached) {
      this.startTimer();
    }
    if (this.stack.length < 10) {
      this.stack.push(route2);
      this.checkStack();
    } else {
      this.stackLimitReached = true;
    }
  },
  hook(to, from, next) {
    this.add(to);
    if (this.stackLimitReached || this.repeatCount > 0 && (this.stopImmediately || this.timeLimitReached)) {
      console.error("Redirect loop blocked: ", this.stack.map((route2) => route2.path));
      this.reset();
    } else if (this.timeLimitReached || !this.timerTimeout) {
      this.reset();
      this.startTimer();
      next();
    } else {
      next();
    }
  }
};
const RouterLoopProtection = {
  install(app, { router, options = {} }) {
    LoopTracker.stopImmediately = !!options.stopImmediately;
    router.beforeEach((to, from, next) => {
      LoopTracker.hook(to, from, next);
    });
  }
};
const RETRY_INTERVAL = 50;
const MAX_SECONDS = 10;
const MAX_RETRIES = MAX_SECONDS * 1e3 / RETRY_INTERVAL;
function useTeleportAwait(target) {
  const resolved = ref(null);
  if (target) {
    onMounted(() => {
      if (typeof target === "string") {
        let counter = 0;
        const interval = setInterval(() => {
          counter += 1;
          if (document.querySelector(target)) {
            clearInterval(interval);
            resolved.value = target;
          }
          if (counter >= MAX_RETRIES) {
            clearInterval(interval);
          }
        }, RETRY_INTERVAL);
      }
    });
  }
  return resolved;
}
const VueFireObjectsPlugin = {
  install(Vue, { FireObject } = {}) {
    if (!FireObject) {
      throw new Error("FireObject must be passed in on VueFireObjectsPlugin.install");
    }
    Vue.mixin({
      created() {
        if (this.$options.fireObjects) {
          for (const field in this.$options.fireObjects) {
            this.$data[field] = this.$options.fireObjects[field];
          }
        }
      }
    });
  }
};
class WorkWatcher {
  constructor() {
    __publicField(this, "tasks");
    __publicField(this, "events");
    __publicField(this, "_progressRef");
    __publicField(this, "working");
    this.tasks = [];
    this.events = mitt();
    this._progressRef = ref(0);
    this.working = ref(false);
  }
  addTask(a, b) {
    const newTask = { progress: 0 };
    if (typeof a === "string" && typeof b === "undefined") {
      newTask.name = a;
    } else if (typeof a === "object" && "then" in a) {
      newTask.promise = a;
    } else if (typeof b !== "undefined" && typeof a === "string") {
      newTask.name = a;
      newTask.promise = b;
    } else if (a instanceof WorkWatcher) {
      newTask.watcher = a;
    } else {
      throw new TypeError("First argument not supported");
    }
    if (typeof newTask.promise !== "undefined") {
      newTask.promise.then(() => {
        newTask.progress = 1;
        this.updateProgress();
      }).catch((err) => {
        newTask.progress = 0;
        this.events.emit("done", err);
      });
    }
    if (typeof newTask.watcher !== "undefined") {
      newTask.watcher.onDone((err) => {
        if (typeof err !== "undefined") {
          newTask.progress = 0;
          this.events.emit("done", err);
        } else {
          newTask.progress = 1;
          this.updateProgress();
        }
      });
    }
    if (typeof newTask.name !== "undefined") {
      const index = this.tasks.findIndex((val) => val.name === newTask.name);
      if (index !== -1) {
        this.tasks.splice(index, 1);
      }
    }
    const old = this.working.value;
    this.working.value = true;
    this.tasks.push(newTask);
    if (!old) {
      this.events.emit("working", true);
    }
  }
  /**
   * Finishes a named task. Promised tasks cannot be finished this way
   * @param name 
   */
  finishTask(name2) {
    this.updateTask(name2, 1);
  }
  /**
   * Update the progress on a task
   * @param name 
   * @param progress 
   */
  updateTask(name2, progress) {
    const task = this.tasks.find((t2) => t2.name === name2);
    if (typeof task === "undefined") {
      throw new Error(`No task found with the name ${name2}`);
    }
    if (typeof task.promise !== "undefined") {
      throw new Error(`Promise tasks cannot be set by name`);
    }
    task.progress = progress;
    this.updateProgress();
  }
  updateProgress() {
    const total = this.tasks.length;
    const counter = this.tasks.reduce((prev, task) => prev + Math.min(task.progress, 1), 0);
    const progress = counter / total;
    this.events.emit("progress", progress);
    this._progressRef.value = progress;
    if (progress >= 1) {
      this.events.emit("done");
      this.working.value = false;
      this.events.emit("working", false);
    }
  }
  /**
   * Fire an event when this watcher's tasks are all completed
   * This can fire multiple times if multiple tasks are added after completion
   * @param handler Error will be undefined if successful. 
   */
  onDone(handler) {
    this.events.on("done", handler);
  }
  /**
   * Fire an event whenever progress is made
   * @param handler Progress is a number between 0 and 1
   */
  onProgress(handler) {
    this.events.on("progress", handler);
  }
  /**
   * Fire an event when working status has changed
   * @param handler 
   */
  onWorkingChange(handler) {
    this.events.on("working", handler);
  }
  /**
   * 
   * @returns A vue computedref for the current progress, between 0-1
   */
  progressRef() {
    return computed(() => this._progressRef.value);
  }
  /**
   * 
   * @returns A vue computedref boolean value for the current working status
   */
  workingRef() {
    return computed(() => this.working.value);
  }
}
const SESSION_KEY_PREFIX = "__mnt-shft-session_";
const SESSION_KEY_IDB_PREFIX = "__mnt-shft-session_idb_";
function saveSessionWithStorage(storage, sessionName) {
  const hashMap = {};
  for (let i = 0; i < storage.length; i++) {
    let key = storage.key(i);
    if (key && !key.startsWith(SESSION_KEY_PREFIX)) {
      hashMap[key] = storage.getItem(key) || "null";
    }
  }
  storage.setItem(SESSION_KEY_PREFIX + sessionName, JSON.stringify(hashMap));
}
function saveCookie(sessionName) {
  localStorage.setItem(SESSION_KEY_PREFIX + "cookie_" + sessionName, document.cookie);
}
async function saveIndexedDB(sessionName) {
  const databases = await indexedDB.databases();
  const serializedDatabases = {};
  databases.forEach(async (database) => {
    if (database && database.name && database.version) {
      serializedDatabases[database.name] = await saveIndexDBDatabase(database.name, database.version);
    }
  });
  localStorage.setItem(SESSION_KEY_IDB_PREFIX + sessionName, JSON.stringify(serializedDatabases));
}
async function saveIndexDBDatabase(databaseName, version) {
  let request = indexedDB.open(databaseName, version);
  const serializedDatabase = {
    version,
    objectStores: {}
  };
  return new Promise((resolve, reject) => {
    request.onsuccess = async () => {
      const keys2 = Object.keys(request.result.objectStoreNames);
      for (const key in keys2) {
        const storeName = request.result.objectStoreNames[key];
        serializedDatabase.objectStores[key] = await saveIndexDBObjectStore(request.result, storeName);
      }
      resolve(serializedDatabase);
    };
  });
}
async function saveIndexDBObjectStore(database, objectStoreName) {
  const transaction = database.transaction(objectStoreName, "readwrite");
  const objectStore = transaction.objectStore(objectStoreName);
  const serializedStore = {};
  return new Promise((resolve, reject) => {
    if (objectStore !== null) {
      const request = objectStore.getAll();
      request.onsuccess = (ev) => {
        if (ev.target) {
          request.result.forEach((item) => {
            serializedStore[item.key] = item.value;
          });
        }
        resolve(serializedStore);
      };
    } else {
      reject(new Error("No such object store"));
    }
  });
}
function saveSession(name2) {
  saveSessionWithStorage(localStorage, name2);
  saveSessionWithStorage(sessionStorage, name2);
  saveCookie(name2);
  saveIndexedDB(name2);
}
function loadSessionFromStorage(storage, sessionName) {
  const hashMap = JSON.parse(storage.getItem(SESSION_KEY_PREFIX + sessionName) || "{}");
  for (let key in hashMap) {
    storage.setItem(key, hashMap[key]);
  }
}
function loadCookie(name2) {
  let cookie = localStorage.getItem(`${SESSION_KEY_PREFIX}cookie_${name2}`);
  if (cookie) {
    document.cookie = cookie;
  }
}
function loadIndexedDB(sessionName) {
  const data = JSON.parse(localStorage.getItem(SESSION_KEY_IDB_PREFIX + sessionName) || "{}");
  if (data) {
    const keys2 = Object.keys(data);
    keys2.forEach(async (key) => {
      await loadIndexDBDatabase(key, data[key]);
    });
  }
}
function loadIndexDBDatabase(databaseName, serializedDatabase) {
  let request = indexedDB.open(databaseName, serializedDatabase.version);
  return new Promise((resolve, reject) => {
    request.onsuccess = async () => {
      for (const key in serializedDatabase.objectStores) {
        await loadIndexDBObjectStore(request.result, key, serializedDatabase.objectStores[key]);
      }
      resolve(serializedDatabase);
    };
  });
}
function loadIndexDBObjectStore(database, objectStoreName, serializedObjectStore) {
  if (!database.objectStoreNames.contains(objectStoreName)) {
    database.createObjectStore(objectStoreName, { keyPath: "key" });
  }
  const promises = [];
  for (const key in serializedObjectStore) {
    const request = database.transaction(objectStoreName, "readwrite").objectStore(objectStoreName).put(serializedObjectStore[key], key);
    promises.push(new Promise((resolve, reject) => {
      request.onsuccess = () => resolve();
      request.onerror = (err) => reject(err);
    }));
  }
  return Promise.all(promises);
}
function loadSession(name2) {
  loadSessionFromStorage(localStorage, name2);
  loadSessionFromStorage(sessionStorage, name2);
  loadCookie(name2);
  loadIndexedDB(name2);
}
function clearCurrentSessionFromStorage(storage) {
  for (let i = 0; i < storage.length; i++) {
    let key = storage.key(i);
    console.log(`Clearing session key ${key}`);
    if (key && !key.startsWith(SESSION_KEY_PREFIX)) {
      storage.removeItem(key);
    }
  }
}
async function clearCurrentIndexedDB() {
  const databases = await indexedDB.databases();
  databases.forEach(async (database) => {
    if (database && database.name) {
      await clearIndexDBDatabase(database.name);
    }
  });
}
async function clearIndexDBDatabase(databaseName) {
  let request = indexedDB.deleteDatabase(databaseName);
  return new Promise((resolve, reject) => {
    request.onsuccess = async () => {
      resolve();
    };
  });
}
async function clearCurrentSession() {
  await clearCurrentSessionFromStorage(localStorage);
  await clearCurrentSessionFromStorage(sessionStorage);
  await clearCurrentIndexedDB();
  document.cookie = "";
}
function reload() {
  window.location.reload();
}
function scoreContextStatus(status) {
  switch (status) {
    case "preferred":
      return 1;
    case "normal":
      return 0.5;
    case "invalid":
      return 0;
  }
}
const CONSOLE_PREFIX = "[mnt.sft]: ";
const BUFFER_SIZE = 100;
function generateMessageKey() {
  return Math.random().toString(36).substring(2, 9);
}
class ShaftLogClass {
  constructor() {
    __publicField(this, "records", []);
    __publicField(this, "callbacks", []);
  }
  log(message) {
    let pointer = { id: generateMessageKey(), message, type: "info" };
    this.records.push(pointer);
    console.log(CONSOLE_PREFIX + message);
    this.clampLogSize();
    this.emitUpdate();
    return pointer;
  }
  error(error) {
    console.error(error);
    this.records.push({ id: generateMessageKey(), message: error.message, type: "error" });
    this.clampLogSize();
    this.emitUpdate();
  }
  warn(message) {
    console.warn(message);
    this.records.push({ id: generateMessageKey(), message, type: "warning" });
    this.clampLogSize();
    this.emitUpdate();
  }
  updateLog(pointer, appendMessage, toType) {
    pointer.message = pointer.message + " " + appendMessage;
    if (toType) {
      pointer.type = toType;
    }
    console.log(CONSOLE_PREFIX + appendMessage);
    this.clampLogSize();
    this.emitUpdate();
  }
  clampLogSize() {
    while (this.records.length > BUFFER_SIZE) {
      this.records.shift();
    }
  }
  emitUpdate() {
    this.callbacks.forEach((callback) => callback(this.records));
  }
  onUpdate(callback) {
    this.callbacks.push(callback);
  }
}
const ShaftLog = new ShaftLogClass();
var baseGetTag = _baseGetTag, isObjectLike = isObjectLike_1;
var symbolTag = "[object Symbol]";
function isSymbol$3(value) {
  return typeof value == "symbol" || isObjectLike(value) && baseGetTag(value) == symbolTag;
}
var isSymbol_1 = isSymbol$3;
var isArray$2 = isArray_1, isSymbol$2 = isSymbol_1;
var reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/, reIsPlainProp = /^\w*$/;
function isKey$1(value, object) {
  if (isArray$2(value)) {
    return false;
  }
  var type = typeof value;
  if (type == "number" || type == "symbol" || type == "boolean" || value == null || isSymbol$2(value)) {
    return true;
  }
  return reIsPlainProp.test(value) || !reIsDeepProp.test(value) || object != null && value in Object(object);
}
var _isKey = isKey$1;
var MapCache = _MapCache;
var FUNC_ERROR_TEXT = "Expected a function";
function memoize$1(func, resolver) {
  if (typeof func != "function" || resolver != null && typeof resolver != "function") {
    throw new TypeError(FUNC_ERROR_TEXT);
  }
  var memoized = function() {
    var args = arguments, key = resolver ? resolver.apply(this, args) : args[0], cache = memoized.cache;
    if (cache.has(key)) {
      return cache.get(key);
    }
    var result = func.apply(this, args);
    memoized.cache = cache.set(key, result) || cache;
    return result;
  };
  memoized.cache = new (memoize$1.Cache || MapCache)();
  return memoized;
}
memoize$1.Cache = MapCache;
var memoize_1 = memoize$1;
var memoize = memoize_1;
var MAX_MEMOIZE_SIZE = 500;
function memoizeCapped$1(func) {
  var result = memoize(func, function(key) {
    if (cache.size === MAX_MEMOIZE_SIZE) {
      cache.clear();
    }
    return key;
  });
  var cache = result.cache;
  return result;
}
var _memoizeCapped = memoizeCapped$1;
var memoizeCapped = _memoizeCapped;
var rePropName = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g;
var reEscapeChar = /\\(\\)?/g;
var stringToPath$1 = memoizeCapped(function(string) {
  var result = [];
  if (string.charCodeAt(0) === 46) {
    result.push("");
  }
  string.replace(rePropName, function(match, number, quote, subString) {
    result.push(quote ? subString.replace(reEscapeChar, "$1") : number || match);
  });
  return result;
});
var _stringToPath = stringToPath$1;
function arrayMap$1(array, iteratee) {
  var index = -1, length = array == null ? 0 : array.length, result = Array(length);
  while (++index < length) {
    result[index] = iteratee(array[index], index, array);
  }
  return result;
}
var _arrayMap = arrayMap$1;
var Symbol$1 = _Symbol, arrayMap = _arrayMap, isArray$1 = isArray_1, isSymbol$1 = isSymbol_1;
var INFINITY$1 = 1 / 0;
var symbolProto = Symbol$1 ? Symbol$1.prototype : void 0, symbolToString = symbolProto ? symbolProto.toString : void 0;
function baseToString$1(value) {
  if (typeof value == "string") {
    return value;
  }
  if (isArray$1(value)) {
    return arrayMap(value, baseToString$1) + "";
  }
  if (isSymbol$1(value)) {
    return symbolToString ? symbolToString.call(value) : "";
  }
  var result = value + "";
  return result == "0" && 1 / value == -INFINITY$1 ? "-0" : result;
}
var _baseToString = baseToString$1;
var baseToString = _baseToString;
function toString$1(value) {
  return value == null ? "" : baseToString(value);
}
var toString_1 = toString$1;
var isArray = isArray_1, isKey = _isKey, stringToPath = _stringToPath, toString = toString_1;
function castPath$1(value, object) {
  if (isArray(value)) {
    return value;
  }
  return isKey(value, object) ? [value] : stringToPath(toString(value));
}
var _castPath = castPath$1;
var isSymbol = isSymbol_1;
var INFINITY = 1 / 0;
function toKey$1(value) {
  if (typeof value == "string" || isSymbol(value)) {
    return value;
  }
  var result = value + "";
  return result == "0" && 1 / value == -INFINITY ? "-0" : result;
}
var _toKey = toKey$1;
var castPath = _castPath, toKey = _toKey;
function baseGet$1(object, path) {
  path = castPath(path, object);
  var index = 0, length = path.length;
  while (object != null && index < length) {
    object = object[toKey(path[index++])];
  }
  return index && index == length ? object : void 0;
}
var _baseGet = baseGet$1;
var baseGet = _baseGet;
function get(object, path, defaultValue) {
  var result = object == null ? void 0 : baseGet(object, path);
  return result === void 0 ? defaultValue : result;
}
var get_1 = get;
const _get = /* @__PURE__ */ getDefaultExportFromCjs(get_1);
const RE_REQ_TOKEN = /<(.+)>/i;
const RE_OPTIONAL_TOKEN = /\[(.+)\]/i;
class ShaftCommand {
  constructor(pattern, fn) {
    __publicField(this, "pattern");
    __publicField(this, "fn");
    __publicField(this, "tokens");
    this.pattern = pattern;
    this.fn = fn;
    this.tokens = [];
    this.tokenize();
  }
  tokenize() {
    this.tokens = this.pattern.split(" ").map((str) => {
      var _a, _b;
      let tokenType;
      if (RE_REQ_TOKEN.test(str)) {
        tokenType = "required";
        ((_a = str.match(RE_REQ_TOKEN)) == null ? void 0 : _a[0]) || "";
      } else if (RE_OPTIONAL_TOKEN.test(str)) {
        tokenType = "optional";
        ((_b = str.match(RE_OPTIONAL_TOKEN)) == null ? void 0 : _b[0]) || "";
      } else {
        tokenType = "keyword";
      }
      const token = {
        raw: str,
        name: str,
        type: tokenType
      };
      return token;
    });
  }
  match(command) {
    const cmdParts = command.split(" ");
    const result = {
      params: {},
      matched: false
    };
    if (cmdParts.length !== this.tokens.length) {
      return result;
    }
    for (let i = 0; i < cmdParts.length; i++) {
      const token = this.tokens[i];
      const cmdPart = cmdParts[i];
      if (token.type === "keyword" && token.name !== cmdPart) {
        return result;
      } else if (token.type === "required" && !cmdPart) {
        return result;
      }
      result.params[token.name] = cmdPart;
    }
    result.matched = true;
    return result;
  }
}
class ShaftInputInterpreterClass {
  constructor() {
    __publicField(this, "commands", []);
  }
  registerCommand(pattern, fn) {
    const command = new ShaftCommand(pattern, fn);
    this.commands.push(command);
  }
  runCommand(command, context) {
    for (let c of this.commands) {
      const result = c.match(command);
      if (result.matched) {
        return c.fn(result.params, context, command);
      }
    }
    return false;
  }
}
const ShaftInputInterpreter = new ShaftInputInterpreterClass();
ShaftInputInterpreter.registerCommand("help", async (args, context, command) => {
  ShaftInputInterpreter.commands.forEach((c) => {
    ShaftLog.log(c.pattern);
  });
  return true;
});
ShaftInputInterpreter.registerCommand("print [property]", async (args, context, command) => {
  try {
    let val = _get(context, args.property);
    if (!val) {
      throw new Error(`Property ${args.property} not found`);
    }
    let output = typeof val === "string" ? val : JSON.stringify(val, null, 2);
    ShaftLog.log(output);
    return true;
  } catch (err) {
    ShaftLog.error(err.message);
    return false;
  }
});
const RE_CODABLE_NAME = /^[a-zA-z_\-0-9]+$/i;
function flattenRoutes(basePath, flatRoutes, route2) {
  const clone = { ...route2 };
  if (basePath) {
    clone.path = `${basePath}/${route2.path}`;
  } else {
    clone.path = route2.path;
  }
  flatRoutes.push(clone);
  if (route2.children) {
    for (const childRoute of route2.children) {
      flattenRoutes(clone.path, flatRoutes, childRoute);
    }
  }
}
class MaintenanceShaftClass {
  constructor() {
    __publicField(this, "actions", []);
    __publicField(this, "shortcuts", []);
    __publicField(this, "contexts", []);
    __publicField(this, "router");
    __publicField(this, "appName");
    __publicField(this, "environment");
    __publicField(this, "mount");
    __publicField(this, "emitter");
    this.registerPresets();
    this.emitter = mitt();
  }
  setName(appName) {
    this.appName = appName;
  }
  setEnvironment(environment) {
    this.environment = environment;
  }
  onUpdate(callback) {
    this.emitter.on("update", callback);
  }
  onInputReceived(input) {
    this.logger.log(`> ${input}`);
    ShaftInputInterpreter.runCommand(input, this);
  }
  get routes() {
    const flatRoutes = [];
    if (this.router) {
      for (const r of this.router.options.routes) {
        flattenRoutes(null, flatRoutes, r);
      }
    }
    return flatRoutes.filter((route2) => !route2.redirect).map((route2) => ({
      ...route2,
      name: route2.name ? `${String(route2.name)}: ${route2.path}` : route2.path,
      action: () => {
        var _a;
        (_a = this.router) == null ? void 0 : _a.push(route2.path);
      }
    }));
  }
  get invocationContext() {
    return {
      get: (name2) => this.contextByName(name2) || null
    };
  }
  get logger() {
    return ShaftLog;
  }
  registerPresets() {
    this.registerAction("Save Session 1", {
      action: () => saveSession("1")
    });
    this.registerAction("Load Session 1", {
      action: () => loadSession("1")
    });
    this.registerAction("Clear Current Session", {
      action: () => clearCurrentSession()
    });
    this.registerAction("Reload", {
      action: () => reload()
    });
    this.logger.log("Loaded presets");
  }
  sortMacros(macros) {
    macros.forEach((m) => {
      this.checkMacroForContexts(m);
    });
    return macros.sort((a, b) => {
      const scoreA = scoreContextStatus(a.contextStatus);
      const scoreB = scoreContextStatus(b.contextStatus);
      if (scoreA === scoreB) {
        return 0;
      } else {
        return scoreA < scoreB ? 1 : -1;
      }
    });
  }
  checkMacroForContexts(macro) {
    let preferredResult, requiredResult;
    if (macro.requiredContexts) {
      requiredResult = this.checkAllContextsWithConditions(macro.requiredContexts, this.contexts);
    }
    if (macro.preferredContexts) {
      preferredResult = this.checkAllContextsWithConditions(macro.preferredContexts, this.contexts);
    }
    if (requiredResult && requiredResult.applies && !requiredResult.meetsAll) {
      macro.contextStatus = "invalid";
    } else if (preferredResult && preferredResult.applies && preferredResult.meetsAny) {
      macro.contextStatus = "preferred";
    } else {
      macro.contextStatus = "normal";
    }
  }
  /**
   * Returns whether which conditions (if any) are met for all the contexts
   * @param conditions
   * @param contexts
   * @returns
   */
  checkAllContextsWithConditions(conditions, contexts) {
    const result = {
      applies: false,
      meetsAny: false,
      meetsAll: true
    };
    for (let ctx of contexts) {
      let ctxResult = this.checkContextConditions(conditions, ctx);
      if (ctxResult.applies) {
        result.applies = true;
        if (ctxResult.meetsAny) {
          result.meetsAny = true;
        }
        if (!ctxResult.meetsAll) {
          result.meetsAll = false;
        }
      }
    }
    return result;
  }
  /**
   * Returns whether conditions apply to the context, if it meetsAny and meetsAll conditions
   * @param conditions
   * @param context
   * @returns
   */
  checkContextConditions(conditions, context) {
    const result = {
      applies: false,
      meetsAny: false,
      meetsAll: true
    };
    for (let c of conditions) {
      let [conditionName, condition] = c;
      if (context.name === conditionName) {
        result.applies = true;
      }
      if (result.applies) {
        if (condition === true && !!context.value) {
          result.meetsAny = true;
        } else if (typeof condition === "function" && condition(context)) {
          result.meetsAny = true;
        } else {
          result.meetsAll = false;
        }
      }
    }
    return result;
  }
  contextByName(name2) {
    return this.contexts.find((c) => c.name === name2);
  }
  shortcutByName(name2) {
    return this.shortcuts.find((shortcut) => shortcut.name === name2);
  }
  actionByName(name2) {
    return this.actions.find((action) => action.name === name2);
  }
  async executeMacro(macro) {
    let logIndex = ShaftLog.log(`Execute: ${macro.name}`);
    try {
      await Promise.resolve(macro.action(Shaft.invocationContext, macro.name));
      ShaftLog.updateLog(logIndex, "Success");
    } catch (err) {
      ShaftLog.updateLog(logIndex, "Error", "error");
      ShaftLog.error(typeof err === "string" ? Error(err) : err);
    }
  }
  /**
   * Defines a context for actions and shortcuts
   * @param name
   * @param options
   * @returns
   */
  defineContext(name2, options) {
    if (!name2.trim() || !RE_CODABLE_NAME.test(name2)) {
      throw new Error(`Invalid context name: ${name2}, can only contain letters, numbers, underscores, and dashes. Use options.label for more descriptive names`);
    }
    const ctxEmitter = mitt();
    const context = {
      name: name2,
      label: options.label || name2,
      type: options.type,
      value: options.initialValue,
      valueLabelFunction: options.valueLabelFunction,
      update: options.update || function update(newValue) {
        context.value = newValue;
        ctxEmitter.emit("update", context);
      },
      onUpdate(callback) {
        ctxEmitter.on("update", callback);
      },
      watch(watchFn) {
        watch(watchFn, (newVal) => {
          if (newVal) {
            context.update(newVal);
          } else {
            context.update(null);
          }
        });
      }
    };
    context.onUpdate((ctx) => {
      this.emitter.emit("update");
    });
    this.contexts.push(context);
    return context;
  }
  /** Registers an action (such as a reset)
   *  that will perform some sort of change
   *  or take some particular action
   */
  registerAction(name2, options) {
    const { tag, action, preferredContexts, requiredContexts } = options;
    this.actions.push({
      name: name2,
      tag,
      action,
      preferredContexts,
      requiredContexts,
      contextStatus: "normal"
    });
  }
  /**
   * Shortcut to a particular state + location
   * in an app (e.g. user logged in and on dashboard)
   */
  registerShortcut(name2, options) {
    const { tag, action, preferredContexts, requiredContexts } = options;
    this.shortcuts.push({
      name: name2,
      tag,
      action,
      preferredContexts,
      requiredContexts,
      contextStatus: "normal"
    });
  }
  /**
   * Accepts a VueRouter instance
   */
  registerRoutes(router) {
    this.router = router;
    if (!this.contextByName("currentRoute")) {
      const context = this.defineContext("currentRoute", {
        type: "string",
        initialValue: router.currentRoute.value,
        valueLabelFunction: (currentRoute) => currentRoute.path
      });
      this.router.afterEach((to, from) => {
        context.update(to);
      });
    }
  }
}
const MaintenanceShaft = new MaintenanceShaftClass();
const Shaft = MaintenanceShaft;
const _sfc_main$d = /* @__PURE__ */ defineComponent({
  __name: "shaft-item",
  props: {
    item: {},
    labelField: { type: [null, String, Function] }
  },
  setup(__props) {
    const props = __props;
    function resolveName(item) {
      if (!props.labelField) {
        return item.name;
      } else if (typeof props.labelField === "string") {
        return item[props.labelField];
      } else if (typeof props.labelField === "function") {
        return props.labelField(item);
      }
    }
    function execute() {
      if (props.item.contextStatus !== "invalid") {
        Shaft.executeMacro(props.item);
      }
    }
    const classes = computed(() => {
      const cls = [];
      cls.push(props.item.contextStatus);
      return cls;
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        class: normalizeClass(["shaft-item", classes.value]),
        onClick: execute
      }, [
        createElementVNode("label", null, toDisplayString(resolveName(_ctx.item)), 1)
      ], 2);
    };
  }
});
const _sfc_main$c = /* @__PURE__ */ defineComponent({
  __name: "shaft-tab",
  props: {
    items: {}
  },
  setup(__props) {
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("section", null, [
        renderSlot(_ctx.$slots, "default"),
        (openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.items, (item) => {
          return openBlock(), createBlock(_sfc_main$d, {
            key: item.name,
            item
          }, null, 8, ["item"]);
        }), 128))
      ]);
    };
  }
});
const _hoisted_1$a = { class: "shaft-log" };
const _sfc_main$b = /* @__PURE__ */ defineComponent({
  __name: "shaft-log",
  setup(__props) {
    const records = ref([]);
    function updateRecords() {
      while (records.value.length > 0) {
        records.value.pop();
      }
      for (let record of ShaftLog.records) {
        records.value.push(record);
      }
    }
    ShaftLog.onUpdate(() => {
      updateRecords();
    });
    onMounted(() => {
      updateRecords();
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("ul", _hoisted_1$a, [
        (openBlock(true), createElementBlock(Fragment, null, renderList(records.value, (record) => {
          return openBlock(), createElementBlock("li", {
            key: record.id,
            class: normalizeClass(record.type)
          }, toDisplayString(record.message), 3);
        }), 128))
      ]);
    };
  }
});
const _hoisted_1$9 = { class: "shaft-input" };
const _hoisted_2$4 = { class: "input-line" };
const _sfc_main$a = /* @__PURE__ */ defineComponent({
  __name: "shaft-input",
  setup(__props) {
    const rawInput = ref("");
    function submit() {
      if (rawInput.value.length > 0) {
        Shaft.onInputReceived(rawInput.value);
        rawInput.value = "";
      }
    }
    function enter() {
    }
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("ul", _hoisted_1$9, [
        _cache[1] || (_cache[1] = createElementVNode("div", { class: "prompt" }, "Type below", -1)),
        createElementVNode("div", _hoisted_2$4, [
          withDirectives(createElementVNode("input", {
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => rawInput.value = $event),
            onKeyup: withKeys(submit, ["enter"])
          }, null, 544), [
            [vModelText, rawInput.value]
          ]),
          createElementVNode("button", { onClick: enter }, "Enter")
        ])
      ]);
    };
  }
});
const _hoisted_1$8 = { class: "shaft-context-item" };
const _hoisted_2$3 = { class: "value" };
const _sfc_main$9 = /* @__PURE__ */ defineComponent({
  __name: "shaft-context-item",
  props: {
    item: {}
  },
  setup(__props) {
    const currentValue = ref();
    const props = __props;
    function setCurrentValue(context) {
      if (!context) {
        console.warn("No context provided");
        return;
      }
      if (context.valueLabelFunction) {
        currentValue.value = context.valueLabelFunction(context.value);
      } else {
        currentValue.value = context.value;
      }
    }
    props.item.onUpdate((context) => {
      setCurrentValue(context);
    });
    setCurrentValue(props.item);
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", _hoisted_1$8, [
        createElementVNode("label", null, toDisplayString(_ctx.item.name), 1),
        createElementVNode("div", _hoisted_2$3, toDisplayString(currentValue.value || "-"), 1)
      ]);
    };
  }
});
const _hoisted_1$7 = { class: "shaft-context" };
const _sfc_main$8 = /* @__PURE__ */ defineComponent({
  __name: "shaft-context",
  setup(__props) {
    const contextItems = ref(MaintenanceShaft.contexts);
    const expanded = ref(true);
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("section", _hoisted_1$7, [
        createElementVNode("h2", {
          onClick: _cache[0] || (_cache[0] = ($event) => expanded.value = !expanded.value)
        }, "Context"),
        expanded.value ? (openBlock(true), createElementBlock(Fragment, { key: 0 }, renderList(contextItems.value, (item) => {
          return openBlock(), createBlock(_sfc_main$9, {
            key: item.name,
            item
          }, null, 8, ["item"]);
        }), 128)) : createCommentVNode("", true)
      ]);
    };
  }
});
const _sfc_main$7 = /* @__PURE__ */ defineComponent({
  __name: "shaft",
  setup(__props) {
    const shaft = ref(MaintenanceShaft);
    const routes = ref([]);
    const expanded = ref(false);
    const alignedRight = ref(true);
    const activeTab = ref("Actions");
    const sortedActions = ref([]);
    const sortedShortcuts = ref([]);
    function rebuildLists() {
      sortedActions.value = MaintenanceShaft.sortMacros(shaft.value.actions);
      sortedShortcuts.value = MaintenanceShaft.sortMacros(shaft.value.shortcuts);
    }
    MaintenanceShaft.onUpdate(() => {
      rebuildLists();
    });
    rebuildLists();
    function loadRoutes() {
      while (routes.value.length > 0) {
        routes.value.pop();
      }
      console.log("MaintenanceShaft.routes", MaintenanceShaft.routes);
      if (MaintenanceShaft.routes) {
        for (const route2 of MaintenanceShaft.routes) {
          routes.value.push(route2);
        }
      }
    }
    function hide() {
      expanded.value = false;
    }
    function show() {
      expanded.value = true;
    }
    watch(() => MaintenanceShaft.router, () => {
      loadRoutes();
    }, { deep: true });
    onMounted(() => {
      loadRoutes();
    });
    return (_ctx, _cache) => {
      return expanded.value ? (openBlock(), createElementBlock("div", {
        key: 0,
        id: "maintenance-shaft",
        class: normalizeClass({ "left-align": !alignedRight.value })
      }, [
        createElementVNode("header", null, [
          _cache[2] || (_cache[2] = createElementVNode("h1", null, "> mnt.sft", -1)),
          createElementVNode("p", null, toDisplayString(shaft.value.appName) + toDisplayString(shaft.value.environment ? ` / ${shaft.value.environment}` : ""), 1),
          createElementVNode("button", {
            class: "move-shaft",
            onClick: _cache[0] || (_cache[0] = ($event) => alignedRight.value = !alignedRight.value)
          }, toDisplayString(alignedRight.value ? "<" : ">"), 1),
          createElementVNode("button", {
            class: "close-shaft",
            onClick: hide
          }, "X")
        ]),
        createVNode(_sfc_main$b),
        createVNode(_sfc_main$a),
        createVNode(_sfc_main$8),
        withDirectives(createElementVNode("select", {
          class: "active-tab",
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => activeTab.value = $event)
        }, _cache[3] || (_cache[3] = [
          createElementVNode("option", null, "Actions", -1),
          createElementVNode("option", null, "Shortcuts", -1),
          createElementVNode("option", null, "Routes", -1),
          createElementVNode("option", null, "Data", -1)
        ]), 512), [
          [vModelSelect, activeTab.value]
        ]),
        activeTab.value == "Actions" ? (openBlock(), createBlock(_sfc_main$c, {
          key: 0,
          items: sortedActions.value
        }, null, 8, ["items"])) : createCommentVNode("", true),
        activeTab.value == "Shortcuts" ? (openBlock(), createBlock(_sfc_main$c, {
          key: 1,
          items: sortedShortcuts.value
        }, null, 8, ["items"])) : createCommentVNode("", true),
        activeTab.value == "Routes" ? (openBlock(), createBlock(_sfc_main$c, {
          key: 2,
          items: routes.value
        }, null, 8, ["items"])) : createCommentVNode("", true)
      ], 2)) : (openBlock(), createElementBlock("button", {
        key: 1,
        id: "maintenance-shaft-toggle",
        onClick: show
      }, "> mnt.sft"));
    };
  }
});
let ShaftApp;
const SHAFT_MOUNT_KEY = `${SESSION_KEY_PREFIX}_mounted`;
function SetupMaintenance(root2) {
  if (!ShaftApp) {
    if (!Shaft.appName) {
      console.warn("Shaft has not been initialized yet! Call setName first!");
      return;
    }
    ShaftApp = createApp(_sfc_main$7);
    localStorage.setItem(SHAFT_MOUNT_KEY, "true");
    if (!root2) {
      const rootEl = document.createElement("div");
      rootEl.id = "maintenance-shaft-root";
      document.body.appendChild(rootEl);
      ShaftApp.mount(rootEl);
    } else {
      ShaftApp.mount(root2);
    }
  } else {
    Shaft.logger.warn("Maintenance shaft is already installed!");
  }
}
Shaft.mount = SetupMaintenance;
document.onreadystatechange = function() {
  window.Shaft = Shaft;
  if (localStorage.getItem(SHAFT_MOUNT_KEY) === "true") {
    SetupMaintenance();
  }
};
const _hoisted_1$6 = { class: "layout-plain" };
const _sfc_main$6 = /* @__PURE__ */ defineComponent({
  __name: "layout-plain",
  setup(__props) {
    onMounted(() => {
    });
    return (_ctx, _cache) => {
      const _component_app_main = resolveComponent("app-main");
      return openBlock(), createElementBlock("div", _hoisted_1$6, [
        createVNode(_component_app_main)
      ]);
    };
  }
});
const showHeader = ref(true);
const showFooter = ref(true);
const showMainSidebar = ref(true);
const showSecondarySidebar = ref(true);
const _hoisted_1$5 = { class: "layout-inner-sidebars" };
const _sfc_main$5 = /* @__PURE__ */ defineComponent({
  __name: "layout-inner-sidebars",
  setup(__props) {
    onMounted(() => {
    });
    return (_ctx, _cache) => {
      const _component_app_header = resolveComponent("app-header");
      const _component_app_sidebar = resolveComponent("app-sidebar");
      const _component_app_secondary_sidebar = resolveComponent("app-secondary-sidebar");
      const _component_app_main = resolveComponent("app-main");
      const _component_app_footer = resolveComponent("app-footer");
      return openBlock(), createElementBlock("div", _hoisted_1$5, [
        unref(showHeader) ? (openBlock(), createBlock(_component_app_header, { key: 0 })) : createCommentVNode("", true),
        createVNode(_component_app_main, null, {
          "before-route": withCtx(() => [
            unref(showMainSidebar) ? (openBlock(), createBlock(_component_app_sidebar, { key: 0 })) : createCommentVNode("", true)
          ]),
          "after-route": withCtx(() => [
            unref(showSecondarySidebar) ? (openBlock(), createBlock(_component_app_secondary_sidebar, { key: 0 })) : createCommentVNode("", true)
          ]),
          _: 1
        }),
        unref(showFooter) ? (openBlock(), createBlock(_component_app_footer, { key: 1 })) : createCommentVNode("", true)
      ]);
    };
  }
});
const _hoisted_1$4 = { class: "layout-outer-sidebars" };
const _hoisted_2$2 = { class: "layout-outer-sidebars-content" };
const _sfc_main$4 = /* @__PURE__ */ defineComponent({
  __name: "layout-outer-sidebars",
  setup(__props) {
    onMounted(() => {
    });
    return (_ctx, _cache) => {
      const _component_app_sidebar = resolveComponent("app-sidebar");
      const _component_app_header = resolveComponent("app-header");
      const _component_app_main = resolveComponent("app-main");
      const _component_app_footer = resolveComponent("app-footer");
      const _component_app_secondary_sidebar = resolveComponent("app-secondary-sidebar");
      return openBlock(), createElementBlock("div", _hoisted_1$4, [
        unref(showMainSidebar) ? (openBlock(), createBlock(_component_app_sidebar, { key: 0 })) : createCommentVNode("", true),
        createElementVNode("div", _hoisted_2$2, [
          unref(showHeader) ? (openBlock(), createBlock(_component_app_header, { key: 0 })) : createCommentVNode("", true),
          createVNode(_component_app_main),
          unref(showFooter) ? (openBlock(), createBlock(_component_app_footer, { key: 1 })) : createCommentVNode("", true)
        ]),
        unref(showSecondarySidebar) ? (openBlock(), createBlock(_component_app_secondary_sidebar, { key: 1 })) : createCommentVNode("", true)
      ]);
    };
  }
});
const _hoisted_1$3 = { class: "layout-mobile-footer" };
const _sfc_main$3 = /* @__PURE__ */ defineComponent({
  __name: "layout-mobile-footer",
  setup(__props) {
    onMounted(() => {
    });
    return (_ctx, _cache) => {
      const _component_app_mobile_header = resolveComponent("app-mobile-header");
      const _component_app_main = resolveComponent("app-main");
      const _component_mobile_nav_footer = resolveComponent("mobile-nav-footer");
      return openBlock(), createElementBlock("div", _hoisted_1$3, [
        unref(showHeader) ? (openBlock(), createBlock(_component_app_mobile_header, { key: 0 })) : createCommentVNode("", true),
        createVNode(_component_app_main),
        unref(showFooter) ? (openBlock(), createBlock(_component_mobile_nav_footer, { key: 1 })) : createCommentVNode("", true)
      ]);
    };
  }
});
var Layouts = /* @__PURE__ */ ((Layouts2) => {
  Layouts2["PLAIN"] = "LayoutPlain";
  Layouts2["INNER_SIDEBARS"] = "LayoutInnerSidebars";
  Layouts2["OUTER_SIDEBARS"] = "LayoutOuterSidebars";
  Layouts2["MOBILE_FOOTER"] = "LayoutMobileFooter";
  return Layouts2;
})(Layouts || {});
const OverrideLayout = ref(null);
const DefaultLayout = ref(_sfc_main$5);
const route = computed(() => useRoute());
const AppliedLayout = computed(() => {
  var _a;
  const meta = (_a = route.value) == null ? void 0 : _a.meta;
  if (OverrideLayout.value) {
    return OverrideLayout.value;
  }
  switch (meta == null ? void 0 : meta.layout) {
    case "LayoutPlain":
      return toRaw(_sfc_main$6);
    case "LayoutInnerSidebars":
      return toRaw(_sfc_main$5);
    case "LayoutOuterSidebars":
      return toRaw(_sfc_main$4);
    case "LayoutMobileFooter":
      return toRaw(_sfc_main$3);
    default:
      if (meta == null ? void 0 : meta.layout) {
        console.warn(`Unsupported layout: ${meta.layout}`);
      }
      return toRaw(DefaultLayout.value);
  }
});
const EventBubblingPlugin = {
  install(app) {
    app.config.globalProperties.$bubble = function $bubble(eventName, ...args) {
      let component = this;
      do {
        component == null ? void 0 : component.$emit(eventName, ...args);
        component = component == null ? void 0 : component.$parent;
      } while (component);
    };
    app.config.globalProperties.$bubbleFromInstance = function $bubbleFromInstance(instance, eventName, ...args) {
      if (!(instance == null ? void 0 : instance.emit) || !(instance == null ? void 0 : instance.parent)) {
        console.error("Bubble From instance was not passed a proper instance");
      }
      let component = instance;
      do {
        component == null ? void 0 : component.emit(eventName, ...args);
        component = component.parent;
      } while (component);
    };
  }
};
const _imports_0 = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAwAAAAMCAYAAABWdVznAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAC9SURBVHgBlVDdDYIwEL4rDMAGroBRX7FsogNI4iREFoBNbHw0ENjAugEPPlrOs4nEYJHwPd31+7neATgg8zqQaR24OHSJ6WHOlnx6sTou21HDR0wEoSURmqEJf8UYIJAmLhDFApDab5Po498tgRZGxDzhzuIbcs2vGqYQncpim1W5ixMwE7MNvsyqXQeUX5J1fwCBqMxAGGUlr4Z735WiklUBYxMAOw2ENgEm4Hmmsd+Q6TXkLvyr5mB12KgXVgdT+l3WfUMAAAAASUVORK5CYII=";
const _hoisted_1$2 = { class: "inline-error" };
const _sfc_main$2 = /* @__PURE__ */ defineComponent({
  __name: "inline-error",
  props: {
    errors: {}
  },
  setup(__props) {
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", _hoisted_1$2, [
        (openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.errors, (error) => {
          return openBlock(), createElementBlock("div", {
            class: "error-line",
            key: error
          }, [
            _ctx.errors.length > 1 ? (openBlock(), createElementBlock(Fragment, { key: 0 }, [
              createTextVNode("-")
            ], 64)) : createCommentVNode("", true),
            createTextVNode(" " + toDisplayString(error), 1)
          ]);
        }), 128))
      ]);
    };
  }
});
var FieldType = /* @__PURE__ */ ((FieldType2) => {
  FieldType2["INPUT"] = "input";
  FieldType2["TEXTAREA"] = "textArea";
  FieldType2["PASSWORD"] = "password";
  FieldType2["SELECT"] = "select";
  FieldType2["CHECKBOX"] = "checkbox";
  FieldType2["RADIO"] = "radio";
  FieldType2["DATE"] = "date";
  FieldType2["FILE"] = "file";
  return FieldType2;
})(FieldType || {});
var ErrorDisplayTypes = /* @__PURE__ */ ((ErrorDisplayTypes2) => {
  ErrorDisplayTypes2["INLINE"] = "inline";
  ErrorDisplayTypes2["LISTED"] = "listed";
  ErrorDisplayTypes2["BOTH"] = "both";
  return ErrorDisplayTypes2;
})(ErrorDisplayTypes || {});
var FieldLabelPosition = /* @__PURE__ */ ((FieldLabelPosition2) => {
  FieldLabelPosition2["BEFORE"] = "before";
  FieldLabelPosition2["AFTER"] = "after";
  FieldLabelPosition2["ABSOLUTE"] = "absolute";
  FieldLabelPosition2["CUSTOM"] = "custom";
  return FieldLabelPosition2;
})(FieldLabelPosition || {});
const FORM_INJECTION_KEY = "HexFormModel";
function useElement(type) {
  return computed(() => {
    switch (type) {
      case FieldType.TEXTAREA:
      case FieldType.SELECT:
      case FieldType.FILE:
      case FieldType.DATE:
        return type;
      case FieldType.CHECKBOX:
      default:
        return "input";
    }
  });
}
function useLabelPosition(type) {
  switch (type) {
    case FieldType.INPUT:
    case FieldType.PASSWORD:
    case FieldType.SELECT:
    case FieldType.TEXTAREA:
      return FieldLabelPosition.BEFORE;
    case FieldType.CHECKBOX:
    case FieldType.RADIO:
      return FieldLabelPosition.AFTER;
    case FieldType.DATE:
      return FieldLabelPosition.BEFORE;
    case FieldType.FILE:
      return FieldLabelPosition.BEFORE;
    default:
      throw new Error(`Unknown field type: ${type}`);
  }
}
function initializeField(model, props, form) {
  const hooks = [];
  if (form || props.name) {
    if (props.field) {
      throw new Error("When a hex-field is nested in a form, do not use the v-model or field attribute on the hex-field");
    }
    if (form && props.name) {
      model.value = form.fields[props.name].value;
      hooks.push(watch(() => model.value, (value) => {
        var _a, _b;
        if (((_b = (_a = form.fields) == null ? void 0 : _a[(props == null ? void 0 : props.name) || ""]) == null ? void 0 : _b.value) !== void 0) {
          form.fields[(props == null ? void 0 : props.name) || ""].value = value;
        }
      }));
      hooks.push(watch(() => form, (newForm) => {
        if (props.name) {
          const field = newForm.fields[props.name];
          if (field) {
            if (model.value !== field.value) {
              model.value = field.value;
            }
          } else {
            model.value = "";
          }
        }
      }, { deep: true }));
      return () => {
        hooks.forEach((hook) => hook());
      };
    } else {
      throw new Error("If a hex-field is nested inside of a hex-form, you must must apply the name field");
    }
  } else if (props.field) ;
}
function initializeEventBubbling(instance) {
  var _a;
  if (instance) {
    if ((_a = instance == null ? void 0 : instance.appContext.config.globalProperties) == null ? void 0 : _a.$bubbleFromInstance) {
      const bubbleEvent = (type, message) => {
        instance.appContext.config.globalProperties.$bubbleFromInstance(instance, type, message);
      };
      return bubbleEvent;
    } else {
      throw new Error("Custom Event Bubbler is not installed");
    }
  } else {
    throw new Error("GetCurrentInstance must be called from a lifecycle hook in the compositionAPI and cannot be null for Custom Event Bubbling");
  }
}
function validateValue$1(validator, value) {
  if (validator) {
    try {
      validator.parse(value);
      return true;
    } catch (e) {
      return e;
    }
  }
  return true;
}
function aggregateZodErrors$1(error) {
  if (error.message.indexOf("{") >= 0) {
    const errors = JSON.parse(error.message);
    return errors.map((err) => err.message).join(", ");
  } else {
    return error.message;
  }
}
function initializeErrors$1(errorsModel, props, form) {
  if (form || props.name) {
    if (props.field) {
      throw new Error("When a hex-field is nested in a form, do not use the v-model or field attribute on the hex-field");
    }
    if (form && props.name) {
      errorsModel.value = form.fields[props.name].errors || [];
      return watch(() => errorsModel.value, (value) => {
        var _a, _b;
        if (((_b = (_a = form.fields) == null ? void 0 : _a[(props == null ? void 0 : props.name) || ""]) == null ? void 0 : _b.errors) !== void 0) {
          form.fields[(props == null ? void 0 : props.name) || ""].errors = value;
        }
      });
    } else {
      throw new Error("If a hex-field is nested inside of a hex-form, you must must apply the name field");
    }
  } else if (props.field) ;
}
function initializeFieldValidation(model, errorsModel, props, form) {
  const hooks = [];
  const errorHook = initializeErrors$1(errorsModel, props, form);
  if (errorHook) hooks.push(errorHook);
  const validators = computed(() => {
    var _a, _b;
    if (props.field) {
      return ((_a = props.field) == null ? void 0 : _a.validators) || [];
    } else if (form && props.name) {
      return ((_b = form.fields[props.name]) == null ? void 0 : _b.validators) || [];
    } else {
      return props.validators || [];
    }
  });
  hooks.push(watch(() => model.value, (newValue) => {
    if (validators.value && validators.value.length > 0 && newValue !== "") {
      const totalErrors = [];
      validators.value.forEach((v) => {
        if (v == null ? void 0 : v.parse) {
          const result = validateValue$1(v, newValue);
          if (result instanceof Error) {
            const zodError = new Error(aggregateZodErrors$1(result));
            totalErrors.push(zodError.message);
          }
        } else {
          totalErrors.push(...(v == null ? void 0 : v(model.value)) || []);
        }
      });
      errorsModel.value = totalErrors;
    } else {
      errorsModel.value = [];
    }
  }));
  return () => {
    hooks.forEach((hook) => hook());
  };
}
const hasBlobConstructor = typeof Blob !== "undefined" && function() {
  try {
    return Boolean(new Blob());
  } catch (e) {
    return false;
  }
}();
const hasArrayBufferViewSupport = hasBlobConstructor && typeof Uint8Array !== "undefined" && function() {
  try {
    return new Blob([new Uint8Array(100)]).size === 100;
  } catch (e) {
    return false;
  }
}();
const hasToBlobSupport = typeof HTMLCanvasElement !== "undefined" ? HTMLCanvasElement.prototype.toBlob : false;
const hasBlobSupport = hasToBlobSupport || typeof Uint8Array !== "undefined" && typeof ArrayBuffer !== "undefined" && typeof atob !== "undefined";
const hasReaderSupport = typeof FileReader !== "undefined" || typeof URL !== "undefined";
const _ImageTools = class _ImageTools {
  static resize(file, maxDimensions, callback) {
    if (typeof maxDimensions === "function") {
      callback = maxDimensions;
      maxDimensions = {
        width: 640,
        height: 480
      };
    }
    if (!_ImageTools.isSupported() || !file.type.match(/image.*/)) {
      callback(file, false);
      return false;
    }
    if (file.type.match(/image\/gif/)) {
      callback(file, false);
      return false;
    }
    const image = document.createElement("img");
    image.onload = (imgEvt) => {
      let { width, height } = image;
      let isTooLarge = false;
      if (width >= height && width > maxDimensions.width) {
        isTooLarge = true;
      } else if (height > maxDimensions.height) {
        isTooLarge = true;
      }
      if (!isTooLarge) {
        try {
          callback(file, false);
        } catch (e) {
          throw new Error(e instanceof Error ? e.message : e);
        }
        return;
      }
      const scaleRatio = maxDimensions.width / width;
      width *= scaleRatio;
      height *= scaleRatio;
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        console.error("Unable to get Canvas Context");
        return false;
      }
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(image, 0, 0, width, height);
      if (hasToBlobSupport) {
        canvas.toBlob((blob) => {
          callback(blob, true);
        }, file.type);
      } else {
        const blob = _ImageTools._toBlob(canvas, file.type);
        callback(blob, true);
      }
    };
    _ImageTools._loadImage(image, file);
    return true;
  }
  static _toBlob(canvas, type) {
    const dataURI = canvas.toDataURL(type);
    const dataURIParts = dataURI.split(",");
    let byteString;
    if (dataURIParts[0].indexOf("base64") >= 0) {
      byteString = atob(dataURIParts[1]);
    } else {
      byteString = decodeURIComponent(dataURIParts[1]);
    }
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const intArray = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i += 1) {
      intArray[i] = byteString.charCodeAt(i);
    }
    const mimeString = dataURIParts[0].split(":")[1].split(";")[0];
    let blob = null;
    if (hasBlobConstructor) {
      blob = new Blob(
        [hasArrayBufferViewSupport ? intArray : arrayBuffer],
        { type: mimeString }
      );
    } else {
      blob = new Blob([arrayBuffer]);
    }
    return blob;
  }
  static _loadImage(image, file, callback) {
    if (typeof URL === "undefined") {
      const reader = new FileReader();
      reader.onload = function(evt) {
        image.src = evt.target.result;
        if (callback) {
          callback();
        }
      };
      reader.readAsDataURL(file);
    } else {
      image.src = URL.createObjectURL(file);
      if (callback) {
        callback();
      }
    }
  }
  static isSupported() {
    return typeof HTMLCanvasElement !== "undefined" && hasBlobSupport && hasReaderSupport;
  }
};
__publicField(_ImageTools, "_toFile", (theBlob, fileName) => {
  const b = theBlob;
  b.lastModifiedDate = /* @__PURE__ */ new Date();
  b.name = fileName;
  return theBlob;
});
let ImageTools = _ImageTools;
const _hoisted_1$1 = ["for"];
const _hoisted_2$1 = {
  key: 0,
  class: "required"
};
const _hoisted_3 = ["id", "placeholder", "type"];
const _hoisted_4 = ["id", "placeholder"];
const _hoisted_5 = ["id"];
const _hoisted_6 = ["value"];
const _hoisted_7 = ["id", "type"];
const _hoisted_8 = {
  key: 5,
  class: "g-file-upload"
};
const _hoisted_9 = ["accept"];
const _hoisted_10 = { class: "g-upload-btn-wrapper" };
const _hoisted_11 = {
  key: 0,
  class: "g-img-wrapper"
};
const _hoisted_12 = {
  key: 1,
  class: "errors"
};
const _hoisted_13 = ["for"];
const _hoisted_14 = {
  key: 0,
  class: "required"
};
const _sfc_main$1 = /* @__PURE__ */ defineComponent({
  __name: "hex-field",
  props: /* @__PURE__ */ mergeModels({
    field: {},
    name: {},
    placeholder: {},
    validators: {}
  }, {
    "modelValue": {},
    "modelModifiers": {},
    "errors": {},
    "errorsModifiers": {}
  }),
  emits: ["update:modelValue", "update:errors"],
  setup(__props) {
    var _a, _b, _c, _d, _e, _f, _g;
    const props = __props;
    const model = useModel(__props, "modelValue");
    const errorsModel = useModel(__props, "errors");
    const bubbleEventFunc = ref(null);
    onMounted(() => {
      try {
        bubbleEventFunc.value = initializeEventBubbling(getCurrentInstance());
      } catch (e) {
        console.warn("[VCL Warn] An Error Occurred while trying to setup event bubbling: ", e);
      }
    });
    const imgOkay = ref(false);
    const fileInput = ref(null);
    const imgName = ref("");
    const imgBlob = ref(null);
    const imgPreview = computed(() => imgBlob.value ? window.URL.createObjectURL(imgBlob.value) : imgUrl.value || null);
    function getResizedImage(file, size) {
      return new Promise((resolve, reject) => {
        ImageTools.resize(
          file,
          size,
          (blob, didItResize) => {
            if (!didItResize) {
              reject("Image Didnt Resize");
            } else if (blob) {
              resolve(blob);
            }
          }
        );
      });
    }
    const imageErrors = ref([]);
    async function handleImageUpload() {
      var _a2, _b2;
      imageErrors.value = [];
      if ((((_b2 = (_a2 = fileInput.value) == null ? void 0 : _a2.files) == null ? void 0 : _b2.length) || 0) > 0) {
        imgOkay.value = true;
        if (fileInput.value && fileInput.value.files && fileInput.value.files[0].name) {
          imgName.value = fileInput.value.files[0].name;
          model.value = fileInput.value.files[0];
          try {
            imgBlob.value = await getResizedImage(fileInput.value.files[0], { width: 155, height: 105 });
          } catch (e) {
            imageErrors.value.push("An Error Occured while processing image");
          }
        }
      } else {
        imgName.value = "";
        imgOkay.value = false;
        model.value = null;
      }
    }
    const form = inject(FORM_INJECTION_KEY);
    const formProps = ((_a = form == null ? void 0 : form.fields) == null ? void 0 : _a[(props == null ? void 0 : props.name) || ""]) || void 0;
    const fieldName = (props == null ? void 0 : props.name) || "field";
    const inputId = ref(newId(`hex-${fieldName}-`));
    const label = ((_b = props.field) == null ? void 0 : _b.label) || (formProps == null ? void 0 : formProps.label);
    const placeholder = ((_c = props.field) == null ? void 0 : _c.placeholder) || (formProps == null ? void 0 : formProps.placeholder) || "";
    const acceptedFileTypes = ((_d = props.field) == null ? void 0 : _d.acceptedFileTypes) || ".png, .jpeg, .jpg";
    const type = ((_e = props.field) == null ? void 0 : _e.type) || (formProps == null ? void 0 : formProps.type) || FieldType.INPUT;
    const showErrors = !!((form == null ? void 0 : form.errorDisplay) !== ErrorDisplayTypes.LISTED || ((_f = props.field) == null ? void 0 : _f.errors) !== void 0);
    const labelPosition = (formProps == null ? void 0 : formProps.labelPosition) || ((_g = props.field) == null ? void 0 : _g.labelPosition) || useLabelPosition(type);
    const imgUrl = computed(() => {
      var _a2;
      const imgFormProps = (_a2 = form == null ? void 0 : form.fields) == null ? void 0 : _a2[(props == null ? void 0 : props.name) || ""];
      if (imgFormProps == null ? void 0 : imgFormProps.imgUrl) {
        return imgFormProps == null ? void 0 : imgFormProps.imgUrl;
      }
      return null;
    });
    const showImg = computed(() => {
      if (formProps == null ? void 0 : formProps.showImg) {
        return true;
      }
      return false;
    });
    const options = computed(() => (formProps == null ? void 0 : formProps.options) || []);
    const isRequired = computed(() => {
      if (formProps == null ? void 0 : formProps.required) {
        return true;
      }
      return false;
    });
    const watchHooks = [];
    watchHooks.push(initializeField(model, props, form));
    watchHooks.push(initializeFieldValidation(model, errorsModel, props, form));
    onUnmounted(() => watchHooks.forEach((w) => w == null ? void 0 : w()));
    const isFocused = ref(false);
    function addFocus() {
      isFocused.value = true;
    }
    function removeFocus() {
      isFocused.value = false;
    }
    const classes = computed(() => {
      var _a2;
      const cls = [];
      if ((((_a2 = errorsModel.value) == null ? void 0 : _a2.length) || 0) > 0) cls.push("field-error");
      else if (model.value !== void 0) cls.push("field-valid");
      if (isFocused.value) {
        cls.push("field-focused");
      }
      return cls;
    });
    const element = type ? useElement(type) : void 0;
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        class: normalizeClass(["hex-field", classes.value])
      }, [
        unref(label) && unref(labelPosition) === unref(FieldLabelPosition).BEFORE ? (openBlock(), createElementBlock("div", {
          key: 0,
          class: "label-wrapper",
          for: inputId.value
        }, [
          isRequired.value ? (openBlock(), createElementBlock("div", _hoisted_2$1, "*")) : createCommentVNode("", true),
          createElementVNode("label", null, toDisplayString(unref(label)), 1)
        ], 8, _hoisted_1$1)) : createCommentVNode("", true),
        unref(element) === "input" ? withDirectives((openBlock(), createElementBlock("input", {
          key: 1,
          id: inputId.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => model.value = $event),
          placeholder: unref(placeholder),
          type: unref(type),
          onFocus: addFocus,
          onBlur: removeFocus
        }, null, 40, _hoisted_3)), [
          [vModelDynamic, model.value]
        ]) : unref(element) === "textArea" ? withDirectives((openBlock(), createElementBlock("textarea", {
          key: 2,
          id: inputId.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => model.value = $event),
          placeholder: unref(placeholder)
        }, null, 8, _hoisted_4)), [
          [vModelText, model.value]
        ]) : unref(element) === "select" ? withDirectives((openBlock(), createElementBlock("select", {
          key: 3,
          id: inputId.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => model.value = $event),
          onFocus: addFocus,
          onBlur: removeFocus
        }, [
          (openBlock(true), createElementBlock(Fragment, null, renderList(options.value, (opt) => {
            return openBlock(), createElementBlock("option", {
              key: typeof opt === "string" ? opt : opt.value,
              value: typeof opt === "string" ? opt : opt.value
            }, toDisplayString(typeof opt === "string" ? opt : opt.label), 9, _hoisted_6);
          }), 128))
        ], 40, _hoisted_5)), [
          [vModelSelect, model.value]
        ]) : unref(element) === "date" ? withDirectives((openBlock(), createElementBlock("input", {
          key: 4,
          id: inputId.value,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => model.value = $event),
          type: unref(type),
          max: "9999-12-31",
          onFocus: addFocus,
          onBlur: removeFocus
        }, null, 40, _hoisted_7)), [
          [vModelDynamic, model.value]
        ]) : unref(element) === "file" ? (openBlock(), createElementBlock("div", _hoisted_8, [
          createElementVNode("input", {
            ref_key: "fileInput",
            ref: fileInput,
            type: "file",
            accept: unref(acceptedFileTypes),
            style: { "display": "none" },
            onChange: handleImageUpload
          }, null, 40, _hoisted_9),
          createElementVNode("div", _hoisted_10, [
            createElementVNode("button", {
              class: "g-upload-btn",
              type: "button",
              onClick: _cache[4] || (_cache[4] = ($event) => {
                var _a2;
                return (_a2 = fileInput.value) == null ? void 0 : _a2.click();
              })
            }, _cache[5] || (_cache[5] = [
              createElementVNode("div", { class: "text" }, "Upload", -1),
              createElementVNode("img", { src: _imports_0 }, null, -1)
            ]))
          ]),
          showImg.value ? (openBlock(), createElementBlock("div", _hoisted_11, [
            imgPreview.value ? (openBlock(), createElementBlock("div", {
              key: 0,
              class: "img",
              style: normalizeStyle({ backgroundImage: `url('${imgPreview.value}')` })
            }, null, 4)) : createCommentVNode("", true)
          ])) : createCommentVNode("", true),
          imageErrors.value.length > 0 ? (openBlock(), createElementBlock("div", _hoisted_12, [
            createVNode(_sfc_main$2, { errors: imageErrors.value }, null, 8, ["errors"])
          ])) : createCommentVNode("", true)
        ])) : createCommentVNode("", true),
        unref(label) && unref(labelPosition) === unref(FieldLabelPosition).AFTER ? (openBlock(), createElementBlock("div", {
          key: 6,
          class: "label-wrapper",
          for: inputId.value
        }, [
          createElementVNode("label", null, toDisplayString(unref(label)), 1),
          isRequired.value ? (openBlock(), createElementBlock("div", _hoisted_14, "*")) : createCommentVNode("", true)
        ], 8, _hoisted_13)) : createCommentVNode("", true),
        showErrors ? (openBlock(), createBlock(_sfc_main$2, {
          key: 7,
          errors: errorsModel.value || []
        }, null, 8, ["errors"])) : createCommentVNode("", true)
      ], 2);
    };
  }
});
function initializeErrors(model) {
  const aggregateFieldErrors = computed(() => {
    const totalErrors = [];
    if (model.value) {
      Object.keys(model.value.fields).forEach((key) => {
        var _a, _b;
        (_b = (_a = model.value) == null ? void 0 : _a.fields[key]) == null ? void 0 : _b.errors.forEach((e) => {
          var _a2;
          totalErrors.push(`${(_a2 = model.value) == null ? void 0 : _a2.fields[key].label} - ${e}`);
        });
      });
    }
    return totalErrors;
  });
  const formErrors = computed(() => {
    var _a, _b, _c;
    switch ((_a = model.value) == null ? void 0 : _a.errorDisplay) {
      case ErrorDisplayTypes.LISTED:
        return [...aggregateFieldErrors.value, ...((_b = model.value) == null ? void 0 : _b.errors) || []];
      case ErrorDisplayTypes.INLINE:
      case ErrorDisplayTypes.BOTH:
        return [...((_c = model.value) == null ? void 0 : _c.errors) || []];
      default:
        return [];
    }
  });
  return { aggregateFieldErrors, formErrors };
}
function validateValue(validator, value) {
  if (validator) {
    try {
      validator.parse(value);
      return true;
    } catch (e) {
      return e;
    }
  }
  return true;
}
function aggregateZodErrors(error) {
  if (error.message.indexOf("{") >= 0) {
    const errors = JSON.parse(error.message);
    return errors.map((err) => err.message).join(", ");
  } else {
    return error.message;
  }
}
function initializeFormValidators(model) {
  const hooks = [];
  hooks.push(watch(() => model.value.fields, (newValue) => {
    if (model.value && model.value.validators && model.value.validators.length > 0) {
      const totalErrors = [];
      model.value.validators.forEach((v) => {
        if (v == null ? void 0 : v.parse) {
          const result = validateValue(v, newValue);
          if (result instanceof Error) {
            const zodError = new Error(aggregateZodErrors(result));
            totalErrors.push(zodError.message);
          }
        } else {
          totalErrors.push(...(v == null ? void 0 : v(newValue)) || []);
        }
      });
      model.value.errors = totalErrors;
    } else {
      model.value.errors = [];
    }
  }, { deep: true }));
  return () => {
    hooks.forEach((hook) => hook());
  };
}
const _hoisted_1 = {
  key: 0,
  class: "submit",
  type: "submit"
};
const _hoisted_2 = {
  key: 1,
  class: "errors"
};
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "hex-form",
  props: {
    "modelValue": {},
    "modelModifiers": {}
  },
  emits: /* @__PURE__ */ mergeModels(["submit"], ["update:modelValue"]),
  setup(__props, { expose: __expose, emit: __emit }) {
    var _a, _b;
    const model = useModel(__props, "modelValue");
    const emit = __emit;
    provide(FORM_INJECTION_KEY, model.value);
    const showErrors = ref(false);
    const { aggregateFieldErrors, formErrors } = initializeErrors(model);
    const watchHooks = [];
    watchHooks.push(initializeFormValidators(model));
    onUnmounted(() => watchHooks.forEach((w) => w == null ? void 0 : w()));
    function save() {
      var _a2, _b2, _c, _d;
      console.log("Form submitted:");
      const formData = {};
      let requiredFieldNotComplete = false;
      if (model.value) {
        Object.keys(model.value.fields).forEach((key) => {
          var _a3, _b3, _c2, _d2;
          const fieldValue = ((_a3 = model.value) == null ? void 0 : _a3.fields[key].value) || (((_b3 = model.value) == null ? void 0 : _b3.fields[key].type) === FieldType.FILE ? (_c2 = model.value) == null ? void 0 : _c2.fields[key].imgUrl : void 0);
          if (((_d2 = model.value) == null ? void 0 : _d2.fields[key].required) && !fieldValue) {
            requiredFieldNotComplete = true;
          }
          formData[key] = fieldValue || "";
        });
      }
      if (requiredFieldNotComplete) {
        if (((_b2 = (_a2 = model == null ? void 0 : model.value) == null ? void 0 : _a2.errors) == null ? void 0 : _b2.length) === 0) {
          alert("One or more required fields are not complete");
        }
        (_d = (_c = model.value) == null ? void 0 : _c.errors) == null ? void 0 : _d.push("One or more required fields are not complete");
        return false;
      } else {
        emit("submit", formData);
        return formData;
      }
    }
    function submitForm() {
    }
    function resetForm() {
      var _a2;
      console.log("Form reset");
      const fields = (_a2 = model.value) == null ? void 0 : _a2.fields;
      if (!fields) {
        throw new Error("Form model is not defined or has no fields");
      }
      const keys2 = Object.keys(fields);
      keys2.forEach((key) => {
        if (fields[key].defaultValue !== void 0) {
          fields[key].value = fields[key].defaultValue;
        } else {
          switch (fields[key].type) {
            case FieldType.CHECKBOX:
              fields[key].value = false;
              break;
            case FieldType.INPUT:
            case FieldType.PASSWORD:
            case FieldType.SELECT:
            case FieldType.FILE:
            default:
              fields[key].value = "";
          }
        }
      });
    }
    __expose({
      save
    });
    const submitButtonString = ((_b = (_a = model.value) == null ? void 0 : _a.strings) == null ? void 0 : _b.submitButton) || "Submit";
    return (_ctx, _cache) => {
      var _a2;
      return openBlock(), createElementBlock("form", {
        class: "hex-form",
        onSubmit: withModifiers(save, ["prevent"])
      }, [
        renderSlot(_ctx.$slots, "default", {
          submit: submitForm,
          reset: resetForm
        }),
        createElementVNode("footer", null, [
          ((_a2 = model.value) == null ? void 0 : _a2.useDefaultSubmit) ? (openBlock(), createElementBlock("button", _hoisted_1, toDisplayString(unref(submitButtonString)), 1)) : createCommentVNode("", true),
          unref(formErrors).length > 0 ? (openBlock(), createElementBlock("div", _hoisted_2, [
            !showErrors.value ? (openBlock(), createElementBlock("div", {
              key: 0,
              class: "error-toggle",
              onClick: _cache[0] || (_cache[0] = ($event) => showErrors.value = true)
            }, "Show Form Issues")) : (openBlock(), createElementBlock(Fragment, { key: 1 }, [
              createElementVNode("div", {
                class: "error-toggle",
                onClick: _cache[1] || (_cache[1] = ($event) => showErrors.value = false)
              }, "Hide Form Issues"),
              unref(formErrors).length > 0 ? (openBlock(), createBlock(_sfc_main$2, {
                key: 0,
                errors: unref(formErrors)
              }, null, 8, ["errors"])) : createCommentVNode("", true)
            ], 64))
          ])) : createCommentVNode("", true)
        ])
      ], 32);
    };
  }
});
class FormSection {
  constructor(rootForm, key, fields) {
    __publicField(this, "key");
    __publicField(this, "fields");
    __publicField(this, "rootForm");
    __publicField(this, "sectionInitialized");
    this.key = key;
    this.rootForm = rootForm;
    this.fields = fields;
    this.sectionInitialized = false;
  }
  addSection(sectionObject) {
    Object.keys(this.fields).forEach((field) => {
      this.rootForm.value.fields[`${this.key}-${field}`] = cloneDeep$1(this.fields[field]);
      if (sectionObject) {
        const value = sectionObject == null ? void 0 : sectionObject[field];
        if (this.rootForm.value.fields[`${this.key}-${field}`].type === FieldType.FILE && typeof value === "string") {
          this.rootForm.value.fields[`${this.key}-${field}`].imgUrl = value;
        }
        this.rootForm.value.fields[`${this.key}-${field}`].value = value || "";
      }
    });
    this.sectionInitialized = true;
  }
  updateSection(sectionObject) {
    Object.keys(this.fields).forEach((field) => {
      if (sectionObject) {
        const value = sectionObject == null ? void 0 : sectionObject[field];
        if (this.rootForm.value.fields[`${this.key}-${field}`].type === FieldType.FILE && typeof value === "string") {
          this.rootForm.value.fields[`${this.key}-${field}`].imgUrl = value;
        } else if (this.rootForm.value.fields[`${this.key}-${field}`].type === FieldType.FILE && !value) {
          this.rootForm.value.fields[`${this.key}-${field}`].imgUrl = void 0;
        }
        this.rootForm.value.fields[`${this.key}-${field}`].value = value || "";
      }
    });
  }
  removeSection() {
    Object.keys(this.fields).forEach((field) => {
      delete this.rootForm.value.fields[`${this.key}-${field}`];
    });
    this.sectionInitialized = false;
  }
  clearSection() {
    Object.keys(this.fields).forEach((field) => {
      this.rootForm.value.fields[`${this.key}-${field}`].value = "";
      if (this.rootForm.value.fields[`${this.key}-${field}`].type === FieldType.FILE) {
        this.rootForm.value.fields[`${this.key}-${field}`].imgUrl = void 0;
      }
    });
  }
  async saveSection(savedObject, options) {
    const sectionObject = {};
    const promises = [];
    Object.keys(savedObject).forEach((key) => {
      if (key.indexOf(`${this.key}-`) === 0) {
        const originalKey = key.substring(this.key.length + 1);
        if (Object.keys(this.fields).includes(originalKey)) {
          if (this.fields[originalKey].type === FieldType.FILE && typeof savedObject[key] !== "string") {
            if (options == null ? void 0 : options.imageCallback) {
              promises.push(new Promise((resolve, reject) => {
                options.imageCallback(savedObject[key]).then((imageUrl) => {
                  sectionObject[originalKey] = imageUrl;
                  resolve();
                }).catch((e) => reject(e));
              }));
            }
          } else {
            sectionObject[originalKey] = savedObject[key];
          }
          delete savedObject[key];
        }
      }
    });
    await Promise.all(promises);
    savedObject[this.key] = sectionObject;
  }
}
class FormSectionRepeatable {
  constructor(rootForm, key, fields) {
    __publicField(this, "key");
    __publicField(this, "fields");
    __publicField(this, "rootForm");
    __publicField(this, "ids");
    this.key = key;
    this.ids = [];
    this.rootForm = rootForm;
    this.fields = fields;
  }
  addSection(repeatObject) {
    let uniqueId = true;
    let id = 0;
    do {
      if (this.ids.includes(`${id}`)) {
        uniqueId = false;
        id += 1;
      } else {
        uniqueId = true;
      }
    } while (!uniqueId);
    Object.keys(this.fields).forEach((field) => {
      this.rootForm.value.fields[`${this.key}-${field}-${id}`] = cloneDeep$1(this.fields[field]);
      if (repeatObject) {
        const value = repeatObject == null ? void 0 : repeatObject[field];
        if (this.rootForm.value.fields[`${this.key}-${field}-${id}`].type === FieldType.FILE && typeof value === "string") {
          this.rootForm.value.fields[`${this.key}-${field}-${id}`].imgUrl = value;
        }
        this.rootForm.value.fields[`${this.key}-${field}-${id}`].value = value || "";
      }
    });
    this.ids.push(`${id}`);
  }
  removeSection(repeatId) {
    console.log("Remove Section", this.key, repeatId);
    const repeatIdx = this.ids.indexOf(repeatId);
    if (repeatIdx >= 0) {
      this.ids.splice(repeatIdx, 1);
      Object.keys(this.fields).forEach((c) => {
        delete this.rootForm.value.fields[`${this.key}-${c}-${repeatId}`];
      });
    }
  }
  async saveSection(savedObject, options) {
    const sectionObjects = {};
    const promises = [];
    Object.keys(savedObject).forEach((key) => {
      if (key.indexOf(`${this.key}-`) === 0) {
        const indexedKey = key.substring(this.key.length + 1);
        const idIdx = indexedKey.lastIndexOf(`-`);
        const originalKey = indexedKey.substring(0, idIdx);
        const id = indexedKey.substring(idIdx + 1);
        if (Object.keys(this.fields).includes(originalKey) && this.ids.includes(id)) {
          if (!sectionObjects[id]) sectionObjects[id] = {};
          if (this.fields[originalKey].type === FieldType.FILE && typeof savedObject[key] !== "string") {
            if (options == null ? void 0 : options.imageCallback) {
              promises.push(new Promise((resolve, reject) => {
                options.imageCallback(savedObject[key]).then((imageUrl) => {
                  sectionObjects[id][originalKey] = imageUrl;
                  resolve();
                }).catch((e) => reject(e));
              }));
            }
          } else {
            sectionObjects[id][originalKey] = savedObject[key];
          }
          delete savedObject[key];
        }
      }
    });
    await Promise.all(promises);
    savedObject[this.key] = [...Object.keys(sectionObjects).map((id) => sectionObjects[id]) || []];
  }
}
class FormManager {
  constructor(form, formRef) {
    __publicField(this, "rootForm");
    __publicField(this, "form");
    __publicField(this, "formRef");
    __publicField(this, "sectionMap");
    __publicField(this, "initSections", computed(() => {
      var _a;
      const keyToInit = {};
      [...((_a = this.sectionMap) == null ? void 0 : _a.keys()) || []].forEach((key) => {
        var _a2;
        const init = (_a2 = this.sectionMap.get(key)) == null ? void 0 : _a2.sectionInitialized;
        if (init) keyToInit[key] = init;
      });
      return keyToInit;
    }));
    __publicField(this, "repeatableSectionMap");
    __publicField(this, "repeatableKeyToIds", computed(() => {
      var _a;
      const keyToIds = {};
      [...((_a = this.repeatableSectionMap) == null ? void 0 : _a.keys()) || []].forEach((key) => {
        var _a2;
        const ids = (_a2 = this.repeatableSectionMap.get(key)) == null ? void 0 : _a2.ids;
        if (ids) keyToIds[key] = ids;
      });
      return keyToIds;
    }));
    this.rootForm = cloneDeep$1(form);
    this.form = ref(cloneDeep$1(form));
    this.formRef = formRef;
    this.sectionMap = /* @__PURE__ */ new Map();
    this.repeatableSectionMap = /* @__PURE__ */ new Map();
  }
  initField(fieldName, value) {
    if (!this.form.value.fields[fieldName]) return;
    this.form.value.fields[fieldName].value = value;
  }
  async save(options) {
    if (!this.formRef.value.save) throw new Error("Form is not Savable");
    let saveResult = this.formRef.value.save();
    if (options.useRawFormValue) {
      return saveResult;
    }
    const promises = [];
    [...this.sectionMap.keys()].forEach((key) => {
      const section = this.sectionMap.get(key);
      promises.push((section == null ? void 0 : section.saveSection(saveResult, options)) || new Promise((r) => {
        r();
      }));
    });
    [...this.repeatableSectionMap.keys()].forEach((key) => {
      const repeater = this.repeatableSectionMap.get(key);
      promises.push((repeater == null ? void 0 : repeater.saveSection(saveResult, options)) || new Promise((r) => {
        r();
      }));
    });
    if (options.imageCallback) {
      Object.keys(this.rootForm.fields).filter((f) => this.rootForm.fields[f].type === FieldType.FILE).forEach((f) => {
        if (typeof saveResult[f] !== "string") {
          promises.push(new Promise((resolve, reject) => {
            options.imageCallback(saveResult[f]).then((imageUrl) => {
              saveResult[f] = imageUrl;
              resolve();
            }).catch((e) => reject(e));
          }));
        }
      });
    }
    await Promise.all(promises);
    return saveResult;
  }
  clearForm() {
    [...this.sectionMap.keys()].forEach((key) => {
      const section = this.sectionMap.get(key);
      section == null ? void 0 : section.clearSection();
    });
    [...this.repeatableSectionMap.keys()].forEach((key) => {
      const repeater = this.repeatableSectionMap.get(key);
      const ids = cloneDeep$1(repeater == null ? void 0 : repeater.ids);
      if (repeater && ids) ids.forEach((id) => repeater.removeSection(id));
    });
    Object.keys(this.rootForm.fields).forEach((f) => {
      this.form.value.fields[f].value = "";
    });
  }
  addIndividualSection(formKey, fields) {
    const newSection = new FormSection(this.form, formKey, fields);
    this.sectionMap.set(formKey, newSection);
    newSection.addSection();
  }
  initializeSection(formKey, sectionObject) {
    const section = this.sectionMap.get(formKey);
    section == null ? void 0 : section.updateSection(sectionObject);
  }
  addRepeatableSection(formKey, fields) {
    const newRepeatable = new FormSectionRepeatable(this.form, formKey, fields);
    this.repeatableSectionMap.set(formKey, newRepeatable);
  }
  addRepeat(formKey, repeatObject) {
    const repeater = this.repeatableSectionMap.get(formKey);
    if (repeater) {
      repeater.addSection(repeatObject);
    }
  }
  removeRepeat(formKey, repeatId) {
    const repeater = this.repeatableSectionMap.get(formKey);
    if (repeater) {
      repeater.removeSection(repeatId);
    }
  }
}
const HexFormPlugin = {
  install(app, ...options) {
    app.component("hex-field", _sfc_main$1);
    app.component("hex-form", _sfc_main);
  }
};
export {
  AnnouncementManager,
  AppVersionManager,
  AppliedLayout,
  _sfc_main$H as AuthComponent,
  checkbox as Checkbox,
  ConditionalRouting,
  _sfc_main$t as DatePicker,
  DefaultConditions,
  DefaultLayout,
  DialogMode,
  dropdown as Dropdown,
  ErrorDisplayTypes,
  EventBubblingPlugin,
  featureFlag as FeatureFlagComponent,
  FeatureFlags,
  FieldLabelPosition,
  FieldType,
  fireInput as FireInput,
  fireRepeater as FireRepeater,
  FirestoreAnnouncementSource,
  _sfc_main$k as Footer,
  FormManager,
  _sfc_main$i as Header,
  _sfc_main$1 as HexField,
  HexFormPlugin,
  _sfc_main$5 as LayoutHeaderFooter,
  _sfc_main$3 as LayoutMobileFooter,
  _sfc_main$4 as LayoutOuterSidebars,
  _sfc_main$6 as LayoutPlain,
  Layouts,
  legacyModal as LegacyModal,
  Lockdown,
  _sfc_main$o as LockdownGate,
  LoginComponent,
  LoopTracker,
  _sfc_main$m as MainSidebar,
  MaintenanceShaft,
  MissingRoutes,
  _sfc_main$h as MobileHeader,
  _sfc_main$g as MobileNavFooter,
  MockAnnouncementSource,
  OverrideLayout,
  Paginator,
  PlatformPlugin,
  ProgressTracker,
  radioset as Radioset,
  _sfc_main$I as ResetCredentialsComponent,
  RouterLoopProtection,
  SetupMaintenance,
  Shaft,
  SignupComponent,
  _sfc_main$y as SnackBar,
  tagCreator as TagCreator,
  tagListEditor as TagListEditor,
  textField as TextField,
  _sfc_main$v as TwoPointSlider,
  _sfc_main$s as VCLButton,
  _sfc_main$l as Version,
  _sfc_main$r as VersionBanner,
  _sfc_main$q as VersionFullscreen,
  _sfc_main$p as VersionManager,
  _sfc_main$C as VueDialog,
  VueFireObjectsPlugin,
  _sfc_main$D as VueModal,
  WorkWatcher,
  dialogCallback,
  dialogCloseOnScrim,
  dialogConfirmButtonText,
  dialogMessage,
  dialogMode,
  dialogTitle,
  getConfirmation,
  getEmitter,
  initEvents,
  newId,
  normalizeListItems,
  setEmitter,
  shouldShowForPlatform,
  showDialog,
  showFooter,
  showHeader,
  showMainSidebar,
  showMessage,
  showSecondarySidebar,
  usStates,
  usStatesAbbrivOnly,
  usStatesTerrAbbrivOnly,
  usStatesTerritories,
  usTerritories,
  useAnnouncements,
  useAuth,
  useClickOutside,
  useFirestoreQuery,
  useLockdown,
  useMittEvent,
  useProjectMetadata,
  useScreenGeometry,
  useTeleportAwait,
  useVersionTracking
};
//# sourceMappingURL=vue-component-library.js.map
